// src/EditorPage.js
// ━━━ 重构后的主编辑页面：状态声明 + Hook 编排 + JSX 渲染 ━━━
import React, { useState, useCallback, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import ReactFlow, { 
  Background, applyEdgeChanges, applyNodeChanges, addEdge, 
  useReactFlow, ReactFlowProvider, useViewport, useStore
} from 'reactflow';
import { useParams, useNavigate } from 'react-router-dom';
import 'reactflow/dist/style.css';

// ── 组件 ──
import SelectionToolbar from './components/SelectionToolbar';
import MultiSelectHandle from './components/MultiSelectHandle';
import GroupToolbar from './components/GroupToolbar';
import AssetDrawer from './components/AssetDrawer';
import ModelMasterPanel from './components/ModelMasterPanel';
import SaveAssetModal from './components/SaveAssetModal'; 
import CoverCropperModal from './components/CoverCropperModal';
import ImagePickerModal from './components/ImagePickerModal';
import ConfirmModal from './components/ConfirmModal';
import CreditLogsModal from './components/CreditLogsModal';
import RechargeModal from './components/RechargeModal'; // 💳 弹窗（教育版构建时换成空壳 stub）
import VoucherRedeemModal from './components/VoucherRedeemModal'; // 🎓 教育版：兑换算力券
import { IS_EDU } from './config/edition'; // 🎓 部署级版本开关（构建期常量）
import InpaintMaskEditor from './InpaintMaskEditor';
import { NodeCallbackProvider } from './NodeCallbackContext';
import { MenuOpenProvider } from './MenuOpenContext';
import AlertToast from './components/AlertToast';
import CacheBanner from './components/CacheBanner';
import BottomControls from './components/BottomControls';
import HelperLines from './HelperLines';
import CineartLogo from './components/CineartLogo';
import QuickTooltip from './components/QuickTooltip';
import UserAvatar from './components/UserAvatar';
import ChatWindow from './components/ChatWindow';

// ── 外部工具 ──
import {
  Image as ImageIcon, Upload, Clapperboard, MonitorPlay, Sparkles,
  Database, Save, Trash2, GitFork, FilePlus,
  Loader2, ImagePlus, Edit2, User, Sparkle, Link, MessageCircle, Clock,
  LayoutGrid, ScrollText, PanelLeft, PanelBottom, Box,
} from 'lucide-react';
import { useAuth } from './contexts/AuthContext';
import { useProjectLoading } from './contexts/ProjectLoadingContext'; // 🆕 全局项目切换加载状态
import localCacheService from './services/localCacheService';
import { parseTextToSlate } from './components/SlatePromptEditor';
import projectCacheService from './services/projectCacheService';
// 🆕 智能预加载 + 优先级队列
import PriorityQueue from './services/priorityQueue';
import predictivePreloader from './services/predictivePreloader';
import projectHotspotTracker from './services/projectHotspotTracker';
import * as C from './constants'; 
import * as U from './utils';
import { findPreset, findSkillboxPreset } from './utils/presetTree';
import { readNodeDefaults, ratioOrientationSize } from './utils/nodeDefaults';
import api from './api';
import { nodeTypes as initialNodeTypes, edgeTypes as initialEdgeTypes } from './flowTypes';

// ── 提取的工具 & Hooks ──
import { ContextMenuItem, getRandomTextBg, normalizeAsset, IconFilm, calculateLongSide120, calculateLongSide60, calculateLongSideCustom, pickDurableUrl, pickBlobUrl } from './editorUtils';
import useUndoRedo from './hooks/useUndoRedo';
import { getHelperLines } from './utils/helperLines';
import { setOwnOssHosts } from './utils/imageThumb';
import { copyTextToClipboard } from './utils/browserCompat'; // http 站点没有 navigator.clipboard
import { getSnapEnabled } from './hooks/useSnapToggle';
import { useTheme } from './hooks/useTheme';
import useAudioPreload from './hooks/useAudioPreload';
import useWebSocket from './hooks/useWebSocket';
import useChatNotifications from './hooks/useChatNotifications';
import useProjectSave from './hooks/useProjectSave';
import useEditorTabLock from './hooks/useEditorTabLock';
import useNodeActions from './hooks/useNodeActions';
import useMjAction from './hooks/useMjAction';
import useGptInpaint from './hooks/useGptInpaint';
import useH3Upscale from './hooks/useH3Upscale';
import useProjectManagement from './hooks/useProjectManagement';
import useAssetManagement from './hooks/useAssetManagement';
import useKeyboardShortcuts from './hooks/useKeyboardShortcuts';
import useVoiceShortcut from './hooks/useVoiceShortcut';
import useCacheStatus from './hooks/useCacheStatus';
import useAlert from './hooks/useAlert';
import { ensureCustomFontsLoaded } from './fontLoader';

// 自建对象存储（教育版 MinIO）的公开域名 —— 构建期注入，逗号分隔可写多个。
//   写法容忍 `https://host/bucket` 这种带协议和路径前缀的完整域名，这里只取 host。
//   商业版不设这个变量 → 数组为空 → 下面的 own-cloud 判定行为跟改动前完全一致。
const EXTRA_OWN_CLOUD_HOSTS = (process.env.REACT_APP_STORAGE_HOSTS || '')
  .split(',')
  .map(s => s.trim().replace(/^https?:\/\//, '').split('/')[0].toLowerCase())
  .filter(Boolean);

// ═══════════════════════════════════════════════════════
// PreviewConnectLine — 松手后"留住"的预览连线
//   起点存画布坐标(startFlow)，订阅 useViewport 每次缩放/平移都重算屏幕坐标，
//   所以起点始终贴着源节点边线；终点(endX/endY)是菜单左边线的固定屏幕坐标。
function PreviewConnectLine({ sourceId, endX, endY }) {
  const { flowToScreenPosition } = useReactFlow();
  useViewport(); // 订阅视口：缩放/平移时强制重渲染，起点跟着重算
  // 📱 订阅视觉视口变化（iPad 键盘进出/页面被拖动）→ 强制重渲染，重算下方的视觉视口补偿
  const [, forceVV] = useState(0);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    // 📱 iPad Chrome：输入框 focus 弹键盘时，即便 body position:fixed，浏览器仍会把布局/视觉
    //   视口顶起产生残留偏移(vv.offsetTop / window.scrollY 非零)→ 能出滚动条、拖 logo 窜位、
    //   收键盘后留间隙错位。Safari 用 dvh 自动收缩视口所以无此问题。这里在每次视口变化后
    //   把页面强制归位(scrollTo 0,0)，抹掉残留偏移。用 rAF 等浏览器自身的滚动动作先落定。
    const resetScroll = () => {
      if (window.scrollX !== 0 || window.scrollY !== 0) window.scrollTo(0, 0);
      if (document.scrollingElement && document.scrollingElement.scrollTop !== 0) {
        document.scrollingElement.scrollTop = 0;
        document.scrollingElement.scrollLeft = 0;
      }
    };
    const on = () => { forceVV((n) => n + 1); requestAnimationFrame(resetScroll); };
    vv.addEventListener('resize', on);
    vv.addEventListener('scroll', on);
    return () => { vv.removeEventListener('resize', on); vv.removeEventListener('scroll', on); };
  }, []);
  // ⚠️ 起点不用快照，而是每次渲染都从 store 实时读源节点的 handleBounds 算——
  //   与真实连线【同一份活数据】。剧本大师横/竖屏切换会改节点尺寸+重量 handleBounds，
  //   若用 onConnectEnd 那一刻的快照就会拿到旧值（那时还没量好）→ 起点不准。
  const startFlow = useStore((s) => {
    const node = s.nodeInternals.get(sourceId);
    if (!node) return null;
    const pos = node.positionAbsolute || node.position;
    const h = node[Symbol.for('internals')]?.handleBounds?.source?.[0];
    if (pos && h) return { x: pos.x + h.x + h.width / 2, y: pos.y + h.y + h.height / 2 };
    if (pos && node.width != null) return { x: pos.x + node.width, y: pos.y + (node.height || 0) / 2 };
    return null;
  });
  if (!startFlow) return null;
  // 画布坐标 → 窗口坐标：用 flowToScreenPosition（自带 ReactFlow 容器相对窗口的偏移）。
  //   ⚠️ 不能手算 flow*zoom+viewport.x：viewport 是相对【容器】的，而本 SVG 是 fixed 相对
  //   【窗口】。剧本大师侧边栏(拼屏)会把容器整体右推，手算就差一个面板宽度（拖动面板差距还变、
  //   关掉才归零）。flowToScreenPosition 返回窗口坐标，与终点 endX/endY(clientX/Y) 统一。
  const s = flowToScreenPosition({ x: startFlow.x, y: startFlow.y });
  // 📱 视觉视口补偿：iPad 键盘进出/页面被拖动后，视觉视口相对布局视口有偏移。
  //   flowToScreenPosition/clientX 是相对视觉视口的，而本 SVG 是 fixed(相对布局视口)，
  //   两者差 visualViewport.offsetTop/Left → 端点偏移。把这个偏移补进来即对齐。
  const vv = typeof window !== 'undefined' ? window.visualViewport : null;
  const ox = vv ? vv.offsetLeft : 0;
  const oy = vv ? vv.offsetTop : 0;
  const sx = s.x + ox, sy = s.y + oy;
  const ex = endX + ox, ey = endY + oy;
  const dx = Math.abs(ex - sx) * 0.5;
  return (
    <svg style={{ position: 'fixed', inset: 0, width: '100vw', height: '100vh', pointerEvents: 'none', zIndex: 999 }}>
      <path
        d={`M ${sx},${sy} C ${sx + dx},${sy} ${ex - dx},${ey} ${ex},${ey}`}
        fill="none" stroke={C.EDGE_COLOR_MANUAL} strokeWidth={1.5}
      />
      {/* 终点小绿点：与节点连接点上的绿点统一（6px 实心 #22c55e），标记连线落点 */}
      <circle cx={ex} cy={ey} r={3} fill="#22c55e" />
    </svg>
  );
}

// ── 预加载图片尺寸的工具函数 ──
const loadMediaDimensions = (url) => {
  return new Promise((resolve) => {
    const img = new Image();
    const timeout = setTimeout(() => {
      console.warn(`图片加载超时: ${url}`);
      resolve({ width: 800, height: 600 }); // 超时返回默认尺寸
    }, 5000);

    img.onload = () => {
      clearTimeout(timeout);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      clearTimeout(timeout);
      resolve({ width: 800, height: 600 });
    };
    img.src = url;
  });
};

// EditorContent — 编辑器核心（必须在 ReactFlowProvider 内）
// ═══════════════════════════════════════════════════════
function EditorContent() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser, hasMemberFeature, membershipLevel } = useAuth();
  const { stopLoading, startLoading } = useProjectLoading(); // 🆕 全局加载状态管理
  // 🎨 主题：ReactFlow Background 的 color 走 SVG fill，不吃 CSS var，故由 JS 读主题给点色
  const [theme] = useTheme();

  // ── 积分弹窗 ──
  const [isCreditLogsOpen, setIsCreditLogsOpen] = useState(false);
  const [isRechargeOpen, setIsRechargeOpen] = useState(false);
  const [isVoucherOpen, setIsVoucherOpen] = useState(false); // 🎓 教育版算力券兑换

  // 🏷️ 有效期徽章全局开关
  const [showExpiryBadge, setShowExpiryBadge] = useState(false);
  useEffect(() => {
    api.get('/api/settings/show-expiry-badge').then(data => {
      setShowExpiryBadge(!!data.enabled);
    }).catch(() => {});
  }, []);

  // 💳 监听 NodeConsole 事件
  useEffect(() => {
    const handler = () => setIsRechargeOpen(true);
    window.addEventListener('open-recharge', handler);
    return () => window.removeEventListener('open-recharge', handler);
  }, []);

  // 🎓 教育版：积分明细里的「兑换」按钮走这个事件
  useEffect(() => {
    const handler = () => setIsVoucherOpen(true);
    window.addEventListener('open-voucher-redeem', handler);
    return () => window.removeEventListener('open-voucher-redeem', handler);
  }, []);

  // 💰 监听积分明细打开事件（节点积分价格点击触发）
  useEffect(() => {
    const handler = () => { refreshUser(); setIsCreditLogsOpen(true); };
    window.addEventListener('open-credit-logs', handler);
    return () => window.removeEventListener('open-credit-logs', handler);
  }, [refreshUser]);

  // ── 音频预加载 ──
  const { playCompletionSound, playErrorSound } = useAudioPreload();

  // 🆕 优先级队列（全局单例，组件级别）
  const priorityQueueRef = useRef(null);
  if (!priorityQueueRef.current) {
    priorityQueueRef.current = new PriorityQueue(6); // 并发数 6
  }
  const priorityQueue = priorityQueueRef.current;

  // ── 刷新用户信息 ──
  useEffect(() => { refreshUser(); }, [refreshUser]);

  // ── 预加载自定义字体（确保画布上的标注文字能正确渲染字体） ──
  useEffect(() => { ensureCustomFontsLoaded(); }, []);

  // ── 本地缓存辅助 ──
  const cacheFileLocally = useCallback(async (fileUrl, fileType, currentCrewId, currentCrewName) => {
    try {
      if (!currentCrewId) return null;
      const result = await localCacheService.getLocalUrlOrFetch(fileUrl, currentCrewId, currentCrewName, fileType);
      return result;
    } catch (err) {
      console.warn('本地缓存写入失败（不影响正常使用）:', err);
      return null;
    }
  }, []);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // 核心状态
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  // 拖动对齐辅助线（画布坐标），undefined 表示该方向无对齐线
  const [helperLines, setHelperLines] = useState({ horizontal: undefined, vertical: undefined });
  const [restoredViewport, setRestoredViewport] = useState(null);
  const [projectName, setProjectName] = useState("加载中...");
  const [crewName, setCrewName] = useState("加载中...");
  // 品牌配置（3→1 合并，减少独立 re-render 触发点）
  const [branding, setBranding] = useState({ logo: null, color: '#ffffff', opacity: 0.3 });
  const [crewId, setCrewId] = useState(null);
  const [crewRole, setCrewRole] = useState(null); // 'producer' | 'director'
  const [crewMembers, setCrewMembers] = useState([]);
  const [isChatOpen, setIsChatOpen] = useState(false);
  // 聊天目标：null = 公共聊天室 | userId(number) = 一对一私聊
  // 作为受控状态传递给 ChatWindow（chatTarget + onChatTargetChange）
  const [chatTargetId, setChatTargetId] = useState(null);

  const [posterCropUrl, setPosterCropUrl] = useState(null); // 海报裁剪图片
  const [isEditingName, setIsEditingName] = useState(false);
  const [tempName, setTempName] = useState("");
  const [contextMenu, _setContextMenu] = useState(null);
  const [menuData, _setMenuData] = useState(null);
  // 🛡️ ref-tracked 包装（必须在 useNodeActions 之前声明，避免 TDZ 错误）
  const contextMenuRef = useRef(null);
  const menuDataRef = useRef(null);
  // 📱 iPad 长按右键菜单：画布主区 DOM 引用 + 「抑制下次点击」标记。
  //   长按触发即置 true，一直挡到下一次触摸开始（新手势 touchstart 才清零）——
  //   不因单次 click 复位，故松手后连来几个合成点击都挡得住，且不受按住时长影响。
  const flowAreaRef = useRef(null);
  const suppressClickRef = useRef(false);
  // 📱 剧本大师分屏分隔条 DOM 引用（挂原生非 passive touchstart，触屏才能拖动分屏）
  const dividerRef = useRef(null);
  const gripRef = useRef(null);
  // 🔗 连线交互追踪：记录本次拖拽的起点，以及是否已成功连上目标节点。
  //   单击端口 = 一次「起点=终点、没连上」的连接，松手时若 made=false 就弹菜单。
  const connectRef = useRef({ nodeId: null, handleType: null, made: false });
  const setContextMenu = useCallback((val) => { contextMenuRef.current = val; _setContextMenu(val); }, []);
  const setMenuData = useCallback((val) => { menuDataRef.current = val; _setMenuData(val); }, []);
  const dismissMenus = useCallback(() => {
    if (contextMenuRef.current !== null) setContextMenu(null);
    if (menuDataRef.current !== null) setMenuData(null);
  }, [setContextMenu, setMenuData]);

  const [selectedNodes, setSelectedNodes] = useState([]);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  // 🎬 分镜草图：工具栏按钮 hover/click 时展开宫格选择条
  const [storyboardPickerOpen, setStoryboardPickerOpen] = useState(false);
  // 🎬 剧本大师分屏面板：layout = null(关闭) | 'left' | 'bottom'
  const [modelMasterLayout, setModelMasterLayout] = useState(null);
  const [modelMasterMenuOpen, setModelMasterMenuOpen] = useState(false);
  const [modelMasterSize, setModelMasterSize] = useState(() => {
    const saved = parseInt(localStorage.getItem('modelMasterSize'), 10);
    return Number.isFinite(saved) && saved > 0 ? saved : 420; // 左侧为宽度 px，底部为高度 px
  });
  const [saveModalData, setSaveModalData] = useState(null);
  const [globalAssets, setGlobalAssets] = useState([]);
  const [publicAssets, setPublicAssets] = useState([]);
  const [globalSpells, setGlobalSpells] = useState([]);
  // 全局咒语分类排序（来自服务端 settings.global_spell_order）
  const [spellCategoryOrder, setSpellCategoryOrder] = useState([]);
  const [canDeletePublic, setCanDeletePublic] = useState(false);
  const [activeTab, setActiveTab] = useState('project');
  const { alertMsg, setAlertMsg } = useAlert();
  const [isInitialized, setIsInitialized] = useState(false);
  const [coverCropUrl, setCoverCropUrl] = useState(null);
  const [allProjects, setAllProjects] = useState([]);
  const [coverProject, setCoverProject] = useState(null);
  const [projectImages, setProjectImages] = useState([]);

  // 🔗 多节点连接状态
  const [multiSelectConnect, setMultiSelectConnect] = useState({
    isActive: false,
    sourceNodeIds: [],
    startPos: { x: 0, y: 0 },
    currentMousePos: { x: 0, y: 0 }
  });
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // 缓存状态（提取为独立 hook，减少 EditorContent 的 state 数量）
  const {
    cacheInvalid, setCacheInvalid,
    cacheReconfiguring, setCacheReconfiguring,
    showCacheConfirm, setShowCacheConfirm,
    validateAfterLoad,
  } = useCacheStatus();

  // ── Refs ──
  const nameInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const replaceInputRef = useRef(null);
  const activeBlobsRef = useRef(new Set());
  const clipboardRef = useRef([]);
  const globalAssetsRef = useRef([]);
  const lastUploadPosRef = useRef(null);
  const nodesRef = useRef(nodes);
  useEffect(() => { nodesRef.current = nodes; }, [nodes]);
  const callbacksRef = useRef({});
  const publicAssetsRef = useRef([]);
  const globalSpellsRef = useRef([]);
  const spellDiagSeqRef = useRef(0);
  const spellDiagLastSigRef = useRef('');
  const crewIdRef = useRef(null);
  const crewNameRef = useRef(null);
  const skipNextAutoSaveRef = useRef(false);
  const projectVersionRef = useRef(0);
  // 🛡️ 数据就绪标志：只有 Plan B（服务端权威数据）加载完成后才允许触发自动保存。
  //   防御场景：Plan A 缓存秒开 → IndexedDB 里是旧的不完整 nodes（80）+ 旧 version；
  //   Plan B 拉到 96 节点之前若 dirty 被触发，会把 80 节点 + 旧 version 写回服务器，
  //   而服务端的 50% 骤降保护对 96→80（17%）不生效，旧数据成功覆盖新数据。
  //   切项目时必须重置回 false，避免上一个项目的就绪态污染下一个项目的初始化期。
  const isDataReadyRef = useRef(false);
  const prevCrewIdRef = useRef(null);

  // 🆕 同步 predictivePreloader 的节点列表（当 nodes 变化时更新）
  useEffect(() => {
    predictivePreloader.updateNodes(nodes);
  }, [nodes]);

  // 🔄 可见性 revalidate：tab 长时间隐藏后回来,强制重拉服务端权威数据,
  //    避免本地 ReactFlow store 已成陈旧、其他会话已编辑过该项目。
  const [revalidateNonce, setRevalidateNonce] = useState(0);
  const tabHiddenSinceRef = useRef(0);

  // 🔒 单 tab 编辑锁：避免多个标签页同时挂着同一 projectId 时,
  //    陈旧 ReactFlow store 在某次保存时把最新内容覆盖回旧版本。
  //    后到先得：新打开的 tab 接管,旧 tab 自动只读。
  const { isReadOnly: isEditorReadOnly, takeOver: takeOverEditor } = useEditorTabLock(projectId);
  const isReadOnlyRef = useRef(false);
  useEffect(() => { isReadOnlyRef.current = isEditorReadOnly; }, [isEditorReadOnly]);

  useEffect(() => {
    const onVis = () => {
      if (document.hidden) {
        tabHiddenSinceRef.current = Date.now();
      } else {
        const hiddenFor = tabHiddenSinceRef.current ? Date.now() - tabHiddenSinceRef.current : 0;
        tabHiddenSinceRef.current = 0;
        if (hiddenFor > 30000 && projectId) {
          console.log(`🔄 tab 隐藏 ${Math.round(hiddenFor / 1000)}s 后恢复, 触发 revalidate`);
          isDataReadyRef.current = false;
          projectVersionRef.current = -1;
          setRevalidateNonce(n => n + 1);
        }
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [projectId]);

  // 🩺 临时诊断：剧本库/咒语重复来源追踪（仅日志，不改业务逻辑）
  const logSpellLibraryDiag = useCallback((reason, projectList = [], publicList = [], globalList = []) => {
    const pickSpellRows = (list, source) => (Array.isArray(list) ? list : [])
      .filter(a => a && (a.type === 'text' || a.category === 'prompt'))
      .map(a => {
        const text = String(a.text || a?.data?.text || a.url || '').trim();
        const title = String(a.title || '').trim();
        const key = `${title}||${text}`;
        return {
          id: a.id,
          title,
          text,
          key,
          source,
          isPublic: !!a.isPublic,
          isSystem: !!a.isSystem,
          createdBy: a.createdBy ?? a.created_by ?? null,
        };
      });

    const rows = [
      ...pickSpellRows(projectList, 'project'),
      ...pickSpellRows(publicList, 'public'),
      ...pickSpellRows(globalList, 'global'),
    ];
    if (rows.length === 0) return;

    const sig = rows
      .map(r => `${r.source}:${r.id}:${r.title}:${r.text.length}:${r.isPublic ? 1 : 0}:${r.isSystem ? 1 : 0}`)
      .sort()
      .join('|');
    if (sig === spellDiagLastSigRef.current) return;
    spellDiagLastSigRef.current = sig;

    const grouped = new Map();
    rows.forEach(r => {
      if (!grouped.has(r.key)) grouped.set(r.key, []);
      grouped.get(r.key).push(r);
    });

    const duplicates = Array.from(grouped.entries())
      .filter(([, list]) => list.length > 1)
      .map(([key, list]) => ({
        key,
        count: list.length,
        sources: Array.from(new Set(list.map(i => i.source))).join('+'),
        ids: list.map(i => i.id).join(','),
        titles: Array.from(new Set(list.map(i => i.title))).join(' | '),
        isSystemFlags: list.map(i => i.isSystem ? '1' : '0').join(','),
        isPublicFlags: list.map(i => i.isPublic ? '1' : '0').join(','),
      }));

    spellDiagSeqRef.current += 1;
  }, []);

  // ── ReactFlow 实例 ──
  const {
    getNodes, screenToFlowPosition, setEdges: setFlowEdges,
    deleteElements, getEdges, getViewport, setViewport
  } = useReactFlow();
  const { takeSnapshot, undo, redo, canUndo, canRedo } = useUndoRedo(projectId);

  // ── Ref 同步 ──
  useEffect(() => { globalAssetsRef.current = globalAssets; }, [globalAssets]);
  useEffect(() => { publicAssetsRef.current = publicAssets; }, [publicAssets]);
  useEffect(() => { globalSpellsRef.current = globalSpells; }, [globalSpells]);
  useEffect(() => { crewIdRef.current = crewId; }, [crewId]);
  useEffect(() => { crewNameRef.current = crewName; }, [crewName]);

  // 🩺 临时诊断：任何资产列表变化都打印一次咒语来源快照
  useEffect(() => {
    logSpellLibraryDiag('assets-state-changed', globalAssets, publicAssets, globalSpells);
  }, [globalAssets, publicAssets, globalSpells, logSpellLibraryDiag]);

  // ── 加载项目组成员 ──
  useEffect(() => {
    if (!crewId) { setCrewMembers([]); return; }
    let cancelled = false;
    const loadMembers = async () => {
      try {
        const data = await api.get(`/api/crews/${crewId}/members`);
        if (!cancelled) setCrewMembers(data.members || []);
      } catch (err) { console.warn('加载项目组成员失败:', err); }
    };
    loadMembers();
    return () => { cancelled = true; };
  }, [crewId]);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // 品牌配置（右角标）
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  useEffect(() => {
    const loadBranding = async () => {
      try {
        const data = await api.get('/api/settings/login-branding');
        if (data && data.cornerLogo) {
          setBranding({ logo: data.cornerLogo, color: data.cornerLogoColor || '#ffffff', opacity: data.cornerLogoOpacity ?? 0.3 });
        }
      } catch (err) { console.warn('加载右角标配置失败:', err); }
    };
    loadBranding();
  }, []);

  // ── 视口恢复 ──
  useEffect(() => {
    if (restoredViewport && isInitialized) {
      requestAnimationFrame(() => setViewport(restoredViewport, { duration: 0 }));
    }
  }, [restoredViewport, isInitialized, setViewport]);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // WebSocket
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const { socketRef } = useWebSocket({
    projectId, setNodes, setEdges, setAlertMsg, setAllProjects,
    playCompletionSound, playErrorSound, cacheFileLocally,
    crewIdRef, crewNameRef, skipNextAutoSaveRef, projectVersionRef, isDataReadyRef,
    user, refreshUser,
  });

  // 未读消息计数 + 提示音（复用 useWebSocket 的 socket，不创建额外连接）
  const { unreadCounts, markRead } = useChatNotifications({
    crewId,
    user,
    isChatOpen,
    chatTargetId,
    socketRef,
  });

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // 加载项目数据 — 图板切换核心流程
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  //
  // 加载策略（3 层加速）：
  //   Plan A —— 缓存秒开：从 IndexedDB 读取上次保存的节点/边/视口，直接渲染
  //             （不显示 loading 遮罩，用户感知零等待）
  //   Plan B —— API 并行：4 个 API 请求（项目数据/素材/公共素材/项目列表）
  //             全部 Promise.all 并行发出（原为 1 串行 + 3 并行）
  //   图片缓存 —— L0+L1 快速解析：渲染前批量查询内存 + IndexedDB 中的图片 blob
  //             命中即带 localCacheUrl 渲染，避免「空白→shimmer→出图」的跳闪
  //
  // 数据流程：
  //   ┌─────────────────────────────────────────────────────────────────────┐
  //   │ IndexedDB 有缓存？                                                  │
  //   │   ├─ 是 → 注入回调 → resolveLocalOnly → setNodes → 画布秒开          │
  //   │   └─ 否 → 显示 loading 遮罩                                         │
  //   │                                                                     │
  //   │ 4 个 API 并行请求 ─────────────────────────────────────────────────  │
  //   │   → 清洗节点数据（去 blob/data URL、去上传中状态）                     │
  //   │   → 批量 resolveLocalOnly（L0+L1 图片缓存）                          │
  //   │   → setNodes + setEdges（最终渲染）                                  │
  //   │   → 写入 IndexedDB 缓存（供下次秒开）                                │
  //   │   → 后台异步补充 L2+L3 缓存（网络/文件系统）                          │
  //   └─────────────────────────────────────────────────────────────────────┘
  //
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    // 🛡️ 切换项目立即关闭"数据就绪"，必须等 Plan B 服务端数据回来才允许自动保存触发
    isDataReadyRef.current = false;
    const loadProjectData = async () => {
      // ⏱️ 性能基准测试
      const perfTimers = {
        start: performance.now(),
        planA_start: 0,
        planA_end: 0,
        planB_start: 0,
        planB_end: 0,
        rendering_start: 0,
        rendering_end: 0,
        cacheResolution_start: 0,
        cacheResolution_end: 0,
      };

      try {
        // ──────────────────────────────────────────
        // ⚡ Plan A: 缓存秒开
        // ──────────────────────────────────────────
        // 从 IndexedDB（projectCacheService）读取上次渲染的节点/边/视口，
        // 直接 setNodes/setEdges + setIsInitialized(true)，
        // 画布立即可见、可交互（不显示 loading 遮罩）。
        // 缓存写入时机：
        //   1. 服务器数据加载完成后（本函数下方）
        //   2. 每次 autoSave / 手动保存成功后（useProjectSave.js）
        // ──────────────────────────────────────────
        perfTimers.planA_start = performance.now();
        let cacheRendered = false;
        const cached = await projectCacheService.get(projectId);
        perfTimers.planA_end = performance.now();
        if (cached && !cancelled) {
          // 缓存中的节点只有纯数据（函数在写入时已去除），这里重新注入回调
          const cachedNodes = (cached.nodes || []).map(node => ({
            ...node,
            data: {
              ...node.data,
              localCacheUrl: undefined,   // 下一步 resolveLocalOnly 填入
              useLoadingEffect: false,    // 缓存节点不需要入场动画
              onUpdate: (...args) => callbacksRef.current.onUpdate?.(...args),
              onDelete: (...args) => callbacksRef.current.onDelete?.(...args),
              onGenerate: (...args) => callbacksRef.current.onGenerate?.(...args),
              onDetach: (...args) => callbacksRef.current.onDetach?.(...args),
              onDownscaleInPlace: (...args) => callbacksRef.current.onDownscaleInPlace?.(...args),
              onReupload: (...args) => callbacksRef.current.onReupload?.(...args),
              onReplace: (...args) => callbacksRef.current.onReplace?.(...args),
              onUploadAndReplace: (...args) => callbacksRef.current.onUploadAndReplace?.(...args),
              onRemoveRefFile: (...args) => callbacksRef.current.onRemoveRefFile?.(...args),
              onRemoveRefText: (...args) => callbacksRef.current.onRemoveRefText?.(...args),
              onRemoveRefAudio: (...args) => callbacksRef.current.onRemoveRefAudio?.(...args),
              onSlashAssetSpawn: (...args) => callbacksRef.current.onSlashAssetSpawn?.(...args),
              onHandleDoubleClick: (...args) => callbacksRef.current.onHandleDoubleClick?.(...args),
              onDisconnectDownstream: (...args) => callbacksRef.current.onDisconnectDownstream?.(...args),
              onDescribe: (...args) => callbacksRef.current.onDescribe?.(...args),
              onDescribeComplete: (...args) => callbacksRef.current.onDescribeComplete?.(...args),
              getMjDescribeConfig: () => callbacksRef.current.mjDescribeConfigRef?.current,
              onApplyPreset: (...args) => callbacksRef.current.onApplyPreset?.(...args),
              onApplySkillbox: (...args) => callbacksRef.current.onApplySkillbox?.(...args),
              getPresets: () => callbacksRef.current.presetsRef?.current || { toolsets: [] },
              getSkillbox: () => callbacksRef.current.skillboxRef?.current || { toolsets: [] },
              globalAssetsRef,
              publicAssetsRef,
              globalSpellsRef,
            }
          }));

          // L0+L1+L2 快速图片缓存解析（不走网络）：
          //   缓存里如果存了 crewId/crewName，可在此直接查 L2 本地文件夹（无权限弹窗），
          //   消除「Plan A 渲染走 L0+L1 miss → Plan B 才命中 L2」的双闪问题
          const toResolve = cachedNodes.filter(n => n.data?.output && !n.data.output.startsWith('blob:') && !n.data.output.startsWith('data:'));
          if (toResolve.length > 0 && !cancelled) {
            const cachedCrewId = cached.crewId || null;
            const cachedCrewName = cached.crewName || null;
            const results = await Promise.all(
              toResolve.map(async (n) => {
                const ft = n.data.type === 'video' ? 'video'
                         : n.data.type === 'audio' ? 'audio' : 'image';
                const url = await localCacheService.resolveLocalOnly(n.data.output, {
                  crewId: cachedCrewId,
                  crewName: cachedCrewName,
                  fileType: ft,
                });
                return { id: n.id, url };
              })
            );
            for (const { id, url } of results) {
              if (url) {
                const node = cachedNodes.find(n => n.id === id);
                if (node) {
                  node.data.localCacheUrl = url;
                  if (node.data.output?.startsWith('local-cache://')) node.data.uploading = false;
                }
              }
            }
          }

          perfTimers.rendering_start = performance.now();
          const loadingIds = new Set(cachedNodes.filter(n => n.data?.loading).map(n => n.id));
          setNodes(cachedNodes);
          setEdges((cached.edges || []).map(e => ({ ...e, data: { ...e.data, isTransmitting: loadingIds.has(e.target) } })));
          if (cached.viewport && Object.keys(cached.viewport).length > 0) setRestoredViewport(cached.viewport);
          else setRestoredViewport(null);
          // 🛡️ 不能用 cached.version 覆盖 projectVersionRef！
          //   IndexedDB 缓存可能比服务器陈旧（其他设备/会话写过新版本），
          //   缓存秒开期间若把旧 version 暴露出去，配合任意 dirty 触发，
          //   就会绕过乐观锁把 80 节点写回服务器（OCC: WHERE version=旧 → 命中→写入成功）。
          //   先标记一个"陈旧版本"哨兵，等 Plan B 拉到权威版本号再赋值真值。
          projectVersionRef.current = -1;
          setIsInitialized(true);
          stopLoading(); // 🆕 缓存秒开完成，关闭加载反馈
          cacheRendered = true;
          perfTimers.rendering_end = performance.now();
        }

        // 无缓存时才显示 loading 遮罩（有缓存时画布已在上面秒开了）
        if (!cacheRendered) setIsInitialized(false);

        // ──────────────────────────────────────────
        // ⚡ Plan B: API 全并行
        // ──────────────────────────────────────────
        // 原方案：先串行请求 /api/projects/:id，拿到 crew_id 后
        //         再并行请求 assets、publicAssets、allProjects（总计 ~350ms）
        // 新方案：4 个请求同时发出 Promise.all，由 HTTP/2 多路复用，
        //         总耗时 = max(单个最慢) ≈ 200ms
        // ──────────────────────────────────────────
        perfTimers.planB_start = performance.now();
        const [projectData, assetsData, publicData, globalData, allProjectsData] = await Promise.all([
          api.get(`/api/projects/${projectId}`),
          api.get(`/api/assets?projectId=${projectId}`),
          api.get(`/api/assets?isPublic=true`),
          api.get(`/api/assets?isGlobal=true`),
          api.get(`/api/projects?limit=100`)
        ]);
        perfTimers.planB_end = performance.now();

        if (cancelled) return;

        const newCrewId = projectData.crew_id || null;
        if (prevCrewIdRef.current !== null && prevCrewIdRef.current !== newCrewId) {
          localCacheService.clearBlobUrlCache();
        }
        prevCrewIdRef.current = newCrewId;

        setProjectName(projectData.name || '未命名项目');
        setTempName(projectData.name || '未命名项目');
        setCrewName(projectData.crew_name || '默认项目');
        setCrewId(newCrewId);
        setCrewRole(projectData.crew_role || null);

        const normalizedAssets = (assetsData.assets || []).map(normalizeAsset);
        setGlobalAssets(normalizedAssets);
        globalAssetsRef.current = normalizedAssets;

        const normalizedPublic = (publicData.assets || []).map(normalizeAsset);
        setPublicAssets(normalizedPublic);
        publicAssetsRef.current = normalizedPublic;

        const normalizedGlobal = (globalData.assets || []).map(normalizeAsset);
        setGlobalSpells(normalizedGlobal);
        globalSpellsRef.current = normalizedGlobal;

        // 拉取全局咒语分类的持久化排序（失败不影响其他数据）
        api.get('/api/assets/global-spells/order')
          .then(r => { if (!cancelled && Array.isArray(r?.order)) setSpellCategoryOrder(r.order); })
          .catch(() => {});

        logSpellLibraryDiag('api-load', normalizedAssets, normalizedPublic, normalizedGlobal);
        setCanDeletePublic(publicData.canDeletePublic || false);

        setAllProjects(Array.isArray(allProjectsData) ? allProjectsData : (allProjectsData.projects || []));

        projectVersionRef.current = projectData.version || 0;

        const isBlobOrData = (u) => u && (u.startsWith('blob:') || u.startsWith('data:'));

        const loadedNodes = (projectData.nodes || []).map(node => {
          // 清理数据库中残留的过期 blob/data URL
          let { output, outputs, refFiles, refAudios, uploading, title, ...restData } = node.data || {};
          if (isBlobOrData(output)) output = null;
          if (Array.isArray(outputs)) outputs = outputs.filter(u => !isBlobOrData(u));
          if (!output && outputs?.length > 0) output = outputs[0];
          if (Array.isArray(refFiles)) refFiles = refFiles.map(f => isBlobOrData(f?.url) ? { ...f, url: null } : f);
          if (Array.isArray(refAudios)) refAudios = refAudios.map(a => isBlobOrData(a?.url) ? { ...a, url: null } : a);

          // 🛡️ 清理上次会话残留的临时上传状态（切换项目时保存被拦截，导致数据库错误保留了这些瞬态）
          if (uploading === true) uploading = false;
          if (typeof title === 'string' && title.startsWith('上传中...')) {
            title = title.replace(/^上传中\.\.\.\s*/, '') || undefined;
          }

          // 🛡️ 清理僵尸裁剪节点：浏览器端 FFmpeg.wasm 裁剪中途页面刷新/切走后，
          //    clipping=true + 无 output 的节点会永远卡在"正在裁剪..."，需要自动恢复。
          //    (踩坑 2026-04-21: 大视频 WASM 重编码慢，用户等不及切走导致僵尸节点)
          if (restData.clipping === true && !output) {
            restData.clipping = false;
            restData.loading = false;
            restData.statusInfo = null;
            restData.error = '裁剪中断（页面刷新），请重新裁剪';
          }

          return {
            ...node,
            data: {
              ...restData,
              output, outputs, refFiles, refAudios, uploading, title,
              // ⚡ localCacheUrl 暂时留空，下一步批量解析后再填入
              localCacheUrl: undefined,
              // 🛡️ 从数据库加载的节点不需要入场动画，清除残留的 useLoadingEffect
              useLoadingEffect: false,
              onUpdate: (...args) => callbacksRef.current.onUpdate?.(...args),
              onDelete: (...args) => callbacksRef.current.onDelete?.(...args),
              onGenerate: (...args) => callbacksRef.current.onGenerate?.(...args),
              onDetach: (...args) => callbacksRef.current.onDetach?.(...args),
              onDownscaleInPlace: (...args) => callbacksRef.current.onDownscaleInPlace?.(...args),
              onReupload: (...args) => callbacksRef.current.onReupload?.(...args),
              onReplace: (...args) => callbacksRef.current.onReplace?.(...args),
              onUploadAndReplace: (...args) => callbacksRef.current.onUploadAndReplace?.(...args), // 🔧 旋转/翻转后上传
              onRemoveRefFile: (...args) => callbacksRef.current.onRemoveRefFile?.(...args),
              onRemoveRefText: (...args) => callbacksRef.current.onRemoveRefText?.(...args),
              onRemoveRefAudio: (...args) => callbacksRef.current.onRemoveRefAudio?.(...args),
              onSlashAssetSpawn: (...args) => callbacksRef.current.onSlashAssetSpawn?.(...args),
              onHandleDoubleClick: (...args) => callbacksRef.current.onHandleDoubleClick?.(...args),
              onDisconnectDownstream: (...args) => callbacksRef.current.onDisconnectDownstream?.(...args), // 🔧 断开下游
              onDescribe: (...args) => callbacksRef.current.onDescribe?.(...args),
              onDescribeComplete: (...args) => callbacksRef.current.onDescribeComplete?.(...args),
              getMjDescribeConfig: () => callbacksRef.current.mjDescribeConfigRef?.current,
              onApplyPreset: (...args) => callbacksRef.current.onApplyPreset?.(...args),
              onApplySkillbox: (...args) => callbacksRef.current.onApplySkillbox?.(...args),
              getPresets: () => callbacksRef.current.presetsRef?.current || { toolsets: [] },
              getSkillbox: () => callbacksRef.current.skillboxRef?.current || { toolsets: [] },
              globalAssetsRef,
              publicAssetsRef,
              globalSpellsRef,
              // ⚠️ 此回调列表必须与 callbacksRef.current 和 buildNodeData 保持一致
            }
          };
        });

        // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
        // ⚡ 先缓存后渲染——在首次 setNodes 之前批量解析本地缓存
        // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
        // 设计思路：
        //   项目 JSON 中的 output 是 OSS / local-cache:// URL，浏览器无法直接秒显示。
        //   如果先渲染再异步解析缓存（旧方案），用户会看到空白 → shimmer → 图片出现 的跳闪。
        //   新方案：渲染前先用 resolveLocalOnly() 批量查询 L0 内存 + L1 IndexedDB，
        //   命中的节点直接带着 blob:// localCacheUrl 初始渲染，实现「秒开」效果。
        //
        // 性能保证：
        //   - resolveLocalOnly() 仅检查内存 Map + IndexedDB，绝不访问网络
        //   - 所有节点并行 Promise.all（IndexedDB 事务开销 ~5-50ms/节点，批量约 50-200ms）
        //   - Cache miss 的节点 localCacheUrl 仍为 undefined，渲染时走 OSS URL fallback
        //   - Cache miss 部分由后台异步补充（第二阶段），补充后 patch setNodes
        //
        // 注意：这里故意不用 runWithLimit 限流，因为 resolveLocalOnly 不涉及网络请求，
        //   IndexedDB 并发读取不会造成压力，Promise.all 可以最快完成。
        perfTimers.cacheResolution_start = performance.now();
        const outputsToResolve = loadedNodes
          .filter(n => n.data?.output && !n.data.output.startsWith('blob:') && !n.data.output.startsWith('data:'));

        if (outputsToResolve.length > 0 && !cancelled) {
          // ⚡ L2 本地文件夹查找需要 crewId/crewName/fileType（仅在用户已授权目录时生效）
          // 配过 L2 的用户首屏即可直接出图，避免「OSS 占位 → blob 切换」的两次闪烁
          const earlyCrewId = projectData.crew_id;
          const earlyCrewName = projectData.crew_name || '默认项目';
          const cacheResults = await Promise.all(
            outputsToResolve.map(async (node) => {
              const ft = node.data.type === 'video' ? 'video'
                       : node.data.type === 'audio' ? 'audio' : 'image';
              const blobUrl = await localCacheService.resolveLocalOnly(node.data.output, {
                crewId: earlyCrewId,
                crewName: earlyCrewName,
                fileType: ft,
              });
              return { nodeId: node.id, blobUrl };
            })
          );

          // 将命中的 blob URL 写入 loadedNodes
          const cacheMap = new Map();
          for (const { nodeId, blobUrl } of cacheResults) {
            if (blobUrl) cacheMap.set(nodeId, blobUrl);
          }
          if (cacheMap.size > 0) {
            for (const node of loadedNodes) {
              const cached = cacheMap.get(node.id);
              if (cached) {
                node.data.localCacheUrl = cached;
                // local-cache:// 节点如果已有缓存 blob，无需继续显示 uploading 状态
                if (node.data.output?.startsWith('local-cache://')) {
                  node.data.uploading = false;
                }
              }
            }
          }
        }
        perfTimers.cacheResolution_end = performance.now();

        const loadedEdges = projectData.edges || [];

        const loadingNodeIds = new Set(loadedNodes.filter(n => n.data?.loading === true).map(n => n.id));
        const edgesWithTransmitting = loadedEdges.map(edge => ({
          ...edge, data: { ...edge.data, isTransmitting: loadingNodeIds.has(edge.target) }
        }));

        // 🔔 如果有节点仍在生成中，提示用户任务在后台继续运行
        if (loadingNodeIds.size > 0) {
          setAlertMsg({ type: 'info', text: `${loadingNodeIds.size} 个生成任务正在后台运行中，完成后会自动更新` });
        }

        // ⚡ 此时 localCacheUrl 已尽可能填好，首次渲染即可显示本地缓存的图片/视频
        // 🔧 对 loading 中的节点，保留前端已有的 statusInfo/generationStartTime（WebSocket 实时推送值）
        // 避免 Plan B 覆盖掉比 DB 更新的进度状态，导致计时器/进度条消失
        setNodes(currentNodes => {
          const currentMap = new Map(currentNodes.map(n => [n.id, n]));
          return loadedNodes.map(n => {
            if (n.data?.loading) {
              const cur = currentMap.get(n.id);
              if (cur?.data?.loading) {
                return {
                  ...n,
                  data: {
                    ...n.data,
                    statusInfo: cur.data.statusInfo ?? n.data.statusInfo,
                    generationStartTime: cur.data.generationStartTime ?? n.data.generationStartTime,
                  }
                };
              }
            }
            return n;
          });
        });
        setEdges(edgesWithTransmitting);

        // ──────────────────────────────────────────
        // ⚡ 写入 IndexedDB 项目缓存（供下次 Plan A 秒开使用）
        // ──────────────────────────────────────────
        // 注意：节点 data 中含回调函数（onUpdate 等），IndexedDB 无法序列化函数，
        // 所以写入前用 filter 去除所有 typeof === 'function' 的属性。
        // 读取时（Plan A 上方）会重新注入这些回调。
        try {
          const nodesToCache = loadedNodes.map(n => ({
            ...n,
            data: Object.fromEntries(
              Object.entries(n.data || {}).filter(([, v]) => typeof v !== 'function')
            )
          }));
          projectCacheService.put(projectId, {
            nodes: nodesToCache,
            edges: loadedEdges,
            viewport: projectData.viewport || {},
            version: projectData.version || 0,
            crewId: projectData.crew_id,
            crewName: projectData.crew_name,
          });
        } catch (e) { /* 缓存写入失败不影响正常流程 */ }

        // 🛡️ 服务端权威数据已就绪：projectVersionRef + setNodes(loadedNodes) 已完成。
        //   此后才允许 dirty 标记触发自动保存，避免 Plan A 缓存秒开期间的旧节点被回写。
        isDataReadyRef.current = true;

        // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
        // 后台异步补充缓存（处理首屏 cache miss + 参考文件 + 素材库）
        // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
        // 设计思路：
        //   - 首屏 resolveLocalOnly 只查 L0 + L1，cache miss 的节点此处补充
        //   - getLocalUrlOrFetch 会走 L2 本地文件夹 + L3 远程下载（如果需要）
        //   - 这些操作在后台进行，不阻塞首次渲染和用户交互
        //
        // 性能优化：
        //   - 并行度限制为 6（避免同时发起过多网络请求）
        //   - 使用 Map 收集缓存结果，每阶段仅调用 1 次 setNodes（O(1) 而非 O(N) 次重渲染）
        //   - cancelled 标志位防止组件卸载后仍然写入 state
        //
        // 分 3 个阶段：
        //   1. 节点 output（仅首屏未命中的节点）
        //   2. 节点 refFiles + refAudios（参考图/音频缩略图）
        //   3. 全局素材库 assets（侧边栏素材缩略图）
        const currentCrewId = projectData.crew_id;
        const currentCrewName = projectData.crew_name || '默认项目';

        // 🆕 记录项目打开事件（用于热点追踪）
        if (currentCrewId && currentCrewName) {
          projectHotspotTracker.recordOpen(currentCrewId, currentCrewName);
        }
        if (currentCrewId && !cancelled) {
          (async () => {
            try {
              // ⚡ 按需加载分端：是否配了本地缓存文件夹（桌面重度用户 = 全量预热灌文件夹；
              //   iPad/未配 = 懒加载，打开只查本地缓存不下载原图，原图交给节点进视口时拉）
              const folderConfigured = await localCacheService.hasCacheConfigured();
              // own-cloud（自有 OSS/R2/本地代理）URL 判定 —— 供「分级预热」与下方「死链恢复」共用。
              //   own-cloud URL 永不过期，可安全推迟下载；厂商临时 URL 会过期，必须尽早抓。
              const isOwnCloudUrl = (url) => {
                if (!url || (!url.startsWith('http://') && !url.startsWith('https://'))) return false;
                try {
                  const u = new URL(url);
                  const host = u.hostname;
                  if ((host === window.location.hostname || host === 'localhost' || host === '127.0.0.1')
                    && (u.pathname.startsWith('/uploads/') || u.pathname.startsWith('/thumbnails/'))) return true;
                  if (/^cineart-(canvas|ref)[^/]*\.aliyuncs\.com$/i.test(host)) return true;
                  if (/^r2-(shucai|ref)(\.|-)/i.test(host)) return true;
                  if (/\.r2\.dev$/i.test(host)) return true;
                  if (/\.r2\.cloudflarestorage\.com$/i.test(host)) return true;
                  // 自建 MinIO（教育版）：域名由构建期注入，见文件顶部 EXTRA_OWN_CLOUD_HOSTS
                  if (EXTRA_OWN_CLOUD_HOSTS.includes(host.toLowerCase())) return true;
                  return false;
                } catch { return false; }
              };
              // 配文件夹端「完整本地镜像」改为后台空闲慢慢灌：这里收集打开时本地缺失的 own-cloud
              //   原图 URL，阶段跑完后 fire-and-forget 一个 idle 预热器（见块末），不阻塞打开、不抢带宽。
              const mirrorPrewarmTargets = [];
              // ─── 并行缓存加载（代替逐节点串行，大幅减少黑屏等待时间）───
              const CACHE_CONCURRENCY = 6;
              // runWithLimit: 简易并发池，最多同时执行 N 个异步任务
              const runWithLimit = async (fns) => {
                const executing = [];
                for (const fn of fns) {
                  if (cancelled) break;
                  const p = fn().finally(() => executing.splice(executing.indexOf(p), 1));
                  executing.push(p);
                  if (executing.length >= CACHE_CONCURRENCY) await Promise.race(executing);
                }
                await Promise.allSettled(executing);
              };

              // ── 阶段 1: 节点 output 缓存（仅处理首屏 cache miss 的节点）──
              // 首屏 resolveLocalOnly 已处理 L0+L1 命中的节点，这里补充 L2+L3
              //
              // C1 优化：所有缓存命中的节点都写回 localCacheUrl（包括 OSS URL 节点）
              //   blob URL 已在内存中，切换几乎无闪烁，且后续 tab 切换/组件 remount
              //   不再重新请求 OSS，显著减少带宽消耗。
              //
              // 🔧 优化：使用低优先级任务，避免阻塞主流程（网速慢时卡顿的根因）
              const outputCacheMap = new Map(); // nodeId → { localCacheUrl, uploading? }
              const outputTasks = loadedNodes
                .filter(n => {
                  const o = n.data?.output;
                  if (!o || o.startsWith('blob:') || o.startsWith('data:')) return false;
                  // ⚡ 跳过已在首屏 resolveLocalOnly 中命中缓存的节点
                  if (n.data.localCacheUrl) return false;
                   // 🔧 跳过 local-cache:// 型 URL：这些伪协议URL在本地查不到时会卡住
                   // 它们应该在工作流导入时已处理，或用户转移项目时需主动重新加载
                   if (o.startsWith('local-cache://')) return false;
                  return true;
                })
                .map(node => async () => {
                  if (cancelled) return;
                  const output = node.data.output;
                  const fileType = node.data.type === 'video' ? 'video' : node.data.type === 'audio' ? 'audio' : 'image';
                  // ⚡ 分端：配了文件夹 → getLocalUrlOrFetch（含 L3 下载，全量预热灌文件夹）；
                  //   未配 → resolveLocalOnly（0 网络，只查本地缓存，命中就用，未命中留给节点进视口拉）
                  // ⚡ 分端 + 分级：
                  //   - 配文件夹 + 厂商临时 URL（会过期）→ 立即 getLocalUrlOrFetch 下载，
                  //     保住紧随其后的「死链恢复」能趁链接失效前抢救到云存储。
                  //   - 其余（own-cloud URL，或未配文件夹）→ resolveLocalOnly（0 网络，只查本地）；
                  //     配文件夹端把本地缺失的 own-cloud 原图收进队列，交后台空闲预热慢慢灌满镜像。
                  let result;
                  try {
                    if (folderConfigured && !isOwnCloudUrl(output)) {
                      result = await localCacheService.getLocalUrlOrFetch(output, currentCrewId, currentCrewName, fileType);
                    } else {
                      const localUrl = await localCacheService.resolveLocalOnly(output, { crewId: currentCrewId, crewName: currentCrewName, fileType });
                      result = { url: localUrl || output, isLocal: !!localUrl };
                      if (folderConfigured && !result.isLocal) mirrorPrewarmTargets.push({ url: output, fileType });
                    }
                  } catch (err) {
                    // 🔇 静默跳过失败的下载，不阻塞其他节点
                    console.debug('后台补充失败，跳过:', output, err?.message);
                    return;
                  }
                  if (cancelled) return;
                  if (result.isLocal && result.url !== output) {
                    // 🔑 命中缓存的节点写回 localCacheUrl：
                    //   - local-cache:// 节点：当前处于 shimmer，patch 后立即显示
                    //   - OSS URL 节点：blob URL 已在内存中，切换几乎无闪烁
                    const patch = { localCacheUrl: result.url };
                    if (output.startsWith('local-cache://')) patch.uploading = false;
                    outputCacheMap.set(node.id, patch);
                  }
                });

              // 🔧 使用优先级队列替代 runWithLimit
              const runWithPriority = async (tasks, priority = 'normal') => {
                const promises = tasks.map(task =>
                  priorityQueue.add(task, priority, `${currentCrewId}-${Math.random()}`)
                );
                return Promise.allSettled(promises);
              };

              // 🆕 使用优先级队列 + requestIdleCallback 降低优先级，不阻塞主流程
              const scheduleBackgroundFetch = (tasks) => {
                if (tasks.length === 0) return;

                const doFetch = async () => {
                  // 🆕 使用优先级队列，首屏 miss 的节点用普通优先级
                  await runWithPriority(tasks, 'normal');
                  if (outputCacheMap.size > 0 && !cancelled) {
                    setNodes(nds => nds.map(n => {
                      const patch = outputCacheMap.get(n.id);
                      return patch ? { ...n, data: { ...n.data, ...patch } } : n;
                    }));
                  }
                };

                // 使用 requestIdleCallback 或 setTimeout 降低优先级
                if (typeof requestIdleCallback !== 'undefined') {
                  requestIdleCallback(doFetch, { timeout: 5000 });
                } else {
                  setTimeout(doFetch, 100);
                }
              };

              scheduleBackgroundFetch(outputTasks);

              // ── 1.5: 厂商死链恢复（本地缓存 → 云存储）──
              // 检测 output 为非自有云存储 URL 但本地缓存命中的节点，自动上传到当前存储并替换 URL。
              // 覆盖所有第三方厂商临时链接：Midjourney、OpenPT、LibLib、签名过期链接等。
              if (!cancelled) {
                const vendorNodes = loadedNodes.filter(n => {
                  const o = n.data?.output;
                  if (!o) return false;
                  // 跳过非 HTTP URL（blob:, data:, local-cache:// 等）
                  if (!o.startsWith('http://') && !o.startsWith('https://')) return false;
                  // 跳过自有云 URL（这些不是厂商临时链接）
                  if (isOwnCloudUrl(o)) return false;
                  // 只恢复本地缓存命中的节点
                  return n.data.localCacheUrl || outputCacheMap.has(n.id);
                });
                if (vendorNodes.length > 0) {
                  console.log(`🔄 [死链恢复] 发现 ${vendorNodes.length} 个厂商URL节点有本地缓存，启动上传到云存储…`);
                  const recoverTasks = vendorNodes.map(node => async () => {
                    if (cancelled) return;
                    try {
                      const oldUrl = node.data.output;
                      const fileName = localCacheService.extractFileNameFromUrl(oldUrl);
                      const fileType = node.data.type === 'video' ? 'video' : node.data.type === 'audio' ? 'audio' : 'image';
                      const blob = await localCacheService.readFileFromCache(currentCrewId, currentCrewName, fileType, fileName);
                      if (!blob || cancelled) return;
                      // 🛡️ 护栏 3：拒绝用 0 字节脏数据覆盖原 vendor URL
                      // 历史事故：fetchBlobWithProgress 没校验完整性时，CDN 空响应被缓存，
                      // 这里读出来 0 字节，照样上传到自家 OSS → 节点变成永久死链。
                      if (blob.size === 0) {
                        console.warn(`⚠️ [死链恢复] ${node.id}: 本地缓存为 0 字节，跳过上传，保留 vendor URL`);
                        return;
                      }
                      // 构造 File 对象用于 presignUpload
                      const ext = fileName.includes('.') ? fileName.substring(fileName.lastIndexOf('.')) : '.png';
                      const mimeMap = { '.png': 'image/png', '.jpg': 'image/jpeg', '.jpeg': 'image/jpeg', '.webp': 'image/webp', '.gif': 'image/gif', '.mp4': 'video/mp4', '.webm': 'video/webm', '.mp3': 'audio/mpeg', '.wav': 'audio/wav' };
                      const mime = mimeMap[ext.toLowerCase()] || (fileType === 'video' ? 'video/mp4' : fileType === 'audio' ? 'audio/mpeg' : 'image/png');
                      const file = new File([blob], fileName, { type: mime });
                      const { url: cloudUrl } = await api.presignUpload(file);
                      if (!cloudUrl || cancelled) return;
                      // 更新节点 output → 云存储 URL
                      setNodes(nds => nds.map(n => {
                        if (n.id !== node.id) return n;
                        return { ...n, data: { ...n.data, output: cloudUrl, outputs: [cloudUrl] } };
                      }));
                      console.log(`✅ [死链恢复] ${node.id}: ${fileName} → 云存储成功`);
                    } catch (e) {
                      console.warn(`⚠️ [死链恢复] ${node.id} 上传失败:`, e.message);
                    }
                  });
                  await runWithLimit(recoverTasks);
                }
              }

              // 2. 节点 refFiles / refAudios 缓存 — 并行，收集结果后批量更新
              //   ⚡ 分端：配文件夹 → getLocalUrlOrFetch（含下载）；未配 → resolveLocalOnly（纯本地）
              const resolveRef = async (url, ft) => {
                if (folderConfigured) {
                  return await localCacheService.getLocalUrlOrFetch(url, currentCrewId, currentCrewName, ft);
                }
                const localUrl = await localCacheService.resolveLocalOnly(url, { crewId: currentCrewId, crewName: currentCrewName, fileType: ft });
                return { url: localUrl || url, isLocal: !!localUrl };
              };
              const refCacheMap = new Map(); // nodeId → { refFiles, refAudios }
              const refTasks = loadedNodes
                .filter(n => (n.data.refFiles?.length > 0) || (n.data.refAudios?.length > 0))
                .map(node => async () => {
                  if (cancelled) return;
                  let hasUpdate = false;
                  const updatedRefFiles = (node.data.refFiles || []).map(f => ({ ...f }));
                  const updatedRefAudios = (node.data.refAudios || []).map(a => ({ ...a }));
                  for (const ref of updatedRefFiles) {
                    if (cancelled) break;
                    if (!ref.url || ref.url.startsWith('blob:') || ref.url.startsWith('data:')) continue;
                    const ft = ref.type === 'video' ? 'video' : 'image';
                    const result = await resolveRef(ref.url, ft);
                    if (cancelled) break;
                    if (result.isLocal && result.url !== ref.url) { ref.localCacheUrl = result.url; hasUpdate = true; }
                  }
                  for (const ref of updatedRefAudios) {
                    if (cancelled) break;
                    if (!ref.url || ref.url.startsWith('blob:') || ref.url.startsWith('data:')) continue;
                    const result = await resolveRef(ref.url, 'audio');
                    if (cancelled) break;
                    if (result.isLocal && result.url !== ref.url) { ref.localCacheUrl = result.url; hasUpdate = true; }
                  }
                  if (hasUpdate && !cancelled) {
                    refCacheMap.set(node.id, { refFiles: updatedRefFiles, refAudios: updatedRefAudios });
                  }
                });
              await runWithLimit(refTasks);
              if (refCacheMap.size > 0 && !cancelled) {
                setNodes(nds => nds.map(n => {
                  const patch = refCacheMap.get(n.id);
                  return patch ? { ...n, data: { ...n.data, ...patch } } : n;
                }));
              }

              // 3. globalAssets 缓存 — 并行
              if (!cancelled) {
                const assetCacheMap = new Map();
                const assetTasks = normalizedAssets
                  .filter(a => a.url && !a.url.startsWith('blob:') && !a.url.startsWith('data:'))
                  .map(asset => async () => {
                    if (cancelled) return;
                    const ft = asset.category === 'videos' ? 'video' : asset.category === 'audios' ? 'audio' : 'image';
                    // ⚡ 未配文件夹端：素材全尺寸文件只查本地不下载（侧边栏显示靠缩略图）
                    const result = await resolveRef(asset.url, ft);
                    if (result.isLocal) assetCacheMap.set(asset.url, result.url);
                    if (asset.thumbnailUrl && asset.thumbnailUrl !== asset.url) {
                      // 缩略图两端都缓存（小、侧边栏要显示）
                      const tR = await localCacheService.getLocalUrlOrFetch(asset.thumbnailUrl, currentCrewId, currentCrewName, 'image');
                      if (tR.isLocal) assetCacheMap.set(asset.thumbnailUrl, tR.url);
                    }
                  });
                await runWithLimit(assetTasks);
                if (assetCacheMap.size > 0 && !cancelled) {
                  setGlobalAssets(prev => prev.map(a => ({
                    ...a,
                    localCacheUrl: assetCacheMap.get(a.url) || a.localCacheUrl,
                    localThumbUrl: assetCacheMap.get(a.thumbnailUrl) || a.localThumbUrl
                  })));
                }
              }
              // 4. publicAssets 缩略图缓存 — 仅缓存缩略图，不缓存全尺寸文件
              if (!cancelled) {
                const pubThumbMap = new Map();
                const pubTasks = normalizedPublic
                  .filter(a => a.thumbnailUrl && !a.thumbnailUrl.startsWith('blob:') && !a.thumbnailUrl.startsWith('data:'))
                  .map(asset => async () => {
                    if (cancelled) return;
                    const tR = await localCacheService.getLocalUrlOrFetch(asset.thumbnailUrl, currentCrewId, currentCrewName, 'image');
                    if (tR.isLocal) pubThumbMap.set(asset.thumbnailUrl, tR.url);
                  });
                await runWithLimit(pubTasks);
                if (pubThumbMap.size > 0 && !cancelled) {
                  setPublicAssets(prev => prev.map(a => ({
                    ...a,
                    localThumbUrl: pubThumbMap.get(a.thumbnailUrl) || a.localThumbUrl
                  })));
                }
              }

              // ── 后台空闲镜像预热（仅配文件夹端）──
              // own-cloud 原图慢慢灌进本地文件夹：requestIdleCallback 逐张让路、串行，
              //   不阻塞打开、不与用户操作抢带宽。cancelled（切项目/卸载）即停。
              //   注意：只灌本地缓存，不回写 node.localCacheUrl —— 画布默认仍显示缩略图
              //   （显示层由 ImageNodeView 的「选中才显示全图」控制），选中时命中这份热缓存秒开。
              if (folderConfigured && mirrorPrewarmTargets.length > 0 && !cancelled) {
                (async () => {
                  for (const t of mirrorPrewarmTargets) {
                    if (cancelled) return;
                    await new Promise(r => (typeof window !== 'undefined' && window.requestIdleCallback)
                      ? window.requestIdleCallback(() => r(), { timeout: 3000 })
                      : setTimeout(r, 400));
                    if (cancelled) return;
                    try {
                      await localCacheService.getLocalUrlOrFetch(t.url, currentCrewId, currentCrewName, t.fileType);
                    } catch (_) { /* 单张失败不影响其余；own-cloud 图不过期，下次开项目会再补 */ }
                  }
                })();
              }
            } catch (err) { console.warn('本地缓存处理出错:', err); }
          })();
        }

        if (projectData.viewport && Object.keys(projectData.viewport).length > 0) {
          setRestoredViewport(projectData.viewport);
        } else {
          setRestoredViewport(null);
        }

        // 只有首次打开（无缓存）时需要在此打开画布；有缓存时 Plan A 已经打开了
        if (!cacheRendered) {
          setIsInitialized(true);
          stopLoading(); // 🆕 API 加载完成，关闭加载反馈
        }

        // ⏱️ 性能基准测试报告
        const totalTime = performance.now() - perfTimers.start;
        const planATime = perfTimers.planA_end - perfTimers.planA_start;
        const planBTime = perfTimers.planB_end - perfTimers.planB_start;
        const cacheResolutionTime = perfTimers.cacheResolution_end - perfTimers.cacheResolution_start;
        const renderingTime = perfTimers.rendering_end - perfTimers.rendering_start;

        // 🔧 缓存验证不阻塞页面渲染，fire-and-forget
        validateAfterLoad().catch(() => {});
      } catch (err) {
        console.error('加载项目失败:', err);
        setAlertMsg({ text: '加载项目失败', type: 'error' });
        setIsInitialized(true);
        stopLoading(); // 🆕 加载失败也要关闭反馈
      }
    };

    loadProjectData();
    return () => { cancelled = true; };
  }, [projectId, revalidateNonce]); // eslint-disable-line react-hooks/exhaustive-deps

  // ━━━ 项目保存 ━━━
  const { saveProject, isMountedRef, activeProjectIdRef } = useProjectSave({
    projectId, getNodes, getEdges, getViewport,
    nodesRef, projectVersionRef, skipNextAutoSaveRef, isDataReadyRef, isReadOnlyRef,
    isInitialized, nodes, edges, setAlertMsg,
    crewIdRef, crewNameRef,
  });

  // ── 离开编辑器前先保存 ──
  // 必须用手动模式 saveProject(true)：手动会 await 在途保存完成、再发起一次新保存，
  // 自动模式会直接跳过在途保存，导致组件卸载时丢失最近改动。
  const handleNavigateHome = useCallback(async () => {
    startLoading('dashboard'); // 🆕 先显示加载反馈，再保存
    try {
      await saveProject(true);
    } catch (e) {
      // 异步保存失败也没关系，syncSaveRef 会在卸载时兜底
    }
    navigate('/');
  }, [saveProject, navigate, startLoading]);

  // ━━━ 节点操作 ━━━
  const {
    createSecureBlob, revokeSecureBlob, abortNodeUpload,
    handleUpdateNode, handleDeleteNodeButton, handleDeleteSelectedNodes, handleConfirmDelete,
    handleRemoveRefAudio, handleRemoveRefFile, handleRemoveRefText,
    handleReuploadLogic, handleReplace, handleReplaceFileChange, uploadAndReplace,
    handleDetachImage, handleGenerate, handleDownscaleInPlace, handleHandleDoubleClick,
    handleDisconnectDownstream,
  } = useNodeActions({
    setNodes, setEdges, setAlertMsg, setMenuData, setDeleteConfirm, deleteConfirm,
    nodesRef, crewIdRef, crewNameRef, crewId, crewName, projectId,
    replaceInputRef, activeBlobsRef, globalAssetsRef, publicAssetsRef,
    callbacksRef,
    saveProject, isMountedRef, activeProjectIdRef,
  });

  // ── MJ Action ──
  const { handleMjAction, inpaintState, handleMjInpaintSubmit, closeInpaintEditor } = useMjAction({
    setNodes, setEdges: setFlowEdges, projectId, setAlertMsg, callbacksRef,
  });

  // ── GPT Inpaint ──
  const { handleGptInpaint, gptInpaintState, handleGptInpaintSubmit, closeGptInpaintEditor } = useGptInpaint({
    setNodes, setEdges: setFlowEdges, projectId, setAlertMsg, callbacksRef,
  });

  // H3 升 2K：无中间编辑器，点一下直接提交
  const { handleH3Upscale } = useH3Upscale({
    setNodes, setEdges: setFlowEdges, projectId, setAlertMsg, callbacksRef,
  });

  // ── MJ 工具箱配置（名称/Logo）──
  // 支持多渠道：根据节点的 vendor 字段，动态选择对应的工具箱配置
  const mjToolboxConfigsRef = useRef([]); // 所有 MJ 工具箱配置（按 vendorId 区分）
  const mjDescribeConfigRef = useRef({ enabled: false, creditCost: 0 });
  const gptInpaintConfigRef = useRef({ enabled: false, name: '', logo: '', creditCost: 0 });
  const h3UpscaleConfigRef = useRef({ enabled: false, name: '', logo: '', creditCost: 0 });
  const presetsRef = useRef([]); // 🎨 预设工具箱（图片节点预设列表）
  const skillboxRef = useRef({ toolsets: [] }); // ✨ 技能工具箱（三层：工具集→分组→预设）
  useEffect(() => {
    const applyToolboxConfig = (toolbox) => {
      // MJ Action：按 vendorId 存储（支持多渠道）
      const mjActions = (toolbox || []).filter(t => t.modelId === 'mj_action' && t.enabled);
      mjToolboxConfigsRef.current = mjActions.map(mjAction => ({
        vendorId: mjAction.vendorId,
        name: mjAction.name || '',
        logo: mjAction.logoUrl || '',
        actionLabels: mjAction.actionLabels || {},
        disabledActions: mjAction.disabledActions || [],
        creditCost: mjAction.creditCost || 0
      }));

      const mjDescribe = (toolbox || []).find(t => t.modelId === 'mj_describe' && t.enabled);
      mjDescribeConfigRef.current = mjDescribe ? { enabled: true, creditCost: mjDescribe.creditCost || 0 } : { enabled: false, creditCost: 0 };
      // 局部重绘：匹配云雾或 ToAPIs 任一启用的模型即可显示重绘胶囊
      const gptInpaint = (toolbox || []).find(t => (t.modelId === 'gpt_inpaint' || t.modelId === 'gpt_inpaint_toapis') && t.enabled);
      gptInpaintConfigRef.current = gptInpaint ? { enabled: true, name: gptInpaint.name || 'GPT 局部重绘', logo: gptInpaint.logoUrl || '', creditCost: gptInpaint.creditCost || 0 } : { enabled: false, name: '', logo: '', creditCost: 0 };
      // H3 升 2K：紫色胶囊，仅 H3 的 768P 视频节点显示
      const h3Upscale = (toolbox || []).find(t => t.modelId === 'h3_upscale' && t.enabled);
      h3UpscaleConfigRef.current = h3Upscale ? { enabled: true, name: h3Upscale.name || 'MiniMax H3 升 2K', logo: h3Upscale.logoUrl || '', creditCost: h3Upscale.creditCost || 0 } : { enabled: false, name: '', logo: '', creditCost: 0 };
    };
    api.get('/api/toolbox-config').then(data => {
      applyToolboxConfig(data.toolbox);
      setOwnOssHosts(data.ownOssHosts || []); // 🆕 注入自家 OSS 域名，供缩略图工具判断
    }).catch(() => {});
    const handler = () => {
      api.get('/api/toolbox-config').then(data => {
        applyToolboxConfig(data.toolbox);
        // 触碰 MJ 节点使工具栏重新渲染（读取最新 ref）
        setNodes(nds => nds.map(n => (n.data?.vendor === 'yunwu-midjourney' || n.data?.vendor === 'apimart-midjourney') && n.data.mjPhase ? { ...n, data: { ...n.data, _mjCfgVer: Date.now() } } : n));
      }).catch(() => {});
    };
    window.addEventListener('modelsUpdated', handler);
    return () => window.removeEventListener('modelsUpdated', handler);
  }, []);

  // ── 🎨 预设工具箱（图片节点预设）加载 ──
  // 超管在后台配置的预设列表。工具条直显图标 / 生成节点分组下拉据此渲染，
  // 生图时按 presetId 实时取 prompt 注入（改后台即生效，无需刷新）。
  useEffect(() => {
    const loadPresets = () => {
      api.get('/api/presets').then(data => {
        presetsRef.current = { toolsets: data.toolsets || [] };
        // 触碰已选中的图片节点，使工具条重新渲染读取最新预设树
        setNodes(nds => nds.map(n => (n.selected && n.data?.type === 'image') ? { ...n, data: { ...n.data, _presetVer: Date.now() } } : n));
      }).catch(() => {});
    };
    loadPresets();
    window.addEventListener('presetsUpdated', loadPresets);
    return () => window.removeEventListener('presetsUpdated', loadPresets);
  }, []);

  // ✨ 加载技能工具箱（三层架构：toolsets → groups → presets）
  useEffect(() => {
    const loadSkillbox = () => {
      api.get('/api/skillbox').then(data => {
        skillboxRef.current = { toolsets: data.toolsets || [] };
        // 触碰已选中的图片节点，使工具条重新渲染读取最新技能树
        setNodes(nds => nds.map(n => (n.selected && n.data?.type === 'image') ? { ...n, data: { ...n.data, _skillboxVer: Date.now() } } : n));
      }).catch(err => {
        console.error('[EditorPage] skillbox load failed:', err);
      });
    };
    loadSkillbox();
    window.addEventListener('skillboxUpdated', loadSkillbox);
    return () => window.removeEventListener('skillboxUpdated', loadSkillbox);
  }, []);

  // ── 🎬 分镜节点实时同步到下游视频节点 ──
  // 🔧 已移除自动同步逻辑，允许用户自由修改下游节点的提示词
  // 分镜大师节点是"模板"，下游节点是"独立实例"，互不干扰
  // useEffect(() => {
  //   // 遍历所有分镜节点，找到连接的视频节点并同步 parseText
  //   edges.forEach(edge => {
  //     const sourceNode = nodes.find(n => n.id === edge.source);
  //     const targetNode = nodes.find(n => n.id === edge.target);
  //
  //     // 检查：源节点是分镜节点 + 目标节点是视频节点
  //     if (sourceNode?.data?.storyboardMode && targetNode?.data?.type === 'video') {
  //       const sbText = (sourceNode.data.parseText || '').trim();
  //
  //       // 如果分镜节点的 parseText 与视频节点的 prompt 不同，则同步
  //       if (sbText !== targetNode.data.prompt) {
  //         console.log('[EditorPage] 分镜节点 parseText 变化，同步到视频节点', {
  //           分镜节点ID: sourceNode.id,
  //           视频节点ID: targetNode.id,
  //           新提示词: sbText.substring(0, 50)
  //         });
  //
  //         // 生成 slateValue
  //         const assets = (sourceNode.data.refFiles || []).map(f => ({
  //           id: f.id,
  //           title: f.title,
  //           type: f.type,
  //           category: f.category || 'prop',
  //           mini_thumbnail_url: f.thumbUrl,
  //           thumbUrl: f.thumbUrl,
  //           url: f.url
  //         }));
  //
  //         const slateNodes = parseTextToSlate(sbText, assets);
  //
  //         // 更新视频节点
  //         setNodes(nds => nds.map(n => {
  //           if (n.id === targetNode.id) {
  //             return {
  //               ...n,
  //               data: {
  //                 ...n.data,
  //                 prompt: sbText,
  //                 slateValue: JSON.stringify(slateNodes)
  //               }
  //             };
  //           }
  //           return n;
  //         }));
  //       }
  //     }
  //   });
  // }, [nodes, edges]); // 监听 nodes 和 edges 的变化

  // ── MJ 反推提示词 ──
  const handleDescribe = useCallback(async (nodeId) => {
    if (!mjDescribeConfigRef.current.enabled) {
      setAlertMsg({ type: 'warning', text: '请先在 API 设置中启用 MJ 反推提示词（Describe）工具箱' });
      return;
    }
    const node = getNodes().find(n => n.id === nodeId);
    if (!node) return;
    const imageUrl = node.data.output?.startsWith('local-cache://') ? null : (node.data.output || null);
    if (!imageUrl) {
      setAlertMsg({ type: 'warning', text: '节点没有可用图片' });
      return;
    }
    try {
      await api.post('/api/mj-describe', { projectId, nodeId, imageUrl });
    } catch (err) {
      setAlertMsg({ type: 'error', text: '反推失败: ' + (err.message || '未知错误') });
    }
  }, [getNodes, projectId, setAlertMsg]);

  // ── Blob 清理 ──
  useEffect(() => {
    const activeBlobs = activeBlobsRef.current;
    return () => {
      activeBlobs.forEach(url => URL.revokeObjectURL(url));
      activeBlobs.clear();
    };
  }, []);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // 云存储回填（local-cache:// → R2/OSS）
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // 背景：
  //   - uploadAndReplace 上传文件时，先立即写入 local-cache:// URL 到节点
  //   - 然后后台持续重试上传到云存储，成功后替换为云存储 URL
  //   - 如果用户在上传完成前关闭浏览器，autoSave 会将 local-cache:// URL 保存到数据库
  //   - 下次打开项目时，这些节点的 output 是 local-cache:// URL，需要"回填"到云存储
  //
  // 回填流程：
  //   1. 扫描所有节点，找出 output 以 local-cache:// 开头的
  //   2. 从 IndexedDB / 本地文件系统读取缓存的原始文件
  //   3. 使用 presignUpload 上传到云存储
  //   4. 更新节点的 output 为云存储 URL
  //   5. 如果本地缓存丢失（跨设备或清除缓存），清除死链接
  //
  // 安全限制：
  //   - MAX_BACKFILL_RETRIES = 15，超过后放弃（避免无限循环）
  //   - 每次重试指数退避：5s → 10s → 20s → ... → 120s 封顶
  //   - OSS_UNAVAILABLE 错误直接跳过（云存储未配置，保持本地模式）
  useEffect(() => {
    if (!isInitialized || !projectId) return;
    let cancelled = false;

    (async () => {
      try {
        const currentNodes = nodesRef.current || [];
        const localCacheNodes = currentNodes.filter(n =>
          n.data?.output && n.data.output.startsWith('local-cache://')
        );
        if (localCacheNodes.length === 0) return;

        console.log(`🔄 发现 ${localCacheNodes.length} 个本地缓存节点，尝试回填到云存储...`);

        for (const node of localCacheNodes) {
          if (cancelled) break;
          const output = node.data.output;
          // 解析 local-cache://crewId/type/fileName
          const match = output.match(/^local-cache:\/\/([^/]+)\/([^/]+)\/(.+)$/);
          if (!match) continue;
          const [, savedCrewId, savedTypeFolder, fileName] = match;
          const ft = savedTypeFolder === 'images' ? 'image' : savedTypeFolder === 'videos' ? 'video' : 'audio';

          // 从本地缓存读取文件
          const blob = await localCacheService.readFileFromCache(savedCrewId, crewNameRef.current, ft, fileName);
          if (cancelled) continue;
          if (!blob) {
            // 🛡️ 本地缓存文件不存在（可能被跨项目切换时的旧回调删除了）
            // 清除死掉的 local-cache:// URL，防止永久卡死
            console.warn(`⚠️ 本地缓存文件丢失，清除死链接: ${fileName}`);
            handleUpdateNode(node.id, {
              output: null,
              outputs: [],
              uploading: false,
            });
            continue;
          }

          // 转为 File 对象（presignUpload 需要）
          const file = new File([blob], fileName, { type: blob.type || 'application/octet-stream' });

          // 🔁 持续重试云存储上传，最多 MAX_BACKFILL_RETRIES 次
          const MAX_BACKFILL_RETRIES = 15;
          const fileSizeMB = file.size / (1024 * 1024);
          let uploaded = false;
          let attempt = 0;
          while (!cancelled && !uploaded && attempt < MAX_BACKFILL_RETRIES) {
            attempt++;
            try {
              console.log(`📤 回填上传 ${fileName} (${fileSizeMB.toFixed(1)}MB) 第 ${attempt} 次...`);
              const data = await api.presignUpload(file);
              if (data.url && !cancelled) {
                // 重置 title（可能残留了“上传中...”前缀）
                const currentTitle = (nodesRef.current || []).find(n => n.id === node.id)?.data?.title || '';
                const cleanTitle = currentTitle.startsWith('上传中...') ? currentTitle.replace(/^上传中\.\.\.\s*/, '') : undefined;
                handleUpdateNode(node.id, {
                  output: data.url,
                  outputs: [data.url],
                  uploading: false,
                  ...(cleanTitle !== undefined ? { title: cleanTitle } : {}),
                });
                const ossFileName = localCacheService.extractFileNameFromUrl(data.url);
                if (ossFileName && ossFileName !== fileName) {
                  localCacheService.writeFileToCache(savedCrewId, crewNameRef.current, ft, ossFileName, file).catch(() => {});
                  localCacheService.deleteFileFromCache(savedCrewId, crewNameRef.current, ft, fileName).catch(() => {});
                }
                console.log(`✅ 本地文件已回填到云存储: ${fileName} → ${ossFileName || fileName}`);
                uploaded = true;
              }
            } catch (err) {
              if (err.code === 'OSS_UNAVAILABLE') {
                console.warn(`⚠️ 云存储未配置，跳过回填: ${fileName}`);
                break;
              }
              // 指数退避：5s → 10s → 20s → 40s → 60s → 120s（封顶 2 分钟）
              const delay = Math.min(5000 * Math.pow(2, attempt - 1), 120000);
              console.warn(`⚠️ 回填第 ${attempt} 次失败 (${fileName}): ${err.message}，${delay / 1000}s 后重试...`);
              await new Promise(r => setTimeout(r, delay));
            }
          }
          if (!uploaded && !cancelled) {
            if (attempt >= MAX_BACKFILL_RETRIES) {
              console.warn(`❌ 回填上传超过 ${MAX_BACKFILL_RETRIES} 次，放弃: ${fileName}`);
            }
            handleUpdateNode(node.id, { uploading: false });
          }
        }

        // 如果有节点被升级，触发一次保存
        if (!cancelled) {
          const updatedNodes = nodesRef.current || [];
          const stillLocal = updatedNodes.some(n => n.data?.output?.startsWith('local-cache://'));
          const hadLocal = localCacheNodes.length > 0;
          if (hadLocal && !stillLocal) {
            setTimeout(() => saveProject(false), 1000);
          } else if (hadLocal) {
            // 部分升级也保存
            setTimeout(() => saveProject(false), 1000);
          }
        }
      } catch (err) {
        console.warn('云存储回填处理出错:', err);
      }
    })();

    return () => { cancelled = true; };
  }, [isInitialized, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ━━━ 基础 ReactFlow 事件处理 ━━━
  const onNodesChange = useCallback((changes) => {
    // ── 拖动对齐吸附 ──
    // 仅处理「单节点拖动」：恰好一个 position 变更且带新坐标（拖动中），
    // 且吸附开关开启、被拖节点不在群组里（群组联动逻辑在 onNodeDrag 单独处理）。
    setNodes((nds) => {
      const posChanges = changes.filter(c => c.type === 'position' && c.dragging && c.position);
      const isSingleDrag = posChanges.length === 1;

      if (isSingleDrag && getSnapEnabled()) {
        const change = posChanges[0];
        const dragged = nds.find(n => n.id === change.id);
        // 群组节点本身 / 群组内子节点不参与对齐吸附（避免和联动逻辑打架）
        const inGroup = dragged && (dragged.type === 'group' || dragged.data?.groupId);
        if (dragged && !inGroup) {
          const { horizontal, vertical, snapPosition } = getHelperLines(change, nds);
          // 用吸附后的坐标覆盖本帧落点
          change.position = {
            x: snapPosition.x ?? change.position.x,
            y: snapPosition.y ?? change.position.y,
          };
          setHelperLines({ horizontal, vertical });
        } else {
          setHelperLines({ horizontal: undefined, vertical: undefined });
        }
      } else if (helperLines.horizontal !== undefined || helperLines.vertical !== undefined) {
        // 非单节点拖动 / 吸附关闭：清掉残留对齐线
        setHelperLines({ horizontal: undefined, vertical: undefined });
      }

      return applyNodeChanges(changes, nds);
    });
  }, [helperLines.horizontal, helperLines.vertical]);
  const onEdgesChange = useCallback((changes) => setEdges(eds => applyEdgeChanges(changes, eds)), []);
  const onNodeDragStart = useCallback(() => {
    // 📱 长按刚弹出菜单：手指微抖别被判成拖动而把菜单关掉（drag 路径绕过 click 防护，需单独兜底）
    if (suppressClickRef.current) return;
    dismissMenus(); takeSnapshot();
  }, [dismissMenus, takeSnapshot]);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // 群组拖拽联动
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // ReactFlow 在每次鼠标移动时调用 onNodeDrag(event, node)，node.position 已是
  // 当前帧的最新值。我们把"上一帧位置"缓存在 lastDragPosRef，对比拿到本帧 delta，
  // 再把 (dx, dy) 应用到联动目标。
  //
  // 两种触发场景：
  //   A. 拖 group 节点本身  → 把 delta 应用到所有 children
  //   B. 拖 group 内的 child → 把 delta 应用到 group + 同组其他 children
  //      （被拖的 child 由 ReactFlow 自动更新，无需重复加 delta）
  //
  // 为什么不用 ReactFlow 自带的 parentNode/extent：与项目里 CustomNode 的渲染层
  // 不兼容（实测群组后子节点 DOM 不可见、不可交互）。这里改成"装饰节点 + 手动联动"。
  //
  // lastDragPosRef.id 用于区分两次连续拖拽（起点不同），切到新拖拽时需重置基准点
  // 避免把跨次的 delta 累加进来。
  const lastDragPosRef = useRef(null);
  const onNodeDrag = useCallback((event, node) => {
    // 决定联动目标：
    //   - 拖 group   → 联动所有 children（不含 group 本身）
    //   - 拖 child   → 联动 group + 同组其他 children（不含被拖的 child）
    //   - 普通节点   → 不联动
    let targetIds;
    if (node.type === 'group') {
      targetIds = node.data?.childIds;
    } else if (node.data?.groupId) {
      const liveNodes = getNodes();
      const group = liveNodes.find(n => n.id === node.data.groupId);
      if (!group) return;
      targetIds = [group.id, ...(group.data?.childIds || []).filter(id => id !== node.id)];
    } else {
      return;
    }
    if (!targetIds || targetIds.length === 0) return;

    const last = lastDragPosRef.current;
    if (!last || last.id !== node.id) {
      // 拖拽首帧：仅记录基准点，不联动
      lastDragPosRef.current = { id: node.id, x: node.position.x, y: node.position.y };
      return;
    }

    const dx = node.position.x - last.x;
    const dy = node.position.y - last.y;
    if (dx === 0 && dy === 0) return;
    lastDragPosRef.current = { id: node.id, x: node.position.x, y: node.position.y };

    const targetSet = new Set(targetIds);
    setNodes(nds => nds.map(n => (
      targetSet.has(n.id)
        ? { ...n, position: { x: n.position.x + dx, y: n.position.y + dy } }
        : n
    )));
  }, [getNodes, setNodes]);

  // 拖拽结束清空基准点，确保下一次拖拽走"首帧记录"分支；同时清掉对齐辅助线
  const onNodeDragStop = useCallback(() => {
    lastDragPosRef.current = null;
    setHelperLines({ horizontal: undefined, vertical: undefined });
  }, []);

  const onNodeClick = useCallback(() => {
    // 📱 iPad 长按弹菜单后的合成点击别关它（标记挡到下次触摸开始，不受按住时长影响）
    if (suppressClickRef.current) return;
    dismissMenus();
  }, [dismissMenus]);

  const onNodesDelete = useCallback((deletedNodes) => {
    // 仅回收内存中的 blob URL，不删除 OSS/本地文件
    // 文件在删除项目或删除用户时按文件夹整体清理

    // ── 🛑 取消正在进行的 AI 生成任务 ──
    // 筛选 loading=true 的节点 → 通过 WebSocket 通知后端
    // 后端收到后：abort 任务 + 立即递减 pending_tasks + 推送状态变化
    const loadingNodeIds = deletedNodes
      .filter(node => node.data?.loading)
      .map(node => node.id);
    if (loadingNodeIds.length > 0 && socketRef.current?.connected && projectId) {
      console.log('🛑 通知后端取消生成任务:', loadingNodeIds);
      socketRef.current.emit('cancel-generation', { projectId, nodeIds: loadingNodeIds });
    }

    deletedNodes.forEach(node => {
      // 🛑 中止该节点正在进行的云存储上传
      abortNodeUpload(node.id);
      revokeSecureBlob(node.data?.output);
      // 🆕 回收 localCacheUrl 的 blob URL（之前遗漏的）
      revokeSecureBlob(node.data?.localCacheUrl);
      if (node.data?.outputs) node.data.outputs.forEach(url => revokeSecureBlob(url));
    });
    // ── 一次性 setNodes：清理引用 + 维护群组关系 ──
    // 群组关系两件事：
    //   a) 若 group 节点本身被删 → 给所有归属它的 children 清掉 data.groupId
    //   b) 若 child 节点被删 → 从其所属 group.data.childIds 中移除该 id；
    //      若移除后 childIds 为空，则该 group 也一并回收（避免留下空壳）
    const deletedIds = new Set(deletedNodes.map(n => n.id));
    const deletedGroupIds = new Set(deletedNodes.filter(n => n.type === 'group').map(n => n.id));

    setNodes(nds => {
      const emptyGroupIds = new Set(); // childIds 清空 → 标记待回收
      const updated = nds.map(node => {
        let newData = { ...node.data };
        let hasChange = false;

        // 清理 refFiles / refAudios / refTexts 中对被删节点的引用
        deletedNodes.forEach(deleted => {
          if (newData.refFiles?.some(f => f.id === deleted.id)) { newData.refFiles = newData.refFiles.filter(f => f.id !== deleted.id); hasChange = true; }
          if (newData.refAudios?.some(a => a.id === deleted.id)) { newData.refAudios = newData.refAudios.filter(a => a.id !== deleted.id); hasChange = true; }
          if (newData.refTexts?.some(t => t.id === deleted.id)) { newData.refTexts = newData.refTexts.filter(t => t.id !== deleted.id); hasChange = true; }
        });

        // (b) 当前节点是 group：从 childIds 移除被删的 children
        if (node.type === 'group' && Array.isArray(newData.childIds)) {
          const remaining = newData.childIds.filter(id => !deletedIds.has(id));
          if (remaining.length !== newData.childIds.length) {
            newData.childIds = remaining;
            hasChange = true;
            if (remaining.length === 0) emptyGroupIds.add(node.id);
          }
        }

        // (a) 当前节点是 child：若所属 group 在被删列表里，清掉 groupId
        if (newData.groupId && deletedGroupIds.has(newData.groupId)) {
          delete newData.groupId;
          hasChange = true;
        }

        return hasChange ? { ...node, data: newData } : node;
      });
      return updated.filter(n => !emptyGroupIds.has(n.id));
    });
  }, [setNodes, revokeSecureBlob, abortNodeUpload, socketRef, projectId]);

  // ── onSelectionChange: 选中节点变化时更新边的视觉样式 ──
  // 性能优化：
  //   - 缓存上一次选中的节点 ID 集合（prevSelectedIdsRef）
  //   - 如果新选中集合与上次完全相同，直接 return（跳过 setEdges 和全量边重算）
  //   - 场景：React Flow 在拖拽/缩放时频繁触发 onSelectionChange，但选中集合不变
  const prevSelectedIdsRef = useRef(new Set());
  const onSelectionChange = useCallback(({ nodes: selNodes }) => {
    // 快速判断选中集合是否真的变了
    const newIds = new Set(selNodes.map(n => n.id));
    const prevIds = prevSelectedIdsRef.current;
    if (newIds.size === prevIds.size && [...newIds].every(id => prevIds.has(id))) {
      return; // 选中集合未变化，跳过 setSelectedNodes + setEdges 重算
    }
    prevSelectedIdsRef.current = newIds;

    // 只在集合真正变化时才更新 selectedNodes（避免 ReactFlow 频繁触发时瘪余 re-render）
    setSelectedNodes(selNodes);

    setEdges(eds => {
      if (selNodes.length === 0) {
        return eds.map(e => ({
          ...e,
          style: {
            ...e.style,
            stroke: e.data?.originalColor || (e.data?.locked ? C.EDGE_COLOR_AUTO : C.EDGE_COLOR_MANUAL),
            strokeWidth: C.EDGE_WIDTH_IDLE, zIndex: 0, opacity: C.OPACITY_IDLE
          }
        }));
      }
      return eds.map(e => {
        const isConnected = newIds.has(e.source) || newIds.has(e.target);
        const isTransmitting = e.data?.isTransmitting;
        const baseColor = e.data?.originalColor || (e.data?.locked ? C.EDGE_COLOR_AUTO : C.EDGE_COLOR_MANUAL);
        const newStroke = isTransmitting ? C.EDGE_COLOR_TRANSMITTING : baseColor;
        const newOpacity = isConnected || isTransmitting ? C.OPACITY_ACTIVE : C.OPACITY_IDLE;
        const newWidth = isConnected || isTransmitting ? C.EDGE_WIDTH_ACTIVE : C.EDGE_WIDTH_IDLE;
        const newZ = isConnected || isTransmitting ? 10 : 0;
        // P2: 仅样式实际变化的边才创建新对象引用，避免级联重渲染
        if (e.style?.stroke === newStroke && e.style?.opacity === newOpacity &&
            e.style?.strokeWidth === newWidth && e.style?.zIndex === newZ) {
          return e;
        }
        return { ...e, style: { ...e.style, stroke: newStroke, opacity: newOpacity, strokeWidth: newWidth, zIndex: newZ } };
      });
    });
  }, [setEdges]);

  const handleSaveWorkflow = useCallback(() => {
    const liveNodes = getNodes();
    const selected = liveNodes.filter(n => n.selected);
    if (selected.length === 0) { setAlertMsg("请选择要保存为工作流的节点"); return; }
    const allEdges = getEdges();
    const selectedIds = new Set(selected.map(n => n.id));
    const relevantEdges = allEdges.filter(e => selectedIds.has(e.source) && selectedIds.has(e.target));
    setSaveModalData({
      isOpen: true,
      nodeData: {
        id: `workflow-${Date.now()}`, type: 'workflow',
        data: { nodes: selected, edges: relevantEdges, viewport: {} }, output: null
      }
    });
  }, [getNodes, getEdges]);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // 群组化 / 解散群组
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // 数据模型：
  //   - group 节点  → type:'group'，data.childIds = string[]
  //   - child 节点 → 不动位置/不嵌套，仅 data.groupId 反向标记所属群组
  //                  + selectable/draggable=false（禁止单独操作）
  //                  + zIndex>=10（确保渲染在 group 装饰矩形之上）
  // 拖动联动 → 见 onNodeDrag。
  // 删除联动 → 见 onNodesDelete。
  //
  // 选中节点 → 浮窗"群组"按钮（SelectionToolbar）→ handleGroupNodes
  // 选中群组 → 浮窗"解散群组"按钮（GroupToolbar）  → handleUngroup
  const handleGroupNodes = useCallback(() => {
    const liveNodes = getNodes();
    // 已经在某个群组里的节点不能再被群组（避免嵌套）；group 节点本身也排除
    const selected = liveNodes.filter(n => n.selected && n.type !== 'group' && !n.data?.groupId);
    if (selected.length < 2) { setAlertMsg("请至少选择 2 个节点进行群组"); return; }
    takeSnapshot();

    // ── 计算包围盒 + padding，给 group 留出标题栏空间 ──
    const PADDING = 30;
    const TITLE_BAR = 36; // 群组左上角标签的预留高度
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    selected.forEach(n => {
      const x = n.position.x;
      const y = n.position.y;
      const w = n.width || n.measured?.width || n.data?.customSize?.width || 200;
      const h = n.height || n.measured?.height || n.data?.customSize?.height || 150;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x + w > maxX) maxX = x + w;
      if (y + h > maxY) maxY = y + h;
    });

    const groupX = minX - PADDING;
    const groupY = minY - PADDING - TITLE_BAR;
    const groupW = (maxX - minX) + PADDING * 2;
    const groupH = (maxY - minY) + PADDING * 2 + TITLE_BAR;
    const groupId = `group-${Date.now()}`;
    const childIds = selected.map(n => n.id);
    const childIdSet = new Set(childIds);

    // group 装饰节点：zIndex=-1 让它绘制在 children 之下；
    // selectable/draggable 默认 true，整框可点选 + 可拖动。
    const groupNode = {
      id: groupId,
      type: 'group',
      position: { x: groupX, y: groupY },
      selectable: true,
      draggable: true,
      zIndex: -1,
      style: {
        width: groupW,
        height: groupH,
        // 覆盖 .react-flow__node-group 默认的浅灰底/灰边，否则会在圆角蓝框里露出白方框
        background: 'transparent',
        border: 'none',
      },
      data: { label: '群组', childIds },
      selected: false,
    };

    setNodes(nds => {
      const updated = nds.map(n => {
        if (!childIdSet.has(n.id)) return n;
        // children 锁定：不可单独选中，但保留 draggable=true 以便在 child 上按住
        // 鼠标也能拖动整个群组（onNodeDrag 中识别 child 拖动并联动整组）
        return {
          ...n,
          zIndex: Math.max(n.zIndex || 0, 10),
          selectable: false,
          draggable: true,
          data: { ...n.data, groupId },
          selected: false,
        };
      });
      return [groupNode, ...updated];
    });
  }, [getNodes, setNodes, takeSnapshot, setAlertMsg]);

  // 解散群组：删除 group 节点 + 还原 children（清 groupId/zIndex/selectable/draggable）
  const handleUngroup = useCallback((groupId) => {
    takeSnapshot();
    setNodes(nds => nds
      .filter(n => n.id !== groupId)
      .map(n => {
        if (n.data?.groupId !== groupId) return n;
        const { groupId: _gid, ...restData } = n.data;
        // 恢复 React Flow 默认行为：不显式设 zIndex/selectable/draggable
        const { zIndex: _z, selectable: _s, draggable: _d, ...restNode } = n;
        return { ...restNode, data: restData };
      })
    );
  }, [setNodes, takeSnapshot]);

  // ── 重命名全局咒语分类（仅 super_admin）──
  // 调用后端 PATCH 接口全表更新 category 字段，成功后本地 globalSpells 同步替换
  const handleRenameSpellCategory = useCallback(async (oldCategory, newCategory) => {
    if (!oldCategory || !newCategory || oldCategory === newCategory) return;
    await api.patch('/api/assets/global-spells/rename-category', { oldCategory, newCategory });
    setGlobalSpells(prev => prev.map(a => (
      a.type === 'text' && a.category === oldCategory ? { ...a, category: newCategory } : a
    )));
    setSpellCategoryOrder(prev => prev.map(c => (c === oldCategory ? newCategory : c)));
    setAlertMsg({ text: `分类已重命名：${oldCategory} → ${newCategory}`, type: 'success' });
  }, [setGlobalSpells, setAlertMsg]);

  // ── 删除全局咒语分类（仅 super_admin，仅空分类可删）──
  const handleDeleteSpellCategory = useCallback(async (category) => {
    if (!category) return;
    await api.delete('/api/assets/global-spells/category', { category });
    setSpellCategoryOrder(prev => prev.filter(c => c !== category));
    setAlertMsg({ text: `分类已删除：${category}`, type: 'success' });
  }, [setAlertMsg]);

  // ── 重排全局咒语分类（仅 super_admin）──
  const handleReorderSpellCategories = useCallback(async (nextOrder) => {
    if (!Array.isArray(nextOrder)) return;
    // 先乐观更新本地，避免 UI 闪烁
    setSpellCategoryOrder(nextOrder);
    try {
      const r = await api.put('/api/assets/global-spells/order', { order: nextOrder });
      if (Array.isArray(r?.order)) setSpellCategoryOrder(r.order);
    } catch (err) {
      // 失败回退？拉一次最新顺序
      try {
        const r2 = await api.get('/api/assets/global-spells/order');
        if (Array.isArray(r2?.order)) setSpellCategoryOrder(r2.order);
      } catch (_) {}
      throw err;
    }
  }, []);

  // ── 全局咒语分类合并列表（按 spellCategoryOrder 排序 + 未排序的 distinct 追加在末尾） ──
  // 注：默认 'prompt' 不再无条件兜底——它和别的分类一样可以被改名/删除
  // 仅当合并后列表完全为空时，才补一个 'prompt' 作为最后保留项
  const mergedSpellCategories = useMemo(() => {
    const distinct = Array.from(new Set(
      (globalSpells || [])
        .filter(a => a.type === 'text' && a.category)
        .map(a => a.category)
    ));
    const seen = new Set();
    const out = [];
    // 先按 order 列表
    spellCategoryOrder.forEach(cat => {
      if (!cat || seen.has(cat)) return;
      seen.add(cat);
      out.push(cat);
    });
    // 实际有资产但 order 中没有的分类（按字母序）
    distinct.sort().forEach(cat => {
      if (!seen.has(cat)) {
        seen.add(cat);
        out.push(cat);
      }
    });
    // 兜底：如果完全没有任何分类（既没排序也没数据），保留默认 'prompt'
    if (out.length === 0) out.push('prompt');
    return out;
  }, [globalSpells, spellCategoryOrder]);

  // ── 项目名称保存 ──
  const handleSaveName = useCallback(async () => {
    if (!tempName.trim() || tempName === projectName) { setIsEditingName(false); return; }
    try {
      await api.put(`/api/projects/${projectId}`, { name: tempName.trim() });
      setProjectName(tempName.trim());
      setAllProjects(prev => prev.map(p => p.id === parseInt(projectId) ? { ...p, name: tempName.trim() } : p));
      setAlertMsg({ text: "图板已重命名", type: "success" });
    } catch (err) { setAlertMsg({ text: "重命名失败", type: "error" }); }
    setIsEditingName(false);
  }, [tempName, projectName, projectId]);

  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  // ── 智能定位 ──
  // 左侧落点不再以浏览器左缘计算：左侧拼屏时画布主区被剧本大师面板（+分隔条）向右推，
  // 故 x 需加上面板偏移量，节点才会落在工具条右侧的可见区域，而非被面板盖住。
  const getSmartSpawnPosition = useCallback((direction = 'right') => {
    // 左侧拼屏时画布起始 x = 面板宽 + 分隔条(5px)；其它布局为 0
    const leftOffset = modelMasterLayout === 'left' ? modelMasterSize + 5 : 0;
    if (direction === 'left') {
      return screenToFlowPosition({ x: leftOffset + 260, y: window.innerHeight / 2 - 150 });
    }
    const drawerWidth = 320;
    return screenToFlowPosition({ x: window.innerWidth - drawerWidth - 100 - 240, y: window.innerHeight / 2 - 150 });
  }, [screenToFlowPosition, modelMasterLayout, modelMasterSize]);

  const onSlashAssetSpawnProxy = useCallback((...args) => callbacksRef.current._onSlashAssetSpawn?.(...args), []);

  // ━━━ callbacksRef 同步（在 useLayoutEffect 中赋值，避免渲染期副作用） + NodeCallbackProvider ━━━
  useLayoutEffect(() => {
    callbacksRef.current = {
      onUpdate: handleUpdateNode, onDelete: handleDeleteNodeButton,
      onGenerate: handleGenerate, onDetach: handleDetachImage, onDownscaleInPlace: handleDownscaleInPlace,
      onReupload: handleReuploadLogic, onReplace: handleReplace,
      onUploadAndReplace: uploadAndReplace, // 🔧 供 ImageNodeView 旋转/翻转后触发云存储上传
      onRemoveRefFile: handleRemoveRefFile, onRemoveRefText: handleRemoveRefText,
      onRemoveRefAudio: handleRemoveRefAudio, onHandleDoubleClick: handleHandleDoubleClick,
      onDisconnectDownstream: handleDisconnectDownstream,
      onMjAction: handleMjAction,
      mjToolboxConfigsRef,  // 多渠道工具箱配置数组
      onGptInpaint: handleGptInpaint,
      gptInpaintConfigRef,
      onH3Upscale: handleH3Upscale,
      h3UpscaleConfigRef,
      onDescribe: (...args) => callbacksRef.current._onDescribe?.(...args),
      onDescribeComplete: (...args) => callbacksRef.current._onDescribeComplete?.(...args),
      getMjDescribeConfig: () => callbacksRef.current.mjDescribeConfigRef?.current,
      mjDescribeConfigRef,
      onApplySkillbox: handleApplySkillbox, // ✨ 应用技能（弹出下游生成节点）
      onApplyPreset: handleApplyPreset,     // 🎨 预设工具箱按钮调用（从 presetsRef 查找）
      presetsRef,                    // 🎨 预设列表 ref（节点下拉 / 工具条读取）
      skillboxRef,                   // ✨ 技能工具箱 ref（节点工具条读取）
      onSlashAssetSpawn: (...args) => callbacksRef.current._onSlashAssetSpawn?.(...args),
      // 稳定代理：节点 data 里存的是这个箭头函数（闭包捕获 callbacksRef），
      // 每次调用时转发到 _onMentionSync，确保始终执行最新 handleMentionSync
      onMentionSync: (...args) => callbacksRef.current._onMentionSync?.(...args),
      globalAssetsRef, // 确保重绑时也带上 globalAssetsRef
      publicAssetsRef,
            globalSpellsRef,
    };
    // useLayoutEffect 每次渲染都会整体替换 callbacksRef.current 对象，
    // 必须在同一同步块内立即挂上 _onMentionSync，否则下次渲染前调用会找不到
    callbacksRef.current._onMentionSync = handleMentionSync;
    callbacksRef.current._onSlashAssetSpawn = handleSlashAssetSpawn;
    callbacksRef.current._onDescribe = handleDescribe;
    callbacksRef.current._onDescribeComplete = handleDescribeComplete;
  });

  const nodeCallbacks = useMemo(() => ({
    onUpdate: (...args) => callbacksRef.current.onUpdate?.(...args),
    onDelete: (...args) => callbacksRef.current.onDelete?.(...args),
    onGenerate: (...args) => callbacksRef.current.onGenerate?.(...args),
    onDetach: (...args) => callbacksRef.current.onDetach?.(...args),
    onReupload: (...args) => callbacksRef.current.onReupload?.(...args),
    onReplace: (...args) => callbacksRef.current.onReplace?.(...args),
    onUploadAndReplace: (...args) => callbacksRef.current.onUploadAndReplace?.(...args),
    onRemoveRefFile: (...args) => callbacksRef.current.onRemoveRefFile?.(...args),
    onRemoveRefText: (...args) => callbacksRef.current.onRemoveRefText?.(...args),
    onRemoveRefAudio: (...args) => callbacksRef.current.onRemoveRefAudio?.(...args),
    onSlashAssetSpawn: (...args) => callbacksRef.current.onSlashAssetSpawn?.(...args),
    onHandleDoubleClick: (...args) => callbacksRef.current.onHandleDoubleClick?.(...args),
    onDisconnectDownstream: (...args) => callbacksRef.current.onDisconnectDownstream?.(...args),
    onMjAction: (...args) => callbacksRef.current.onMjAction?.(...args),
    getMjToolboxConfig: (vendorId) => {
      // 根据 vendorId 查找对应的工具箱配置
      const configs = callbacksRef.current.mjToolboxConfigsRef?.current || [];
      const config = configs.find(c => c.vendorId === vendorId);
      return config || { name: '', logo: '', actionLabels: {}, disabledActions: [], creditCost: 0 };
    },
    onGptInpaint: (...args) => callbacksRef.current.onGptInpaint?.(...args),
    getGptInpaintConfig: () => callbacksRef.current.gptInpaintConfigRef?.current,
    onH3Upscale: (...args) => callbacksRef.current.onH3Upscale?.(...args),
    getH3UpscaleConfig: () => callbacksRef.current.h3UpscaleConfigRef?.current,
    onDescribe: (...args) => callbacksRef.current.onDescribe?.(...args),
    getMjDescribeConfig: () => callbacksRef.current.mjDescribeConfigRef?.current,
    onApplyPreset: (...args) => callbacksRef.current.onApplyPreset?.(...args),
    getPresets: () => callbacksRef.current.presetsRef?.current || { toolsets: [] },
    getSkillbox: () => callbacksRef.current.skillboxRef?.current || { toolsets: [] },
    onDownscaleInPlace: (...args) => callbacksRef.current.onDownscaleInPlace?.(...args),
  }), []); // 空依赖：wrapper 永远稳定，通过 callbacksRef 间接调用最新回调

  // 项目初始化后绑定回调
  useEffect(() => {
    if (!isInitialized) return;
    const timer = setTimeout(() => {
      setNodes(nds => {
        const needsRebind = nds.some(n => !n.data.onUpdate || !n.data.onHandleDoubleClick || !n.data.globalAssetsRef || !n.data.publicAssetsRef || !n.data.onDescribe);
        if (!needsRebind && nds.length > 0) return nds;
        return nds.map(node => ({ ...node, data: { ...node.data, ...callbacksRef.current } }));
      });
    }, 100);
    return () => clearTimeout(timer);
  }, [isInitialized]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 连线兜底：warm 上游资源的本地 blob ──
  // 目的：项目刚从磁盘/云端加载完就连线时，上游 localCacheUrl 往往还是 undefined，
  //   下游参考区只剩 OSS 直链 → 60px 缩略图被迫现拉整张原图（"刷半天"）。
  // 策略：
  //   - 已有内存 blob（existingBlob 是 blob:）→ 直接钉住，防止 LRU/定时 revoke 抽走；
  //   - 否则后台 warm 一次，拿到 blob 后按 refId 回填 localCacheUrl
  //     （handleUpdateNode 会自动传播到所有引用该 refId 的下游 refFiles/refAudios）并钉住。
  //   url 允许 http(s) 与 local-cache://（后者走本地查找）；blob:/data:/空 无需 warm。
  const warmRefBlob = useCallback((refId, url, fileType, existingBlob) => {
    if (existingBlob && existingBlob.startsWith('blob:')) {
      localCacheService.pinBlobUrl(existingBlob);
      return;
    }
    if (!url || url.startsWith('blob:') || url.startsWith('data:')) return;
    localCacheService.getLocalUrlOrFetch(url, crewIdRef.current, crewNameRef.current, fileType)
      .then(result => {
        if (result?.isLocal && result.url && result.url.startsWith('blob:')) {
          localCacheService.pinBlobUrl(result.url);
          handleUpdateNode(refId, { localCacheUrl: result.url });
        }
      })
      .catch(err => console.warn('连线兜底 warm blob 失败:', err));
  }, [handleUpdateNode]);

  // ── onConnect ──
  const onConnect = useCallback((params) => {
    setMenuData(null);
    // 🔗 落在了合法目标 handle 上：标记本次拖拽已连上，onConnectEnd 就不再弹添加菜单
    connectRef.current.made = true;
    // 🛡️ 防止自连接
    if (params.source === params.target) return;
    const sourceNode = getNodes().find(n => n.id === params.source);
    const targetNode = getNodes().find(n => n.id === params.target);
    if (!sourceNode || !targetNode) return;
    
    // 🛡️ 禁止多图层(PSD)节点作为上游输出（未合并的图层数据不能直接传递）
    // 生成图节点按单图输出处理：即使有 refFiles（参考图输入），也不视为 PSD 源节点。
    // 注意：refFiles 中被 generationRefIds 记录的图片是已被 AI 消费的参考图，不算图层叠加。
    const isPsdSource = (() => {
      const isGenerateImageSource = sourceNode.data.type === 'image'
        && !sourceNode.data.isDirectUpload
        && !sourceNode.data.assetId
        && !sourceNode.data.isDetached;

      if (sourceNode.data.annotationData && !isGenerateImageSource) return true;
      if (sourceNode.data.type !== 'image') return false;
      if (isGenerateImageSource) return false;

      const overlayRefs = (sourceNode.data.refFiles || []).filter(ref =>
        ref.type === 'image' || (typeof ref.url === 'string' && ref.url.match(/\.(jpeg|jpg|png|webp|bmp)/i))
      );
      if (overlayRefs.length === 0) return false;
      const consumedSet = new Set(sourceNode.data.generationRefIds || []);
      const activeOverlays = overlayRefs.filter(ref => !consumedSet.has(ref.id));
      return activeOverlays.length > 0;
    })();
    if (isPsdSource) {
      setAlertMsg("多图层节点需要先合并导出，才能连接到下游节点");
      return;
    }
    
    if ((sourceNode.data.type === 'audio' || sourceNode.data.type === 'video') &&
        (targetNode.data.type === 'image' || targetNode.data.title.includes('图像'))) {
      setAlertMsg(sourceNode.data.type === 'video'
        ? "视频无法连接到图像模型，请使用视频模型。"
        : "音频无法连接到图像模型，请使用视频模型。");
      return;
    }
    const connectionColor = C.TYPE_COLORS[sourceNode.data.type] || C.TYPE_COLORS.default;

    // 🔧 如果 target 节点是图片，且 source 也是图片，target 可能变成多图层节点
    // 此时需要断开 target 的所有下游连线并清理引用（多图层节点不能作为上游）
    //
    // ⚠️ 关键例外：target 是 AI 生成节点时不算多图层
    // AI 生成节点接收图片是"参考图"（refFiles），生成时会被消费写入 generationRefIds，
    // 输出仍是单张 output，可继续连下游。只有"素材节点 / 已剥离节点 / 资产节点"
    // 收到图片后才会进入图层叠加（PSD）状态。
    //
    // 修复 BUG (2026-06-03)：原代码无差别地对所有"图片→图片"断下游，
    // 导致生成节点连参考图后，无法再把生成结果传给下游 AI 节点（必须先剥离）。
    const targetIsGenerateNode = !targetNode.data.isDirectUpload
      && !targetNode.data.assetId
      && !targetNode.data.isDetached;
    const willBecomeMultiLayer = targetNode.data.type === 'image' &&
                                  sourceNode.data.type === 'image' &&
                                  sourceNode.data.output &&
                                  !targetIsGenerateNode;

    if (willBecomeMultiLayer) {
      // 完整断开下游：删除边 + 清理 refFiles
      handleDisconnectDownstream(params.target);
    }
    
    // ⚠️ 必须使用本地 setEdges 而非 setFlowEdges（useReactFlow().setEdges）
    //   handleDisconnectDownstream 内部用 setEdges 删除下游边，
    //   如果这里用 setFlowEdges 添加新边，两者操作不同 state，
    //   ReactFlow 内部状态会覆盖本地 state 的删除结果，导致旧边"复活"。
    setEdges(eds => addEdge({
        ...params, type: 'custom',
        data: { originalColor: connectionColor },
        style: { stroke: connectionColor, strokeWidth: C.EDGE_WIDTH_NORMAL, opacity: C.OPACITY_IDLE }
      }, eds)
    );
    setNodes(nds => nds.map(node => {
      if (node.id === params.target) {
        const newData = { ...node.data };

        // 🎬 特殊逻辑：分镜草图节点 → 视频节点
        //   把分镜节点的【上游图片】+【自己生成的成品图(标记为"分镜草稿")】+【右侧提示词(parseText)】带给视频节点。
        const isStoryboardToVideo = sourceNode.data.storyboardMode && targetNode.data.type === 'video';
        if (isStoryboardToVideo) {
          // ① 图片：取分镜节点上游的 image 类型 refFiles，按来源 id 去重后并入视频节点
          // 🎬 标记为 inherited: true，表示这是从分镜大师继承的素材，不应弹出上游节点
          const existingIds = new Set((newData.refFiles || []).map(f => f.id));
          const sbImages = (sourceNode.data.refFiles || [])
            .filter(f => f.type === 'image' && !existingIds.has(f.id))
            .map(f => ({ ...f, inherited: true }));  // 🔑 添加继承标记
          if (sbImages.length > 0) {
            newData.refFiles = [...(newData.refFiles || []), ...sbImages];
          }
          // ②【分镜草稿】把分镜节点自己生成的成品图也作为参考图带给视频节点。
          //    title 强制写死为"分镜草稿"（不随分镜节点改名变化），并打 isStoryboardDraft 标记，
          //    供 handleUpdateNode 在分镜重新出图时保留该标题。
          if (sourceNode.data.output && !existingIds.has(sourceNode.id)) {
            newData.refFiles = [...(newData.refFiles || []), {
              id: sourceNode.id,
              url: sourceNode.data.output,
              localCacheUrl: sourceNode.data.localCacheUrl,
              thumbUrl: sourceNode.data.thumbUrl,  // 🖼️ 带上 400px 缩略图，参考框不拉全尺寸原图
              title: '分镜草稿',
              type: 'image',
              isStoryboardDraft: true,
              assetId: sourceNode.data.assetId  // 🔗 记录资产ID
            }];
          }
          // ③ 文字：把分镜右侧提示词追加到视频节点 prompt 末尾
          const sbText = (sourceNode.data.parseText || '').trim();
          if (sbText) {
            const cur = (newData.prompt || '').trim();
            newData.prompt = cur ? `${cur}\n${sbText}` : sbText;

            // ✅ 同时生成 Slate 富文本结构，支持气泡解析
            // 素材库：分镜节点的 refFiles（包含上游参考图 + 分镜成品图）
            const assets = (sourceNode.data.refFiles || []).map(f => ({
              id: f.id,
              title: f.title,
              type: f.type,
              category: f.category || 'prop',  // 默认分类
              mini_thumbnail_url: f.thumbUrl,
              thumbUrl: f.thumbUrl,
              url: f.url
            }));

            // 调用 parseTextToSlate 解析气泡（将 "角色" 或 @「角色」转为 mention 节点）
            const slateNodes = parseTextToSlate(newData.prompt, assets);
            newData.slateValue = JSON.stringify(slateNodes);

            // 🔍 调试日志：验证传递逻辑
            console.log('[分镜→视频连线] parseText:', sbText.substring(0, 100));
            console.log('[分镜→视频连线] 最终 prompt:', newData.prompt.substring(0, 100));
            console.log('[分镜→视频连线] slateValue 长度:', newData.slateValue?.length);
            console.log('[分镜→视频连线] 视频节点原 prompt:', cur);
            console.log('[分镜→视频连线] 视频节点原 slateValue:', targetNode.data.slateValue);
          }
          newData.openConsoleOnConnect = true;
          return { ...node, data: newData, selected: true };
        }

        if (sourceNode.data.type === 'text') {
          newData.refTexts = [...(newData.refTexts || []), {
            id: sourceNode.id, title: sourceNode.data.title,
            text: sourceNode.data.prompt, bgColor: sourceNode.data.bgColor || '#1e293b'
          }];
        } else if (sourceNode.data.type === 'audio') {
          newData.refAudios = [...(newData.refAudios || []), {
            id: sourceNode.id, url: sourceNode.data.output,
            localCacheUrl: sourceNode.data.localCacheUrl, title: sourceNode.data.title
          }];
        } else if (sourceNode.data.output) {
          newData.refFiles = [...(newData.refFiles || []), {
            id: sourceNode.id, url: sourceNode.data.output,
            localCacheUrl: sourceNode.data.localCacheUrl,
            thumbUrl: sourceNode.data.thumbUrl,  // 🖼️ 带上 400px 缩略图，参考框不拉全尺寸原图
            title: sourceNode.data.title, type: sourceNode.data.type,
            description: sourceNode.data.description,  // 🆕 传递描述词
            descEnabled: sourceNode.data.descEnabled,   // 🆕 传递描述词开关状态
            // ❌ 移除 assetId，连线是传递内容不是引用资产
          }];
        }
        newData.openConsoleOnConnect = true;
        return { ...node, data: newData, selected: true };
      }
      return { ...node, selected: false };
    }));

    // ⚡ 连线兜底：确保上游资源在下游参考区有可用的本地 blob（见 warmRefBlob 注释）。
    //   按 sourceNode.id 回填 —— 覆盖普通图片/音视频连线里以 sourceNode.id 为 refId 的参考条目。
    if (sourceNode.data.type === 'image' || sourceNode.data.type === 'video' || sourceNode.data.type === 'audio') {
      const fileType = sourceNode.data.type === 'video' ? 'video'
                     : sourceNode.data.type === 'audio' ? 'audio' : 'image';
      warmRefBlob(sourceNode.id, sourceNode.data.output, fileType, sourceNode.data.localCacheUrl);
    }

    // ⚡ 连线兜底（补漏）：分镜节点 → 视频节点时，分镜顺带拖过去的【上游角色图】的 id 是
    //   角色 mention 节点 id，不等于 sourceNode.id，上面那段按 sourceNode.id 的兜底覆盖不到它们，
    //   历史节点又没有 thumbUrl → 视频节点参考区只能拉全尺寸原图（"一直刷新"）。
    //   这里对这些角色图按各自 id warm：角色 mention 节点仍在画布上，id 对得上，
    //   handleUpdateNode 会把 blob 同步到视频节点里的副本，无需依赖 thumbUrl。
    if (sourceNode.data.storyboardMode && targetNode.data.type === 'video') {
      const existingIds = new Set((targetNode.data.refFiles || []).map(f => f.id));
      (sourceNode.data.refFiles || [])
        .filter(f => f.type === 'image' && !existingIds.has(f.id) && f.id !== sourceNode.id)
        .forEach(f => warmRefBlob(f.id, pickDurableUrl(f.url, f.localCacheUrl), 'image', f.localCacheUrl));
    }
  }, [getNodes, setEdges, setNodes, setAlertMsg, setMenuData, handleDisconnectDownstream, handleUpdateNode, warmRefBlob]);

  // ── onConnectStart：记录拖拽起点，清空「已连上」标记 ──
  const onConnectStart = useCallback((_e, { nodeId, handleType }) => {
    connectRef.current = { nodeId, handleType, made: false };
  }, []);

  // ── onConnectEnd：松手时若没连上任何 handle，就在鼠标位置弹「可连接节点」菜单 ──
  //   同时覆盖两种操作：① 单击端口（起点=终点、没连上）② 从端口拖到空白处。
  //   只处理从「输出点(source)」发起的连接——从这里才生成下游节点。
  const onConnectEnd = useCallback((event) => {
    const { nodeId, handleType, made } = connectRef.current;
    connectRef.current = { nodeId: null, handleType: null, made: false };
    if (made || !nodeId || handleType !== 'source') return;

    const sNode = getNodes().find(n => n.id === nodeId);
    if (!sNode) return;
    // 🛡️ PSD 多图层节点禁止生成下游（需先合并导出）
    if (sNode.data.annotationData) {
      setAlertMsg("多图层节点需要先合并导出，才能连接到下游节点");
      return;
    }
    // 触屏事件取 changedTouches，鼠标事件直接取 clientX/Y。
    //   ⚠️ 坐标必须现在就取出：event 是原生 mouseup/touchend，defer 后可能已失效。
    const pt = event.changedTouches?.[0] || event;
    const dropX = pt.clientX, dropY = pt.clientY;
    // 菜单 X = 落点（左边线压在连线终点）；顶部固定放在终点上方 30px：
    //   终点距菜单顶 30px，越过 12px 圆角落在左边线上。用固定小距离而非固定上移量，
    //   菜单高矮（1项/2项）都成立——上移量过大时矮菜单会让终点掉到菜单下方外面。
    const mouseX = dropX, mouseY = dropY - 30;
    // 预览线起点不再在这里快照——PreviewConnectLine 用 sourceId 从 store 实时读
    //   源节点 handleBounds 算，与真实连线同源，节点尺寸/handleBounds 变化也跟得上。
    // 预览线终点 = 松手落点(dropX/dropY)本身，与新节点左侧输入点对齐（新节点左边缘对齐落点X、
    //   垂直中心对齐落点Y），三者重合 → 菜单左边线、连线终点、新节点输入点完全对齐。
    // dropFlowPos：松手落点的画布坐标，用作新建下游节点的位置。
    const dropFlowPos = screenToFlowPosition({ x: dropX, y: dropY });
    const payload = {
      mouseX, mouseY, sourceId: nodeId, sourceType: sNode.data.type, sourceNodePos: sNode.position,
      dropFlowPos, previewEndX: dropX, previewEndY: dropY,
    };
    // 🖱️ 桌面端鼠标 mouseup 之后紧跟一个合成 click，会冒泡到 onNodeClick/onPaneClick 触发
    //   dismissMenus 把菜单又关掉（iPad 触屏无此合成 click，故那边正常）。
    //   延到下一拍再弹，落在那次 dismiss 之后，两端行为一致。
    setTimeout(() => setMenuData(payload), 0);
  }, [getNodes, setMenuData, setAlertMsg, screenToFlowPosition]);

  // ━━━ 🔗 多选框加号拖拽 ━━━
  // 新流程：从多选框加号拖拽 → 松手弹菜单 → 选择后新建节点 + refFiles

  // 1️⃣ 拖拽开始：从 MultiSelectHandle 加号发起
  const handleMultiSelectConnectStart = useCallback((e, sourceNodeIds) => {
    if (!sourceNodeIds || sourceNodeIds.length === 0) return;

    setMultiSelectConnect({
      isActive: true,
      sourceNodeIds,
      currentMousePos: { x: e.clientX, y: e.clientY }
    });
  }, []);

  // 2️⃣ 拖拽中 + 松手：全局监听
  useEffect(() => {
    if (!multiSelectConnect.isActive) return;

    const handleMouseMove = (e) => {
      setMultiSelectConnect(prev => ({
        ...prev,
        currentMousePos: { x: e.clientX, y: e.clientY }
      }));
    };

    const handleMouseUp = (e) => {
      // 松手时弹菜单
      handleMultiSelectConnectEnd(e);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [multiSelectConnect.isActive]);

  // 3️⃣ 拖拽结束：弹菜单
  const handleMultiSelectConnectEnd = useCallback((e) => {
    if (!multiSelectConnect.isActive) return;

    const { sourceNodeIds } = multiSelectConnect;
    setMultiSelectConnect({
      isActive: false,
      sourceNodeIds: [],
      currentMousePos: { x: 0, y: 0 }
    });

    // 获取松手位置
    const pt = e.changedTouches?.[0] || e;
    const dropX = pt.clientX, dropY = pt.clientY;

    // 如果没有源节点，取消
    if (!sourceNodeIds || sourceNodeIds.length === 0) return;

    const sourceNodes = getNodes().filter(n => sourceNodeIds.includes(n.id));
    if (sourceNodes.length === 0) return;

    // 推断源节点的主要类型（用于菜单过滤）
    const sourceType = sourceNodes[0]?.data?.type || 'image';

    // 菜单位置：仿照 onConnectEnd 的逻辑
    const mouseX = dropX, mouseY = dropY - 30;
    const dropFlowPos = screenToFlowPosition({ x: dropX, y: dropY });

    const payload = {
      sourceIds: sourceNodeIds,        // 🔑 改为数组
      sourceNodePos: sourceNodes[0].position,  // 🔑 添加第一个源节点位置（用于节点定位）
      sourceType,                       // 用于菜单过滤
      dropFlowPos,
      previewEndX: dropX,
      previewEndY: dropY,
      mouseX, mouseY,
      isMultiSelect: true               // 标记这是多源连接
    };

    setTimeout(() => setMenuData(payload), 0);
  }, [multiSelectConnect.isActive, getNodes, screenToFlowPosition, setMenuData]);

  // ━━━ 复制 / 粘贴 / 设为封面（右键菜单动作） ━━━
  const handleDuplicateNode = useCallback(() => {
    if (!contextMenu?.targetNodeId) return;
    const originalNode = getNodes().find(n => n.id === contextMenu.targetNodeId);
    if (!originalNode) return;
    const id = `node-${Date.now()}`;
    const offset = 50 + Math.random() * 50;
    const newNode = {
      ...originalNode, id,
      position: { x: originalNode.position.x + offset, y: originalNode.position.y + offset },
      selected: true,
      data: {
        ...originalNode.data, id, title: `${originalNode.data.title} (副本)`,
        // 副本继承原节点的媒体内容（output/outputs/localCacheUrl），但重置生成状态
        loading: false, error: null,
        generationStartTime: null, statusInfo: null,
        refFiles: (originalNode.data.refFiles || []).map(f => ({ ...f })),
        refTexts: (originalNode.data.refTexts || []).map(t => ({ ...t })),
        refAudios: (originalNode.data.refAudios || []).map(a => ({ ...a })),
        onUpdate: handleUpdateNode, onDelete: handleDeleteNodeButton, onGenerate: handleGenerate,
        onDetach: handleDetachImage, onDownscaleInPlace: handleDownscaleInPlace, onReupload: handleReuploadLogic, onReplace: handleReplace,
        onRemoveRefFile: handleRemoveRefFile, onRemoveRefText: handleRemoveRefText, onRemoveRefAudio: handleRemoveRefAudio,
        onSlashAssetSpawn: onSlashAssetSpawnProxy,
        onHandleDoubleClick: (e, nodeId) => {
          const sNode = getNodes().find(n => n.id === nodeId);
          if (sNode) setMenuData({ mouseX: e.clientX + 20, mouseY: e.clientY + 20, sourceId: nodeId, sourceType: sNode.data.type, sourceNodePos: sNode.position });
        }
      }
    };
    setNodes(nds => [...nds.map(n => ({ ...n, selected: false })), newNode]);
    const incomingEdges = getEdges().filter(e => e.target === contextMenu.targetNodeId);
    const newEdges = incomingEdges.map(e => ({
      ...e, id: `edge-${e.source}-${id}-${Date.now()}`, target: id,
      style: { ...e.style, opacity: C.OPACITY_IDLE }
    }));
    setFlowEdges(eds => [...eds, ...newEdges]);
    setContextMenu(null);
    setAlertMsg("已创建副本");
  }, [contextMenu, getNodes, getEdges, handleUpdateNode, handleGenerate, handleDetachImage,
      handleReuploadLogic, handleDeleteNodeButton, handleRemoveRefFile, handleRemoveRefText,
      handleRemoveRefAudio, handleReplace, onSlashAssetSpawnProxy,
      setFlowEdges, setNodes, setAlertMsg, setMenuData, setContextMenu]);

  const handleSystemCopy = useCallback(async () => {
    if (!contextMenu?.targetNodeId) return;
    const node = getNodes().find(n => n.id === contextMenu.targetNodeId);
    if (!node) return;
    try {
      if (node.data.type === 'text') {
        const ok = await copyTextToClipboard(node.data.prompt || node.data.text);
        setAlertMsg(ok ? "文本已复制到剪贴板" : { text: "复制失败", type: 'warning' });
      } else if (node.data.type === 'image') {
        // ⚠️ 图片复制只有 navigator.clipboard.write 一条路，execCommand 顶不了。
        //    http（非安全上下文，如学校局域网站）下 navigator.clipboard 是 undefined，
        //    这里要说清原因，别丢一句看不出所以然的「复制失败」。
        if (!navigator.clipboard?.write) {
          setAlertMsg({ text: '当前站点为 http 访问，浏览器不允许复制图片，请右键另存', type: 'warning' });
        } else {
          const r = await fetch(node.data.localCacheUrl || node.data.output);
          const b = await r.blob();
          await navigator.clipboard.write([new ClipboardItem({ [b.type]: b })]);
          setAlertMsg("图片已复制到剪贴板");
        }
      } else {
        setAlertMsg("暂不支持此类资源的系统复制");
      }
    } catch (err) { setAlertMsg("复制失败"); }
    setContextMenu(null);
  }, [contextMenu, getNodes, setAlertMsg]);

  // 📋 右键菜单「复制分享链接」— 复制节点媒体的网络地址到剪贴板
  const handleCopyShareLink = useCallback(() => {
    if (!contextMenu?.targetNodeId) return;
    const node = getNodes().find(n => n.id === contextMenu.targetNodeId);
    if (!node) { setContextMenu(null); return; }
    const url = node.data.output;
    if (!url || url.startsWith('blob:') || url.startsWith('local-cache://')) {
      setAlertMsg({ text: '该节点尚未上传，没有网络地址', type: 'warning' });
      setContextMenu(null); return;
    }
    copyTextToClipboard(url).then((ok) => {
      setAlertMsg(ok ? '分享链接已复制' : { text: '复制失败', type: 'warning' });
    });
    setContextMenu(null);
  }, [contextMenu, getNodes, setAlertMsg]);

  const handleSetAsCover = useCallback(() => {
    if (!contextMenu?.targetNodeId) return;
    const node = getNodes().find(n => n.id === contextMenu.targetNodeId);
    if (!node || node.data.type !== 'image' || !node.data.output) {
      setAlertMsg({ text: "只有图片节点可以设为封面", type: "warning" });
      setContextMenu(null); return;
    }
    if (node.data.annotationData) {
      setAlertMsg({ text: "多图层节点不支持设为封面，请先合并导出", type: "warning" });
      setContextMenu(null); return;
    }
    setCoverCropUrl(node.data.localCacheUrl || node.data.output);
    setContextMenu(null);
  }, [contextMenu, getNodes, setAlertMsg, setCoverCropUrl]);

  /**
   * 🖼️ 右键菜单「设为海报」— 编辑器内入口
   *
   * 入口：画布右键菜单 → 「设为海报」（仅项目负责人可见，仅图片节点可用）
   * 前置条件：
   *   - crewRole === 'producer'（非项目负责人弹警告）
   *   - 目标节点为 image 类型且有 output URL
   *   - 目标节点不是多图层注释节点（annotationData 存在时不支持）
   *
   * 流程：
   *   1. 取节点的 localCacheUrl（优先）或 output 作为裁剪源图
   *      → 优先用 localCacheUrl（本地 blob），避免 OSS 跨域导致 canvas tainted
   *   2. 设置 posterCropUrl，触发 CoverCropperModal（aspectRatio=9/16）
   *   3. 裁剪完成 → handlePosterSave 上传
   */
  const handleSetAsPoster = useCallback(() => {
    if (!contextMenu?.targetNodeId) return;
    if (crewRole !== 'producer') {
      setAlertMsg({ text: "只有项目负责人可以设置项目海报", type: "warning" });
      setContextMenu(null); return;
    }
    const node = getNodes().find(n => n.id === contextMenu.targetNodeId);
    if (!node || node.data.type !== 'image' || !node.data.output) {
      setAlertMsg({ text: "只有图片节点可以设为海报", type: "warning" });
      setContextMenu(null); return;
    }
    if (node.data.annotationData) {
      setAlertMsg({ text: "多图层节点不支持设为海报，请先合并导出", type: "warning" });
      setContextMenu(null); return;
    }
    // 优先用本地缓存 blob URL，避免 OSS CDN 跨域导致裁剪框 canvas tainted
    setPosterCropUrl(node.data.localCacheUrl || node.data.output);
    setContextMenu(null);
  }, [contextMenu, getNodes, setAlertMsg, crewRole]);

  /**
   * 🖼️ 海报裁剪完成后上传（编辑器入口）
   *
   * @param {Blob} blob - CoverCropperModal 裁剪输出的 JPEG Blob（810×1440，9:16）
   *
   * 流程：
   *   1. 上传 blob 到 /api/upload?type=thumbnail（存服务器本地 /thumbnails/，不走 OSS）
   *   2. PUT /api/crews/:id { poster: url } 持久化到数据库
   *   3. 仪表盘会在下次加载时从 crews.poster 字段读取并显示浮动海报
   *
   * ⚠️ 编辑器不直接维护 crews state，海报更新后仅 toast 提示；
   *    仪表盘的 currentCrew.poster 在下次进入仪表盘时自动刷新。
   */
  const handlePosterSave = useCallback(async (blob) => {
    if (!crewId) return;
    const formData = new FormData();
    const fileName = `poster-${crewId}-${Date.now()}.jpg`;
    formData.append('file', blob, fileName);
    try {
      const uploadData = await api.upload(`/api/upload?type=thumbnail`, formData);
      if (uploadData.url) {
        await api.put(`/api/crews/${crewId}`, { poster: uploadData.url });
        setAlertMsg({ text: "项目海报已设置", type: "success" });
      }
    } catch (e) {
      console.error('保存海报失败:', e);
      setAlertMsg({ text: "保存海报失败", type: "error" });
    }
    setPosterCropUrl(null);
  }, [crewId, setAlertMsg]);

  // ━━━ 项目管理 ━━━
  const {
    handleCreateProject, handleRenameProject, handleDeleteProject,
    handleSetProjectCover, handleSelectCoverImage,
    handleCoverSave, handleOtherProjectCoverSave, handleCloseCoverModals,
  } = useProjectManagement({
    projectId, setAllProjects, setProjectName, setTempName, setAlertMsg,
    setCoverCropUrl, setCoverProject, setProjectImages, setShowImagePicker,
    navigate, coverProject, crewIdRef, crewNameRef,
  });

  // ━━━ 素材管理 ━━━
  const {
    handleSpawnAsset, handleDeleteAsset, handleUpdateAsset, handleUpdateAssetDescription, handleDeletePublicAsset,
    handleDeleteGlobalAsset, handleCommitSave, handleUploadToPublic, handleDownloadFromPublic, handleOpenSaveModal,
    handleSetAssetCover,
    handleEditAsset, // 🆕 编辑资产回调
  } = useAssetManagement({
    setNodes, setEdges: setFlowEdges, setGlobalAssets, setPublicAssets, setGlobalSpells,
    setAlertMsg, setDrawerOpen, setActiveTab, setSaveModalData, setMenuData, setContextMenu,
    globalAssetsRef, publicAssetsRef, globalSpellsRef, crewIdRef, crewNameRef,
    projectId, handleUpdateNode, handleDeleteNodeButton, handleDetachImage,
    handleReuploadLogic, handleReplace, handleGenerate,
    handleRemoveRefFile, handleRemoveRefText, handleRemoveRefAudio,
    revokeSecureBlob, getSmartSpawnPosition, onSlashAssetSpawn: onSlashAssetSpawnProxy, saveModalData, contextMenu,
    callbacksRef,
  });

  // ━━━ 节点工厂（新建节点） ━━━
  const buildNodeData = useCallback((id, type, title, extra = {}) => {
    const nodeData = {
    id, type, title, refFiles: [], refTexts: [], refAudios: [], batchCount: 1, outputs: [],
    bgColor: type === 'text' ? getRandomTextBg() : undefined,
    globalAssetsRef,
    publicAssetsRef,
    globalSpellsRef,
    onUpdate: handleUpdateNode, onDelete: handleDeleteNodeButton, onGenerate: handleGenerate,
    onDetach: handleDetachImage, onDownscaleInPlace: handleDownscaleInPlace, onReupload: handleReuploadLogic, onReplace: handleReplace,
    onUploadAndReplace: uploadAndReplace,       // 🔧 供 ImageNodeView 旋转/翻转后触发云存储上传
    onRemoveRefFile: handleRemoveRefFile, onRemoveRefText: handleRemoveRefText, onRemoveRefAudio: handleRemoveRefAudio,
    onDisconnectDownstream: handleDisconnectDownstream, // 🔧 供 NodeConsole 右键断开下游连线
    onMentionSync: (...args) => callbacksRef.current._onMentionSync?.(...args),
    onSlashAssetSpawn: (...args) => callbacksRef.current._onSlashAssetSpawn?.(...args),
    onDescribe: (...args) => callbacksRef.current._onDescribe?.(...args),
    onDescribeComplete: (...args) => callbacksRef.current._onDescribeComplete?.(...args),
    getMjDescribeConfig: () => callbacksRef.current.mjDescribeConfigRef?.current,
    onApplyPreset: (...args) => callbacksRef.current.onApplyPreset?.(...args),
    getPresets: () => callbacksRef.current.presetsRef?.current || { toolsets: [] },
    getSkillbox: () => callbacksRef.current.skillboxRef?.current || { toolsets: [] },
    onHandleDoubleClick: (e, nid) => {
      const sNode = getNodes().find(n => n.id === nid);
      setMenuData({ mouseX: e.clientX + 20, mouseY: e.clientY + 20, sourceId: nid, sourceType: sNode.data.type, sourceNodePos: sNode.position });
    },
    ...extra,
    };

    // 🧠 新建即套用上次的模型 + 参数（预设节点 / 已带模型的除外）。
    //   普通节点与分镜大师各用独立记忆槽（storyboard），互不污染。
    //   两者都按上次比例摆正朝向：普通节点固定长边，分镜大师固定短边（与 NodeConsole
    //   handleModelChange 一致）。上次模型若已被删/下架这里不校验（拿不到该类型模型列表），
    //   控制台打开时 currentModel 找不到即视为未选，用户重选即可，不影响其他功能。
    if (!extra.modelId && !extra.presetId) {
      const isStoryboard = !!extra.storyboardMode;
      const mem = readNodeDefaults(isStoryboard ? 'storyboard' : type);
      if (mem && mem.modelId) {
        nodeData.modelId = mem.modelId;
        nodeData.modelName = mem.modelName;
        nodeData.modelLogo = mem.modelLogo;
        nodeData.params = { ...(mem.params || {}) };
        // 🔧 支持 aspect_ratio、ratio、size 三种比例参数
        const ratioValue = mem.params?.aspect_ratio || mem.params?.ratio || mem.params?.size;
        if (ratioValue) {
          const base = nodeData.customSize || { width: 320, height: 240 };
          const sz = ratioOrientationSize(ratioValue, base, isStoryboard);
          if (sz) nodeData.customSize = sz;
        }
      }
    }

    return nodeData;
  }, [handleUpdateNode, handleDeleteNodeButton, handleGenerate, handleDetachImage,
        handleReuploadLogic, handleReplace, uploadAndReplace, handleDisconnectDownstream,
        handleRemoveRefFile, handleRemoveRefText,
        handleRemoveRefAudio, getNodes, setMenuData, globalAssetsRef, publicAssetsRef, globalSpellsRef]);
  // ⚠️ 注意：buildNodeData 的回调列表必须与 callbacksRef.current（useLayoutEffect 中）
  //   和项目加载时的回调绑定（loadProjectData 中）保持一致，
  //   否则新建节点 / 加载节点会缺少某些回调导致功能失效。
  //   历史教训：缺少 onUploadAndReplace 导致旋转/翻转后上传不触发 → 图片丢失。

  // 反推结果到达后，在图片节点左侧生成文本节点并连线
  const handleDescribeComplete = useCallback((nodeId, texts) => {
    // 防御性归一化
    let normalizedTexts = [];
    if (Array.isArray(texts)) {
      normalizedTexts = texts.map(t => (typeof t === 'string' ? t.trim() : '')).filter(Boolean);
    } else if (typeof texts === 'string') {
      normalizedTexts = texts.split('\n').map(s => s.trim()).filter(Boolean);
    }
    if (normalizedTexts.length === 0) return;

    const allNodes = getNodes();
    const targetNode = allNodes.find(n => n.id === nodeId);
    if (!targetNode) return;

    // 清除节点上的 describeResult
    handleUpdateNode(nodeId, { describeResult: null });

    // 参照 handleSlashAssetSpawn 的布局逻辑：左侧 560px，纵向排列
    const getNodeHeight = (node) => (
      node?.data?.customSize?.height ||
      (node?.data?.type === 'audio' ? 56 : (node?.data?.type === 'text' ? 150 : 280))
    );
    const linkedSourceNodes = allNodes.filter(n =>
      n.id.startsWith(`describe-${nodeId}-`)
    );
    const occupiedBottom = linkedSourceNodes.length > 0
      ? Math.max(...linkedSourceNodes.map(n => n.position.y + getNodeHeight(n)))
      : null;
    const startY = occupiedBottom !== null ? occupiedBottom + 40 : targetNode.position.y;
    const sourceX = targetNode.position.x - 560;

    const newNodes = [];
    const newEdgeData = [];
    normalizedTexts.forEach((text, i) => {
      const nid = `describe-${nodeId}-${Date.now()}-${i}`;
      newNodes.push({
        id: nid,
        type: 'aiNode',
        position: { x: sourceX, y: startY + i * 190 },
        data: buildNodeData(nid, 'text', `反推提示词 ${i + 1}`, {
          prompt: text,
          text,
          bgColor: getRandomTextBg(),
          customSize: { width: 240, height: 150 },
        }),
      });
      newEdgeData.push({ source: nid, target: nodeId });
    });

    setNodes(nds => [...nds, ...newNodes]);
    setFlowEdges(eds => {
      let result = eds;
      for (const { source, target } of newEdgeData) {
        result = addEdge({
          source,
          target,
          type: 'custom',
          data: { originalColor: C.TYPE_COLORS.text || C.TYPE_COLORS.default },
          style: {
            stroke: C.TYPE_COLORS.text || C.TYPE_COLORS.default,
            strokeWidth: C.EDGE_WIDTH_NORMAL,
            opacity: C.OPACITY_IDLE,
          },
        }, result);
      }
      return result;
    });

    setAlertMsg({ type: 'success', text: `反推完成，生成 ${normalizedTexts.length} 条提示词` });
  }, [getNodes, handleUpdateNode, buildNodeData, setNodes, setFlowEdges, setAlertMsg]);

  const handleSlashAssetSpawn = useCallback((asset, targetNodeId) => {
    if (!asset || asset.type !== 'text') return;

    const allNodes = getNodes();
    const targetNode = allNodes.find(n => n.id === targetNodeId);
    if (!targetNode) return;

    const sourceNodeId = `slash-${asset.id}-for-${targetNodeId}`;
    const existingNode = allNodes.find(n => n.id === sourceNodeId);
    const spellText = asset.text || asset.url || '';
    const spellBgColor = asset.bgColor || getRandomTextBg();

    setNodes((nds) => {
      let sourceExists = false;

      const updatedNodes = nds.map((node) => {
        if (node.id === sourceNodeId) {
          sourceExists = true;
          return {
            ...node,
            data: {
              ...node.data,
              title: asset.title,
              prompt: spellText,
              text: spellText,
              bgColor: spellBgColor,
              // ❌ 移除 assetId，咒语库是模板不是引用
              isSystemSpell: !!asset.isEncrypted,
            },
          };
        }

        if (node.id !== targetNodeId) {
          return node;
        }

        const keepRefTexts = (node.data.refTexts || []).filter((ref) => ref.id !== sourceNodeId);
        return {
          ...node,
          data: {
            ...node.data,
            refTexts: [
              ...keepRefTexts,
              {
                id: sourceNodeId,
                title: asset.title,
                text: spellText,
                bgColor: spellBgColor,
              },
            ],
          },
        };
      });

      if (sourceExists) return updatedNodes;

      const linkedSourceNodes = nds.filter((node) =>
        node.id.endsWith(`-for-${targetNodeId}`) &&
        (node.id.startsWith('slash-') || node.id.startsWith('mention-'))
      );
      const getNodeHeight = (node) => (
        node?.data?.customSize?.height ||
        (node?.data?.type === 'audio' ? 56 : (node?.data?.type === 'text' ? 150 : 280))
      );
      const occupiedBottom = linkedSourceNodes.length > 0
        ? Math.max(...linkedSourceNodes.map((node) => node.position.y + getNodeHeight(node)))
        : null;
      const nextY = occupiedBottom !== null ? occupiedBottom + 40 : targetNode.position.y - 320;
      const sourceX = targetNode.position.x - 560;

      const newNode = {
        id: sourceNodeId,
        type: 'aiNode',
        position: {
          x: sourceX,
          y: nextY,
        },
        data: buildNodeData(sourceNodeId, 'text', asset.title, {
          prompt: spellText,
          text: spellText,
          bgColor: spellBgColor,
          // ❌ 移除 assetId，咒语库是模板不是引用
          isSystemSpell: !!asset.isEncrypted,
          customSize: { width: 240, height: 60 },
        }),
      };

      return [...updatedNodes, newNode];
    });

    setFlowEdges((eds) => {
      if (eds.some((edge) => edge.source === sourceNodeId && edge.target === targetNodeId)) {
        return eds;
      }

      return addEdge({
        source: sourceNodeId,
        target: targetNodeId,
        type: 'custom',
        data: { originalColor: C.TYPE_COLORS.text || C.TYPE_COLORS.default },
        style: {
          stroke: C.TYPE_COLORS.text || C.TYPE_COLORS.default,
          strokeWidth: C.EDGE_WIDTH_NORMAL,
          opacity: C.OPACITY_IDLE,
        },
      }, eds);
    });

    setAlertMsg({
      text: existingNode ? `已定位咒语 "${asset.title}"` : `已插入咒语 "${asset.title}"`,
      type: 'success',
    });
  }, [buildNodeData, getNodes, setNodes, setFlowEdges, setAlertMsg]);

  // ── @ 提及同步：根据 prompt 文本中的 @name 同步参考图 + 左侧节点 + 连线 ──
  const handleMentionSync = useCallback(async (matchedAssets, targetNodeId) => {
    const allNodes = getNodes();
    const targetNode = allNodes.find(n => n.id === targetNodeId);
    if (!targetNode) return;

    // 🎬 判断：如果目标节点的上游有分镜大师节点，不弹出新的资产节点（只更新 refFiles）
    //   但如果目标节点本身就是分镜大师，则正常创建节点
    const isStoryboardNode = targetNode.data?.storyboardMode === true;
    const allEdges = getEdges();
    const hasStoryboardUpstream = !isStoryboardNode && allEdges.some(edge => {
      if (edge.target !== targetNodeId) return false;
      const sourceNode = allNodes.find(n => n.id === edge.source);
      return sourceNode && sourceNode.data?.storyboardMode === true;
    });

    // 🔧 规范化资产数据：确保 width/height 字段存在
    const normalizedAssets = matchedAssets.map(a => normalizeAsset(a));

    const getMentionNodeId = (assetId) => `mention-${assetId}-for-${targetNodeId}`;

    // ── 1. 计算需要创建/删除的节点 ──
    const currentMentionNodes = allNodes.filter(n =>
      n.id.startsWith('mention-') && n.id.endsWith(`-for-${targetNodeId}`)
    );
    const currentAssetIds = new Set(currentMentionNodes.map(n => {
      const s = n.id.replace('mention-', '').replace(`-for-${targetNodeId}`, '');
      return s;
    }));
    const newAssetIds = new Set(normalizedAssets.map(a => String(a.id)));

    // 🎯 智能判断：
    // - 如果上游有分镜大师节点，不创建任何上游节点（素材从分镜大师继承）
    // - 如果没有分镜大师上游，为"不在当前 mention 节点中"的素材创建节点
    const toCreate = hasStoryboardUpstream
      ? []  // 🔑 有分镜大师上游时，不创建任何节点
      : normalizedAssets.filter(a => !currentAssetIds.has(String(a.id)));

    // 🔧 删除节点的逻辑：只删除"提示词中不再提及"的节点
    // ⚠️ 关键判断：如果上游有分镜大师节点，不自动删除任何节点
    //    因为此时节点是用户手动管理的，不应该被自动同步逻辑删除
    const removeNodeIds = new Set();
    if (!hasStoryboardUpstream) {
      currentMentionNodes.forEach(n => {
        const aid = n.id.replace('mention-', '').replace(`-for-${targetNodeId}`, '');
        // 只有当素材不在新的提及列表中时，才删除节点
        if (!newAssetIds.has(aid)) {
          removeNodeIds.add(n.id);
        }
      });
    }

    if (hasStoryboardUpstream) {
      console.log('[@ 提及] 上游有分镜大师节点，跳过弹出资产节点，仅更新 refFiles');
    }

    // ── 2. 构建新节点 ──
    // 统一排布 mention/slash 引用节点：同一目标节点左侧共用一条纵向队列
    const linkedSourceNodes = allNodes.filter(n =>
      n.id.endsWith(`-for-${targetNodeId}`) &&
      (n.id.startsWith('mention-') || n.id.startsWith('slash-')) &&
      !removeNodeIds.has(n.id)
    );
    const getNodeHeight = (node) => (
      node?.data?.customSize?.height ||
      (node?.data?.type === 'audio' ? 56 : (node?.data?.type === 'text' ? 150 : 280))
    );
    const occupiedBottom = linkedSourceNodes.length > 0
      ? Math.max(...linkedSourceNodes.map(n => n.position.y + getNodeHeight(n)))
      : null;
    let nextY = occupiedBottom !== null ? occupiedBottom + 40 : targetNode.position.y - 320;
    const sourceX = targetNode.position.x - 560;

    // 🔧 预加载所有图片/视频的真实尺寸（同步等待，确保间距计算正确）
    const sizeCache = new Map();
    await Promise.all(
      toCreate.map(async (asset) => {
        const isAudioCategory = ['music', 'sfx', 'voice'].includes(asset.category);
        const nodeType = ['image', 'video', 'audio'].includes(asset.type)
          ? asset.type
          : (isAudioCategory ? 'audio' : 'image');

        if (nodeType === 'audio') {
          sizeCache.set(asset.id, { width: 160, height: 56 });
        } else {
          // 图片/视频：从资产数据读取尺寸，如果没有则加载图片
          if (asset.width && asset.height) {
            const size = calculateLongSideCustom(asset.width, asset.height, 90); // 🔑 统一用 90px
            sizeCache.set(asset.id, size);
          } else {
            // 回退：加载图片获取尺寸
            const url = pickDurableUrl(asset.url, asset.localCacheUrl) || asset.url;
            try {
              const dims = await loadMediaDimensions(url);
              const size = calculateLongSideCustom(dims.width, dims.height, 90); // 🔑 统一用 90px
              sizeCache.set(asset.id, size);
            } catch (err) {
              console.warn('[@ 提及] 加载图片尺寸失败:', asset.title, err);
              sizeCache.set(asset.id, { width: 90, height: 120 }); // 默认竖图尺寸
            }
          }
        }
      })
    );

    const newNodeEntries = toCreate.map((asset) => {
      const isAudioCategory = ['music', 'sfx', 'voice'].includes(asset.category);
      const nodeType = ['image', 'video', 'audio'].includes(asset.type)
        ? asset.type
        : (isAudioCategory ? 'audio' : 'image');
      const nodeId = getMentionNodeId(asset.id);
      // 🔧 output 必须是持久 http 直链：拒绝 blob:/local-cache://，否则换设备/清缓存后永久空白（Bug A）
      const outputUrl = pickDurableUrl(asset.url, asset.localCacheUrl);
      // 🐛 调试：音频节点一直显示"加载中"
      // 🔧 上游引用节点尺寸：从预加载缓存读取真实尺寸
      const customSize = sizeCache.get(asset.id) || { width: 90, height: 120 };
      const pos = {
        x: sourceX,
        y: nextY,
      };
      nextY += customSize.height + 40;
      return {
        id: nodeId, type: 'aiNode', position: pos,
        data: buildNodeData(nodeId, nodeType, asset.title, {
          output: outputUrl,
          // outputs 数组必须非空，否则 CustomNode 会认为节点无内容而渲染 NebulaBackground 雾层
          outputs: outputUrl ? [outputUrl] : [],
          // 🔧 localCacheUrl 只放内存 blob（快显用）；绝不放 http/local-cache://（Bug B）
          localCacheUrl: pickBlobUrl(asset.localCacheUrl),
          // ⚠️ 不设 data.thumbUrl：素材库 thumbnail_url 是给抽屉方格用的「裁剪缩略图」
          //   （正方形/角色竖图），cardSrc 会拿它当画布卡片显示图 → 贴的是裁剪图。
          //   留空则 cardSrc 回退 ossThumb(原图,768)，等比缩略、显示真实上传图。
          customSize,
          _preserveSize: true,             // 🔑 阻止 ImageNodeView 在 onLoad 时重新计算尺寸
          description: asset.description,  // 🔑 保留描述词字段（节点左下角 T 角标需要）
          descEnabled: true,               // 🔑 默认启用描述词
        }),
      };
    });

    // ── 3. 计算新的 mention-refs（给目标节点的参考区） ──
    const mentionRefFiles = matchedAssets
      .filter(a => !['music', 'sfx', 'voice'].includes(a.category))
      .map(a => ({
        id: getMentionNodeId(a.id),
        url: pickDurableUrl(a.url, a.localCacheUrl),
        localCacheUrl: pickBlobUrl(a.localCacheUrl),
        thumbUrl: a.thumbnailUrl || undefined,  // 🖼️ 素材库现成缩略图：60px 参考框优先用它，避免拉全尺寸原图（Bug D）
        title: a.title, type: a.type || 'image',
        description: a.description,  // 🔑 保留描述词字段
        descEnabled: true,           // 🔑 默认启用描述词
      }));
    const mentionRefAudios = matchedAssets
      .filter(a => ['music', 'sfx', 'voice'].includes(a.category))
      .map(a => ({
        id: getMentionNodeId(a.id),
        url: pickDurableUrl(a.url, a.localCacheUrl),
        localCacheUrl: pickBlobUrl(a.localCacheUrl),
        title: a.title,
        description: a.description,  // 🔑 保留描述词字段
        descEnabled: true,           // 🔑 默认启用描述词
      }));

    // ── 4. 一次性更新节点列表(新增/删除节点 + 更新目标refFiles) ──
    setNodes(nds => {
      let result = nds.filter(n => !removeNodeIds.has(n.id));
      // 更新目标节点的 mention-refs
      result = result.map(n => {
        if (n.id !== targetNodeId) return n;

        // 🎬 如果有分镜大师上游，保留所有已存在的 refFiles（包括继承的），只追加新的
        // 否则，按原逻辑：清理 mention- 开头的，然后替换为新的
        let newRefFiles, newRefAudios;
        if (hasStoryboardUpstream) {
          // 保留所有已存在的，追加新提及的（去重）
          const existingRefIds = new Set((n.data.refFiles || []).map(f => String(f.id)));
          const existingAudioIds = new Set((n.data.refAudios || []).map(f => String(f.id)));
          newRefFiles = [
            ...(n.data.refFiles || []),
            ...mentionRefFiles.filter(f => !existingRefIds.has(String(f.id)))
          ];
          newRefAudios = [
            ...(n.data.refAudios || []),
            ...mentionRefAudios.filter(f => !existingAudioIds.has(String(f.id)))
          ];
        } else {
          // 原逻辑：清理 mention- 开头的，替换为新的
          const keepFiles = (n.data.refFiles || []).filter(f => !String(f.id).startsWith('mention-'));
          const keepAudios = (n.data.refAudios || []).filter(f => !String(f.id).startsWith('mention-'));
          newRefFiles = [...keepFiles, ...mentionRefFiles];
          newRefAudios = [...keepAudios, ...mentionRefAudios];
        }

        return { ...n, data: {
          ...n.data,
          refFiles: newRefFiles,
          refAudios: newRefAudios,
        }};
      });
      return [...result, ...newNodeEntries];
    });

    // ── 5. 删除被移除节点的边 ──
    if (removeNodeIds.size > 0) {
      setFlowEdges(eds => eds.filter(e => !removeNodeIds.has(e.source) && !removeNodeIds.has(e.target)));
    }

    // ── 6. 为新节点添加边 ──
    if (newNodeEntries.length > 0) {
      setTimeout(() => {
        setFlowEdges(eds => {
          let result = eds;
          newNodeEntries.forEach(node => {
            const color = C.TYPE_COLORS[node.data.type] || C.TYPE_COLORS.default;
            result = addEdge({
              source: node.id, target: targetNodeId, type: 'custom',
              data: { originalColor: color },
              style: { stroke: color, strokeWidth: C.EDGE_WIDTH_NORMAL, opacity: C.OPACITY_IDLE },
            }, result);
          });
          return result;
        });
      }, 100);
    }

    // ── 7. 异步加载图片真实尺寸并更新节点 ──
    newNodeEntries.forEach(nodeEntry => {
      if (nodeEntry.data.type === 'audio') return; // 音频节点跳过

      const img = new Image();
      img.onload = () => {
        const realSize = calculateLongSideCustom(img.naturalWidth, img.naturalHeight, 90);

        // 更新节点尺寸（保持 _preserveSize: true）
        setNodes(nds => nds.map(n => {
          if (n.id !== nodeEntry.id) return n;
          return {
            ...n,
            data: {
              ...n.data,
              customSize: realSize,
              _preserveSize: true, // 继续保持标记
            }
          };
        }));
      };
      img.onerror = () => {
        console.warn('[@ 提及尺寸] 图片加载失败:', nodeEntry.id);
      };
      img.src = nodeEntry.data.output;
    });

    // ⚡ 连线兜底：自动弹出的角色节点常只有 OSS 直链、无内存 blob，下游参考区 60px 缩略图
    //   会被迫现拉全尺寸原图、主图也要等网络首绘。为每个新建 mention 节点 warm 一次 blob，
    //   按 node.id 回填后自动传播到目标节点 refFiles（id 相同）。详见 warmRefBlob 注释。
    newNodeEntries.forEach(node => {
      const fileType = node.data.type === 'video' ? 'video'
                     : node.data.type === 'audio' ? 'audio' : 'image';
      warmRefBlob(node.id, node.data.output, fileType, node.data.localCacheUrl);
    });
  }, [getNodes, buildNodeData, setNodes, setFlowEdges, warmRefBlob]);

  const addNewNode = useCallback((type, pos, title) => {
    const id = `node-${Date.now()}`;
    const customSize = type === 'audio'
      ? { width: 160, height: 56 }
      : (type === 'text' ? { width: 240, height: 150 } : { width: 360, height: 360 });
    setNodes(nds => [...nds, { id, type: 'aiNode', position: pos, data: buildNodeData(id, type, title, { customSize }) }]);
    setContextMenu(null);
  }, [buildNodeData, setNodes]);

  const handlePaneContextMenu = useCallback((event) => {
    event.preventDefault();
    setContextMenu({
      type: 'pane', mouseX: event.clientX, mouseY: event.clientY,
      flowPos: screenToFlowPosition({ x: event.clientX, y: event.clientY })
    });
    setMenuData(null);
  }, [screenToFlowPosition]);

  // 📱 iPad 长按 = 右键菜单：触屏不会可靠地触发 contextmenu，这里用原生 touch 监听自己判长按。
  //   长按 ≥500ms 且手指没怎么动 → 命中节点就弹节点菜单、否则弹画布菜单；
  //   手指移动超过阈值 → 判定为平移/拖拽，取消长按。长按触发后在 touchend preventDefault，
  //   挡掉松手时的合成点击（否则会冒泡到 onPaneClick/onNodeClick 把菜单又关掉）。
  useEffect(() => {
    const el = flowAreaRef.current;
    if (!el) return;
    let timer = null, sx = 0, sy = 0, moved = false;
    const clear = () => { if (timer) { clearTimeout(timer); timer = null; } };

    const onStart = (e) => {
      suppressClickRef.current = false; // 新手势开始：清掉上次长按的抑制标记
      if (e.touches.length !== 1) { clear(); return; }
      // 输入框/连接圆圈上的长按不接管（圆圈要走拉线）
      // 输入框/连接圆圈/交互控件(nodrag：控制台、麦克风、按钮等)上的长按不接管 —— 让它们
      //   走各自的长按逻辑（如麦克风长按录音），只在节点图像区/画布空白处触发右键菜单。
      // 📱 只在画布(.react-flow)内部长按才弹右键菜单：抽屉/保存弹窗/裁剪面板等浮层虽是 flowArea
      //   的 DOM 后代（capture 监听会收到其 touch），但不属于画布，长按不该弹菜单。
      if (!e.target.closest('.react-flow')) return;
      if (e.target.closest('input, textarea, select, [contenteditable="true"], .react-flow__handle, .nodrag, .voice-mic-hit')) return;
      const t = e.touches[0];
      sx = t.clientX; sy = t.clientY; moved = false;
      clear();
      timer = setTimeout(() => {
        timer = null;
        suppressClickRef.current = true; // 长按成立：抑制本次手势松手后的合成点击
        const nodeEl = document.elementFromPoint(sx, sy)?.closest('.react-flow__node');
        const flowPos = screenToFlowPosition({ x: sx, y: sy });
        // 📱 菜单不能弹在手指正下方：否则抬手的合成点击落在菜单顶部按钮上、又被 suppress 吞掉，
        //   表现为"按钮点了没反应、要点两次"。这里把菜单偏移到手指右下方，并做视口边界钳制。
        const MENU_W = 180, MENU_H = 340, GAP = 22;
        let mx = sx + GAP, my = sy + GAP;
        if (mx + MENU_W > window.innerWidth) mx = sx - MENU_W - GAP;   // 右边放不下 → 翻到左侧
        if (mx < 8) mx = 8;
        if (my + MENU_H > window.innerHeight) my = window.innerHeight - MENU_H - 8; // 下边放不下 → 上顶
        if (my < 8) my = 8;
        if (nodeEl?.getAttribute('data-id')) {
          setContextMenu({ type: 'node', mouseX: mx, mouseY: my, targetNodeId: nodeEl.getAttribute('data-id'), flowPos });
        } else {
          setContextMenu({ type: 'pane', mouseX: mx, mouseY: my, flowPos });
        }
        setMenuData(null);
      }, 500);
    };
    const onMove = (e) => {
      if (moved || !e.touches.length) return;
      const t = e.touches[0];
      if (Math.abs(t.clientX - sx) > 12 || Math.abs(t.clientY - sy) > 12) { moved = true; clear(); }
    };
    const onEnd = (e) => {
      clear();
      // 长按成立后松手：挡掉合成点击（onNodeClick/onPaneClick 也会因 suppressClickRef 忽略关闭）
      if (suppressClickRef.current) e.preventDefault();
    };

    // ⚠️ capture:true —— 在 React Flow 内部 d3-zoom 的冒泡监听之前拿到 touch 事件，
    //   否则 touchstart 被它吞掉，长按计时器根本收不到（表现为"长按空白没反应"）。
    el.addEventListener('touchstart', onStart, { passive: true, capture: true });
    el.addEventListener('touchmove', onMove, { passive: true, capture: true });
    el.addEventListener('touchend', onEnd, { passive: false, capture: true });
    el.addEventListener('touchcancel', clear, { passive: true, capture: true });
    return () => {
      clear();
      el.removeEventListener('touchstart', onStart, { capture: true });
      el.removeEventListener('touchmove', onMove, { capture: true });
      el.removeEventListener('touchend', onEnd, { capture: true });
      el.removeEventListener('touchcancel', clear, { capture: true });
    };
  }, [screenToFlowPosition, setContextMenu, setMenuData]);

  const addNodeFromContextMenu = (type) => {
    if (!contextMenu) return;
    const id = `node-${Date.now()}`;
    const isText = type === 'text';
    const defaultSize = type === 'audio' ? { width: 160, height: 56 } : { width: 240, height: 150 };
    const customSize = (type === 'video' || type === 'image') && !isText ? { width: 240, height: 240 } : defaultSize;
    const title = isText ? '咒语' : (type === 'video' ? '视频模型' : '图像模型');
    const newNode = {
      id, type: 'aiNode', position: contextMenu.flowPos, selected: true,
      data: buildNodeData(id, type, title, { customSize })
    };
    setNodes(nds => [...nds.map(n => ({ ...n, selected: false })), newNode]);
    setContextMenu(null);
  };

  const handleAddFromSidebar = useCallback((title) => {
    const pos = getSmartSpawnPosition('left');
    const id = `node-${Date.now()}`;
    const isText = title === "咒语" || title === "文本标签";
    let customSize;
    if (isText) customSize = { width: 240, height: 150 };
    else if (title.includes('视频') || title.includes('图像')) customSize = { width: 240, height: 240 };
    const type = isText ? 'text' : (title.includes('视频') ? 'video' : 'image');
    const newNode = {
      id, type: 'aiNode', position: pos, selected: true,
      data: buildNodeData(id, type, title, { customSize })
    };
    setNodes(nds => [...nds.map(n => ({ ...n, selected: false })), newNode]);
  }, [getSmartSpawnPosition, buildNodeData, setNodes]);

  // 🎬 剧本大师「粘贴到新节点」：把选中的文本生成为画布上的文本节点
  const handleSpawnTextFromModelMaster = useCallback((text) => {
    const content = (text || '').trim();
    if (!content) return;
    const pos = getSmartSpawnPosition('left');
    const id = `node-${Date.now()}`;
    const newNode = {
      id, type: 'aiNode', position: pos, selected: true,
      data: buildNodeData(id, 'text', '剧本大师', {
        prompt: content,
        text: content,
        bgColor: getRandomTextBg(),
        customSize: { width: 240, height: 150 },
      }),
    };
    setNodes(nds => [...nds.map(n => ({ ...n, selected: false })), newNode]);
  }, [getSmartSpawnPosition, buildNodeData, setNodes]);


  // gridType: 4 / 9 / 16 / 25（用户选定，节点生命周期内固定）
  // 创建后宫格区会渲染对应数量的格子，比例联动模型参数 aspect_ratio
  // pos 可选：右键菜单传入鼠标位置；侧边栏不传则用智能落点
  // parseText 可选：从剧本大师「导入到分镜大师」时把该段大纲灌进右侧文本框（不自动铺格子）
  const handleAddStoryboard = useCallback((gridType = 9, pos, parseText = '') => {
    const spawnPos = pos || getSmartSpawnPosition('left');
    const id = `node-${Date.now()}`;
    const newNode = {
      id, type: 'aiNode', position: spawnPos, selected: true,
      data: buildNodeData(id, 'image', '分镜大师', {
        customSize: { width: 480, height: 480 },
        storyboardMode: true,
        defaultPinned: true,  // 📌 新建即默认钉住控制台
        gridType,
        gridCells: [],   // 用户编辑/解析时按需填充
        basePrompts: [],
        ...(parseText ? { parseText } : {}),   // 导入大纲：只灌文本，格子留空
      })
    };
    setNodes(nds => [...nds.map(n => ({ ...n, selected: false })), newNode]);
    setContextMenu(null);
  }, [getSmartSpawnPosition, buildNodeData, setNodes, setContextMenu]);

  // 🎬 剧本大师「导入到分镜大师」：新建分镜大师节点，灌入这段大纲，宫格档位按镜头数匹配好
  const handleImportOutlineToStoryboard = useCallback((text, gridType = 9) => {
    const content = (text || '').trim();
    if (!content) return;
    handleAddStoryboard(gridType, undefined, content);
  }, [handleAddStoryboard]);

  // ── 🔗 把源节点加到目标节点的 refFiles（多选框加号场景）──
  // 必须定义在 addNewNodeFromMenu 之前，因为后者的依赖数组会引用它
  const handleAddRefFilesFromNodes = useCallback((targetNodeId, sourceNodeIds) => {
    setNodes(nds => nds.map(node => {
      if (node.id !== targetNodeId) return node;

      const allNodes = nds;
      const sourceNodes = allNodes.filter(n => sourceNodeIds.includes(n.id));
      const newRefFiles = [];

      sourceNodes.forEach(srcNode => {
        // 从源节点的 output 或 url 构建参考文件对象
        if ((srcNode.data.type === 'image' || srcNode.data.type === 'video') && srcNode.data.output) {
          newRefFiles.push({
            id: srcNode.id,
            type: srcNode.data.type,
            url: srcNode.data.output,
            localCacheUrl: srcNode.data.localCacheUrl,
            title: srcNode.data.title || `${srcNode.data.type === 'video' ? '视频' : '图片'}-${srcNode.id}`
          });
        }
      });

      return {
        ...node,
        data: {
          ...node.data,
          refFiles: newRefFiles
        }
      };
    }));
  }, [setNodes]);

  const addNewNodeFromMenu = useCallback((sourceIds, sourcePos, title, dropFlowPos, isMultiSelect = false) => {
    const id = `node-${Date.now()}`;
    const isText = title === "咒语" || title === "文本标签";
    let customSize;
    if (isText) customSize = { width: 240, height: 150 };
    else if (title.includes('视频') || title.includes('图像')) customSize = { width: 360, height: 360 };
    const type = isText ? 'text' : (title.includes('视频') ? 'video' : 'image');

    // 📍 位置：优先落在拉线松手处(dropFlowPos)——新节点就在菜单位置生成，不回弹到源节点水平线。
    //   落点作为新节点左边缘、垂直居中对齐落点，观感自然。
    //   缺 dropFlowPos（老路径兜底）时，退回源节点右侧 +300、顶对齐。
    let position;
    if (dropFlowPos) {
      const h = customSize?.height || 240;
      position = { x: dropFlowPos.x, y: dropFlowPos.y - h / 2 };
    } else {
      // 兼容老调用（单源时 sourceIds 是单个 ID）
      const srcId = Array.isArray(sourceIds) ? sourceIds[0] : sourceIds;
      const sourceNode = getNodes().find(n => n.id === srcId);
      const sourceWidth = sourceNode?.data?.customSize?.width || 320;
      position = { x: sourcePos.x + sourceWidth + 300, y: sourcePos.y };
    }

    const newNode = {
      id, type: 'aiNode',
      position,
      selected: true,
      data: buildNodeData(id, type, title, {
        customSize,
        defaultPinned: true,            // 📌 控制台默认钉住（打开状态）
        openConsoleOnConnect: true      // 🎬 自动打开控制台
      })
    };

    // 将新节点添加到数组末尾，确保它在上层（React Flow 渲染顺序决定层级）
    setNodes(nds => [...nds.map(n => ({ ...n, selected: false })), newNode]);
    setMenuData(null);

    if (isMultiSelect && Array.isArray(sourceIds) && sourceIds.length > 0) {
      // 🔑 新方案：使用 onConnect 逐个创建连线，完全模仿单选逻辑
      setTimeout(() => {
        // 逐个创建连线（onConnect 会自动添加 refFiles）
        sourceIds.forEach(srcId => {
          onConnect({ source: srcId, target: id, sourceHandle: null, targetHandle: null });
        });

        // 🔑 关键：在所有 onConnect 执行后，强制重新选中新节点
        //   因为最后一次 onConnect 会设置新节点 selected: true，但可能被其他事件清空
        setTimeout(() => {
          console.log('[addNewNodeFromMenu] 强制重新选中新节点:', id);
          setNodes(nds => nds.map(n => ({ ...n, selected: n.id === id })));
        }, 10);
      }, 100);
    } else {
      // 单源模式（旧流程）：创建连接线
      const srcId = Array.isArray(sourceIds) ? sourceIds[0] : sourceIds;
      setTimeout(() =>
        onConnect({ source: srcId, target: id, sourceHandle: null, targetHandle: null }), 100
      );
    }
  }, [buildNodeData, setNodes, setMenuData, onConnect, getNodes]);

  // ── 🎨 应用预设：从预设工具箱点选预设后，在源图右侧弹出图片生成节点 ──
  const handleApplyPreset = useCallback((sourceId, presetId, selectedRefImages = []) => {
    // 从预设工具箱（presetsRef）中查找预设：toolsets → groups → presets
    const hit = findSkillboxPreset(presetsRef.current, presetId);
    if (!hit) {
      console.warn('[handleApplyPreset] preset not found:', presetId);
      setAlertMsg({ type: 'warning', text: '预设不存在或已下线，请刷新后重试' });
      return;
    }
    const preset = hit.preset;
    const sourceNode = getNodes().find(n => n.id === sourceId);
    if (!sourceNode) return;
    const id = `node-${Date.now()}`;
    const sourceWidth = sourceNode.data?.customSize?.width || 320;
    const offsetX = sourceWidth + 300;

    // 提取参考图ID（取第一个，节点只支持单个参考图）
    const refImageId = selectedRefImages.length > 0 ? selectedRefImages[0].id : null;

    // 创建新节点，只保存 presetId，不填充 prompt 和 description（生成时实时从 presetsRef 读取）
    const newNode = {
      id, type: 'aiNode',
      position: { x: sourceNode.position.x + offsetX, y: sourceNode.position.y },
      selected: true,
      data: buildNodeData(id, 'image', '图像模型', {
        customSize: { width: 360, height: 360 },
        presetId: presetId,                     // 保存预设ID（生成时实时读取提示词和描述词）
        presetModelIds: preset.modelIds || [],  // 可用模型白名单（空数组 = 不限制）
        // 不填充 prompt 和 description —— 预设的提示词和描述词在生成时后台拼接，用户看不见
      }),
    };
    setNodes(nds => [...nds.map(n => ({ ...n, selected: false })), newNode]);
    // 自动连线：把源图作为参考图
    setTimeout(() => onConnect({ source: sourceId, target: id, sourceHandle: null, targetHandle: null }), 100);
  }, [buildNodeData, setNodes, onConnect, getNodes, setAlertMsg]);

  // ── ✨ 应用技能：从技能工具箱点选技能后，在源图右侧弹出图片生成节点，预选技能 ──
  // 与预设工具箱逻辑一致：只存 skillboxPresetId，生成时实时读取技能的 prompt
  const handleApplySkillbox = useCallback((sourceId, presetId, selectedRefImages = []) => {
    // 从三层结构中查找技能：toolsets → groups → presets
    const hit = findSkillboxPreset(skillboxRef.current, presetId);
    if (!hit) {
      console.warn('[handleApplySkillbox] preset not found:', presetId);
      setAlertMsg({ type: 'warning', text: '技能不存在或已下线，请刷新后重试' });
      return;
    }
    const preset = hit.preset;
    const sourceNode = getNodes().find(n => n.id === sourceId);
    if (!sourceNode) return;
    const id = `node-${Date.now()}`;
    const sourceWidth = sourceNode.data?.customSize?.width || 320;
    const offsetX = sourceWidth + 300;

    // 提取参考图ID（取第一个，节点只支持单个参考图）
    const refImageId = selectedRefImages.length > 0 ? selectedRefImages[0].id : null;

    // 创建新节点，保存技能ID和参考图ID
    const newNode = {
      id, type: 'aiNode',
      position: { x: sourceNode.position.x + offsetX, y: sourceNode.position.y },
      selected: true,
      data: buildNodeData(id, 'image', '图像模型', {
        customSize: { width: 360, height: 360 },
        skillboxPresetId: presetId,         // 技能ID
        skillboxRefImageId: refImageId,     // 参考图ID（单个）
      }),
    };
    setNodes(nds => [...nds.map(n => ({ ...n, selected: false })), newNode]);
    // 自动连线：把源图作为参考图
    setTimeout(() => onConnect({ source: sourceId, target: id, sourceHandle: null, targetHandle: null }), 100);
  }, [buildNodeData, setNodes, onConnect, getNodes, setAlertMsg]);

  const processBatchUpload = useCallback((files, position) => {
    if (!files || files.length === 0) return;
    const newNodes = [];
    const filesToUpload = [];
    const oversizedFiles = [];
    Array.from(files).forEach((file, index) => {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      const isAudio = file.type.startsWith('audio/');
      if (!isImage && !isVideo && !isAudio) return;
      // 文件大小检查（视频/音频）
      if ((isVideo || isAudio) && file.size > C.MAX_UPLOAD_SIZE) {
        oversizedFiles.push(file.name);
        return;
      }
      const id = `node-${Date.now()}-${index}`;
      const blobUrl = createSecureBlob(file);
      let customSize;
      if (isImage) customSize = { width: 240, height: 240 };
      if (isVideo) customSize = { width: 240, height: 240 };
      if (isAudio) customSize = { width: 160, height: 56 };
      if (isImage) {
        const img = new Image();
        img.onload = () => {
          const newSize = calculateLongSide120(img.width, img.height);
          console.log(`[图片拖入] ${file.name} - img.onload 触发`, {
            原始尺寸: `${img.width}×${img.height}`,
            计算后节点尺寸: `${newSize.width}×${newSize.height}`,
            节点ID: id
          });
          handleUpdateNode(id, { customSize: newSize });
          img.src = '';
        };
        img.src = blobUrl;
      }
      if (isVideo) {
        const vid = document.createElement('video');
        vid.preload = 'metadata';
        vid.onloadedmetadata = () => { handleUpdateNode(id, { customSize: calculateLongSide120(vid.videoWidth, vid.videoHeight) }); vid.src = ''; vid.load(); };
        vid.src = blobUrl;
      }
      const colCount = 4;
      const row = Math.floor(index / colCount);
      const col = index % colCount;
      // 间距：图片/视频节点 240px + 40px 间隙 = 280px，音频节点用 180px
      const horizontalSpacing = isAudio ? 180 : 280;
      const verticalSpacing = isAudio ? 80 : 280;
      const nodeType = isImage ? 'image' : (isVideo ? 'video' : 'audio');
      const newNode = {
        id, type: 'aiNode',
        position: { x: position.x + col * horizontalSpacing, y: position.y + row * verticalSpacing },
        selected: index === 0,
        data: buildNodeData(id, nodeType, file.name, {
          output: blobUrl, outputs: [blobUrl], isDirectUpload: true, customSize,
        })
      };
      newNodes.push(newNode);
      filesToUpload.push({ id, file, blobUrl });
    });
    // 提示超出大小限制的文件
    if (oversizedFiles.length > 0) {
      const names = oversizedFiles.length <= 3 ? oversizedFiles.join('、') : `${oversizedFiles.slice(0, 3).join('、')} 等 ${oversizedFiles.length} 个文件`;
      setAlertMsg({ text: `${names} 超过 ${C.MAX_UPLOAD_SIZE_LABEL} 限制，已跳过`, type: 'error' });
    }
    if (newNodes.length === 0) return;
    setNodes(nds => [...nds.map(n => ({ ...n, selected: false })), ...newNodes]);
    filesToUpload.forEach(({ id, file, blobUrl }) => uploadAndReplace(id, file, blobUrl));
  }, [createSecureBlob, handleUpdateNode, buildNodeData, setNodes, uploadAndReplace, setAlertMsg]);

  const handleDirectUpload = (e) => {
    // 落点同样避开左侧拼屏面板：无缓存位置时按面板偏移落在可见画布区
    const leftOffset = modelMasterLayout === 'left' ? modelMasterSize + 5 : 0;
    const pos = lastUploadPosRef.current || screenToFlowPosition({ x: leftOffset + 260, y: window.innerHeight / 2 - 150 });
    processBatchUpload(e.target.files, pos);
    lastUploadPosRef.current = null;
    e.target.value = '';
  };

  // ━━━ 键盘快捷键 & 拖放 ━━━
  const { onDragOver, onDrop } = useKeyboardShortcuts({
    saveProject, undo, redo, setNodes, setAlertMsg, clipboardRef,
    handleDeleteSelectedNodes,
    handleUpdateNode, handleDeleteNodeButton, handleGenerate, handleDetachImage,
    handleReuploadLogic, handleReplace, handleRemoveRefFile, handleRemoveRefText, handleRemoveRefAudio,
    addNewNode, processBatchUpload, buildNodeData,
  });

  // ━━━ 全局语音快捷键（按住录音，松开停止） ━━━
  useVoiceShortcut();

  // ━━━ 剧本大师分屏：拖动 divider 调整面板大小 ━━━
  // 左侧拼屏拖宽度，底部拼屏拖高度；松手时持久化到 localStorage。
  // 同时支持鼠标与触摸（iPad）：取坐标时兼容 touches/changedTouches。
  const handleModelMasterDragStart = useCallback((e) => {
    if (e.cancelable) e.preventDefault();
    const isTouch = e.type === 'touchstart';
    const getPoint = (ev) => ev.touches?.[0] || ev.changedTouches?.[0] || ev;
    const isLeft = modelMasterLayout === 'left';
    const p0 = getPoint(e);
    const startPos = isLeft ? p0.clientX : p0.clientY;
    const startSize = modelMasterSize;
    let latest = startSize;
    const onMove = (ev) => {
      const p = getPoint(ev);
      // 左侧：向右拖变宽；底部：向上拖变高（故用 start - cur）
      const delta = isLeft ? (p.clientX - startPos) : (startPos - p.clientY);
      const min = isLeft ? 360 : 280;
      const max = isLeft ? window.innerWidth - 240 : window.innerHeight - 160;
      latest = Math.max(min, Math.min(max, startSize + delta));
      setModelMasterSize(latest);
      if (ev.cancelable) ev.preventDefault(); // 触摸拖动时阻止页面滚动
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onUp);
      document.removeEventListener('touchcancel', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      localStorage.setItem('modelMasterSize', String(Math.round(latest)));
    };
    document.body.style.cursor = isLeft ? 'col-resize' : 'row-resize';
    document.body.style.userSelect = 'none';
    if (isTouch) {
      document.addEventListener('touchmove', onMove, { passive: false });
      document.addEventListener('touchend', onUp);
      document.addEventListener('touchcancel', onUp);
    } else {
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    }
  }, [modelMasterLayout, modelMasterSize]);

  // 📱 分隔条 + 把手 原生 touchstart（React 的 onTouchStart 可能被注册为 passive、无法 preventDefault）。
  //   非 passive 挂载，触屏按住分隔条或把手即可拖动调分屏大小。
  useEffect(() => {
    const els = [dividerRef.current, gripRef.current].filter(Boolean);
    if (!els.length) return;
    const start = (e) => handleModelMasterDragStart(e);
    els.forEach((el) => el.addEventListener('touchstart', start, { passive: false }));
    return () => els.forEach((el) => el.removeEventListener('touchstart', start));
  }, [handleModelMasterDragStart, modelMasterLayout]);

  // ━━━ 渲染 ━━━
  return (
    <div
      style={{
        // 📱 高度用 --app-vh(JS 实测 visualViewport.height，键盘进出即时跟随)，CSS 变量自带
        //   fallback 100dvh 应对 JS 首帧未设时。这是 iPad Chrome 键盘弹出视口不收缩(内容溢出
        //   可滚、拖 logo 窜位、收起留间隙)的根治。App.js 的 useViewportHeightVar 负责写变量。
        width: '100%', height: 'var(--app-vh, 100dvh)', overflow: 'hidden', backgroundColor: 'var(--bg-canvas)',
        display: 'flex',
        flexDirection: modelMasterLayout === 'bottom' ? 'column' : 'row',
      }}
    >
      {/* ── 画布主区（所有原有界面都在这里，代码结构不变，只是被放进 flex 子项） ── */}
      <div
        ref={flowAreaRef}
        style={{
          order: modelMasterLayout === 'left' ? 2 : 0,
          flex: 1, minWidth: 0, minHeight: 0, position: 'relative', overflow: 'hidden',
        }}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onContextMenu={(e) => e.preventDefault()}
        // 📱 只在画布(.react-flow)内部阻止默认行为，别波及抽屉/弹窗/右键菜单等浮层 —— 否则
        //   浮层里的按钮 touchstart 冒泡到这里被 preventDefault，会吞掉合成 click 导致点了没反应。
        onMouseDown={(e) => { if (e.target.closest('.react-flow') && !e.target.closest('input, textarea, select, [contenteditable="true"]')) e.preventDefault(); }}
        onTouchStart={(e) => { if (e.target.closest('.react-flow') && !e.target.closest('input, textarea, select, [contenteditable="true"]')) e.preventDefault(); }}
      >
      {/* ── 左侧工具栏 ── */}
      <div style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', display: 'flex', flexDirection: 'column', gap: '12px', zIndex: 10000, alignItems: 'center' }}>
        <div style={{ ...C.sidebarStyle, position: 'relative', left: 0, top: 0, transform: 'none' }}>
          <div style={C.sectionStyle}>
            <QuickTooltip content="上传素材" position="right" delay={300}>
              <button onClick={() => fileInputRef.current.click()} style={C.sidebarBtnStyle} className="sidebar-btn"><Upload size={20}/></button>
            </QuickTooltip>
            <QuickTooltip content="素材库" position="right" delay={300}>
              <button onClick={() => setDrawerOpen(true)} style={{...C.sidebarBtnStyle, color: isDrawerOpen ? '#3b82f6' : '#999'}} className="sidebar-btn"><Database size={20}/></button>
            </QuickTooltip>
          </div>
          <div style={{ height: '1px', background: 'var(--sidebar-divider)', margin: '4px 0' }} />
          <div style={C.sectionStyle}>
            <QuickTooltip content="新建咒语/文本" position="right" delay={300}>
              <button onClick={() => handleAddFromSidebar("咒语")} style={C.sidebarBtnStyle} className="sidebar-btn"><Sparkles size={20}/></button>
            </QuickTooltip>
            {["图像模型", "视频模型"].map(t => (
              <QuickTooltip key={t} content={t.replace('模型', '生成')} position="right" delay={300}>
                <button onClick={() => handleAddFromSidebar(t)} style={C.sidebarBtnStyle} className="sidebar-btn">
                  {t.includes('视频') ? <MonitorPlay size={20}/> : <ImageIcon size={20}/>}
                </button>
              </QuickTooltip>
            ))}
          </div>

          {/* ── 特殊技能：分镜大师 + 剧本大师 同组（hover 展开两排选择条） ── */}
          <div style={{ height: '1px', background: 'var(--sidebar-divider)', margin: '4px 0' }} />
          <div style={C.sectionStyle}>
            {/* 不用 QuickTooltip：hover 直接展开带「分镜大师」标题的宫格选择条，
                避免气泡(延迟300ms)与宫格(即时)分属两套状态产生竞态/闪烁 */}
            <div
              style={{ position: 'relative' }}
              onMouseEnter={() => setStoryboardPickerOpen(true)}
              onMouseLeave={() => setStoryboardPickerOpen(false)}
            >
              <button style={{ ...C.sidebarBtnStyle, color: hasMemberFeature('storyboard_master') ? '#94a3b8' : '#475569', opacity: hasMemberFeature('storyboard_master') ? 1 : 0.6 }} className="sidebar-btn">
                <LayoutGrid size={20}/>
              </button>

              {/* 横向展开的宫格选择条；外层 left:100% + paddingLeft 充当透明 hover 桥，
                  鼠标从按钮移到宫格不会跨越空隙触发 onMouseLeave */}
              {storyboardPickerOpen && (
                <div style={{
                  position: 'absolute', left: '100%', top: '50%', transform: 'translateY(-50%)',
                  paddingLeft: 10, zIndex: 10001,
                }}>
                  <div style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6, padding: '8px 10px',
                    background: 'rgba(20, 20, 24, 0.95)', borderRadius: 8,
                    border: '1px solid rgba(255,255,255,0.1)',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)',
                    whiteSpace: 'nowrap',
                  }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8' }}>分镜大师{hasMemberFeature('storyboard_master') ? '' : '（不可用）'}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {[4, 9, 16, 25].map(n => {
                        const allowed = hasMemberFeature('storyboard_master');
                        return (
                        <button
                          key={n}
                          onClick={() => {
                            if (!allowed) { setAlertMsg({ type: 'warning', text: '没有权限操作，当前会员等级未开通「分镜大师」，请联系管理员开通' }); setStoryboardPickerOpen(false); return; }
                            handleAddStoryboard(n); setStoryboardPickerOpen(false);
                          }}
                          style={{
                            padding: '4px 10px', fontSize: 12, fontWeight: 600,
                            background: 'rgba(255,255,255,0.05)', color: allowed ? '#cbd5e1' : '#64748b',
                            border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6,
                            cursor: allowed ? 'pointer' : 'not-allowed', transition: 'all 0.15s',
                          }}
                          onMouseEnter={e => { if (!allowed) return; e.currentTarget.style.background = '#3b82f6'; e.currentTarget.style.color = '#fff'; }}
                          onMouseLeave={e => { if (!allowed) return; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = '#cbd5e1'; }}
                        >
                          {n}宫格
                        </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ── 🎬 建模大师（与分镜大师同组，hover 展开两排拼屏方式选择） ── */}
            <div
              style={{ position: 'relative' }}
              onMouseEnter={() => setModelMasterMenuOpen(true)}
              onMouseLeave={() => setModelMasterMenuOpen(false)}
            >
              <button
                style={{ ...C.sidebarBtnStyle, color: modelMasterLayout ? '#fbbf24' : (hasMemberFeature('model_master') ? '#94a3b8' : '#475569'), opacity: hasMemberFeature('model_master') ? 1 : 0.6 }}
                className="sidebar-btn"
              >
                <Box size={20}/>
              </button>

              {/* 两排展开：第一排标题「建模大师」，第二排横屏 / 竖屏；外层 paddingLeft 充当 hover 桥 */}
              {modelMasterMenuOpen && (
                <div style={{
                  position: 'absolute', left: '100%', top: '50%', transform: 'translateY(-50%)',
                  paddingLeft: 10, zIndex: 10001,
                }}>
                  <div style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6, padding: '8px 10px',
                    background: 'var(--bg-panel)', borderRadius: 8,
                    border: '1px solid var(--border-subtle)',
                    boxShadow: 'var(--shadow-panel)', backdropFilter: 'blur(8px)',
                    whiteSpace: 'nowrap',
                  }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>建模大师{hasMemberFeature('model_master') ? '' : '（不可用）'}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {[
                        { key: 'left', label: '横屏', icon: <PanelLeft size={16} /> },
                        { key: 'bottom', label: '竖屏', icon: <PanelBottom size={16} /> },
                      ].map(opt => {
                        const allowed = hasMemberFeature('model_master');
                        return (
                        <button
                          key={opt.key}
                          onClick={() => {
                            if (!allowed) { setAlertMsg({ type: 'warning', text: '没有权限操作，当前会员等级未开通「建模大师」，请联系管理员开通' }); setModelMasterMenuOpen(false); return; }
                            setModelMasterLayout(opt.key); setModelMasterMenuOpen(false);
                          }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 4,
                            padding: '4px 10px', fontSize: 12, fontWeight: 600,
                            background: modelMasterLayout === opt.key ? '#3b82f6' : 'var(--bg-hover)',
                            color: modelMasterLayout === opt.key ? '#fff' : (allowed ? 'var(--text-body)' : 'var(--text-muted)'),
                            border: '1px solid var(--border-subtle)', borderRadius: 6,
                            cursor: allowed ? 'pointer' : 'not-allowed', transition: 'all 0.15s',
                          }}
                          onMouseEnter={e => { if (!allowed) return; e.currentTarget.style.background = '#3b82f6'; e.currentTarget.style.color = '#fff'; }}
                          onMouseLeave={e => {
                            if (!allowed) return;
                            const active = modelMasterLayout === opt.key;
                            e.currentTarget.style.background = active ? '#3b82f6' : 'var(--bg-hover)';
                            e.currentTarget.style.color = active ? '#fff' : 'var(--text-body)';
                          }}
                        >
                          {opt.icon}{opt.label}
                        </button>
                        );
                      })}
                      {modelMasterLayout && (
                        <button
                          onClick={() => { setModelMasterLayout(null); setModelMasterMenuOpen(false); }}
                          style={{
                            padding: '4px 10px', fontSize: 12, fontWeight: 600,
                            background: 'rgba(239,68,68,0.15)', color: '#fca5a5',
                            border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, cursor: 'pointer',
                          }}
                        >
                          关闭
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 用户头像 */}
        <QuickTooltip content={user?.username || '用户'} position="right" delay={300}>
          <div onClick={handleNavigateHome} className="sidebar-user-avatar sidebar-btn"
            style={{ width: '40px', height: '40px', borderRadius: '50%', background: C.TOOLBAR_BG, border: '2px solid #fbbf24', boxShadow: '0 0 10px rgba(251, 191, 36, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', overflow: 'hidden', transition: 'all 0.2s' }}>
            {user?.avatar ? (
              <img src={user.avatar} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt=""
                onError={(e) => { e.target.style.display = 'none'; const svg = e.target.parentNode?.querySelector('svg'); if (svg) svg.style.display = 'flex'; }} />
            ) : null}
            <User size={20} color="#94a3b8" style={{ display: user?.avatar ? 'none' : 'flex' }} />
          </div>
        </QuickTooltip>

        {/* 积分余额 */}
        <QuickTooltip content="查看积分明细" position="right" delay={300}>
          <div onClick={() => { refreshUser(); setIsCreditLogsOpen(true); }}
            style={{ background: C.TOOLBAR_BG, padding: '6px 10px', borderRadius: '20px', border: '1px solid var(--border-subtle)', boxShadow: '0 4px 12px rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'var(--text-primary)', fontSize: '12px', fontWeight: '600', transition: 'all 0.2s' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
            onMouseLeave={e => e.currentTarget.style.background = C.TOOLBAR_BG}>
            <Sparkle size={12} fill="#fbbf24" strokeWidth={0} />
            <span>{Math.round(user?.credits || 0)}</span>
          </div>
        </QuickTooltip>

        {/* 🎖️ 会员等级（积分下方、剩余天数上方） */}
        {(() => {
          const MEMBERSHIP_META = {
            education: { label: '教育版', color: '#34d399', bg: 'rgba(52,211,153,0.12)', border: 'rgba(52,211,153,0.3)' },
            commercial: { label: '商用版', color: '#a78bfa', bg: 'rgba(167,139,250,0.15)', border: 'rgba(167,139,250,0.35)' },
            vip: { label: 'VIP版', color: '#f59e0b', bg: 'rgba(245,158,11,0.15)', border: 'rgba(245,158,11,0.35)' },
          };
          const meta = MEMBERSHIP_META[membershipLevel] || MEMBERSHIP_META.education;
          return (
            <div style={{
              background: meta.bg, padding: '3px 10px', borderRadius: '20px',
              border: `1px solid ${meta.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: meta.color, fontSize: '11px', fontWeight: '600',
            }}>
              {meta.label}
            </div>
          );
        })()}

        {/* 🏷️ 有效期剩余天数（超级管理员在用户管理中开启后全局可见） */}
        {showExpiryBadge && user?.role !== 'super_admin' && (() => {
          if (!user?.expires_at) return null;
          const now = new Date();
          const exp = new Date(user.expires_at);
          const diffDays = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
          let color, label;
          if (diffDays <= 0) { color = '#71717a'; label = '已过期'; }
          else if (diffDays <= 7) { color = '#ef4444'; label = `${diffDays}天`; }
          else if (diffDays <= 30) { color = '#f59e0b'; label = `${diffDays}天`; }
          else { color = '#22c55e'; label = `${diffDays}天`; }
          return (
            <QuickTooltip content={`账号有效期: ${diffDays <= 0 ? '已过期' : `剩余 ${diffDays} 天`}`} position="right" delay={300}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '3px', color, fontSize: '10px', fontWeight: '600', marginTop: '-2px' }}>
                <Clock size={10} />
                <span>{label}</span>
              </div>
            </QuickTooltip>
          );
        })()}

        <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleDirectUpload} accept="image/*,video/*,audio/*" multiple />
        <input type="file" ref={replaceInputRef} style={{ display: 'none' }} onChange={handleReplaceFileChange} />
      </div>

      {/* 右角标 */}
      {branding.logo && (
        <div style={{ position: 'absolute', bottom: 100, left: 20, zIndex: 999, userSelect: 'none', pointerEvents: 'none' }}>
          <img src={branding.logo} alt=""
            onError={(e) => { e.target.style.display = 'none'; }}
            style={{
              width: 'auto', height: '100px', objectFit: 'contain', display: 'block',
              opacity: branding.opacity || 0.3,
              filter: branding.color === '#ffffff' ? 'brightness(0) invert(1)'
                : branding.color === '#9ca3af' ? 'brightness(0) invert(0.6)'
                : branding.color === '#000000' ? 'brightness(0) invert(0)'
                : branding.color === '#ef4444' ? 'brightness(0) saturate(100%) invert(38%) sepia(94%) saturate(3498%) hue-rotate(345deg) brightness(96%) contrast(92%)'
                : branding.color === '#fbbf24' ? 'brightness(0) saturate(100%) invert(79%) sepia(61%) saturate(555%) hue-rotate(359deg) brightness(102%) contrast(97%)'
                : branding.color === '#3b82f6' ? 'brightness(0) saturate(100%) invert(46%) sepia(98%) saturate(1677%) hue-rotate(207deg) brightness(100%) contrast(93%)'
                : branding.color === '#10b981' ? 'brightness(0) saturate(100%) invert(66%) sepia(38%) saturate(3260%) hue-rotate(118deg) brightness(91%) contrast(86%)'
                : 'brightness(0) invert(1)',
              transition: 'all 0.3s ease'
            }} />
        </div>
      )}

      {/* 底部标题栏 */}
      <div style={{ position: 'absolute', bottom: 70, left: 20, zIndex: 1000, color: theme === 'light' ? '#3f3f46' : '#fff', fontSize: 13, fontWeight: '500', display: 'flex', alignItems: 'center', gap: 8 }}>
        <IconFilm size={14} color="var(--amber-accent)" />
        <span style={{ color: 'var(--amber-accent)' }}>{crewName}</span>
        <span style={{ opacity: 0.3, fontSize: 10, margin: '0 2px' }}>/</span>
        {isEditingName ? (
          <input ref={nameInputRef} value={tempName} onChange={e => setTempName(e.target.value)} onBlur={handleSaveName} onKeyDown={e => e.key === 'Enter' && handleSaveName()}
            style={{ background: 'transparent', border: 'none', borderBottom: '1px solid #3b82f6', color: theme === 'light' ? '#27272a' : '#fff', fontSize: 13, outline: 'none', width: 200, padding: 0 }} />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span onDoubleClick={() => setIsEditingName(true)} title="双击重命名图板" style={{ cursor: 'pointer', borderBottom: '1px dashed rgba(255,255,255,0.2)' }}>
              {projectName || '未命名项目'}
            </span>
            <Edit2 size={10} style={{ color: '#666', cursor: 'pointer', transition: 'color 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.color = '#3b82f6'} onMouseLeave={e => e.currentTarget.style.color = '#666'} onClick={() => setIsEditingName(true)} />
          </div>
        )}

        {/* 项目组聊天入口：红色圈=公共聊天室，成员头像=一对一私聊
            点击后设置 chatTargetId 并打开 ChatWindow */}
        {crewMembers.length > 1 && (
          <>
            <span style={{ opacity: 0.3, fontSize: 10, margin: '0 4px' }}>|</span>
            <div style={{ display: 'flex', alignItems: 'center', marginLeft: 2 }}>
              {/* 公共聊天室红色圆形图标 */}
              <QuickTooltip content="公共聊天室" position="top" delay={300}>
                <div
                  onClick={() => { setChatTargetId(null); setIsChatOpen(true); markRead(null); }}
                  style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: 'rgba(239, 68, 68, 0.85)',
                    border: '2px solid #b91c1c',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', transition: 'all 0.15s', flexShrink: 0,
                    zIndex: crewMembers.length + 1, position: 'relative',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                >
                  <MessageCircle size={14} color="#fff" />
                  {/* 公共聊天未读角标 */}
                  {unreadCounts['public'] > 0 && (
                    <span style={{
                      position: 'absolute', top: -5, right: -5,
                      background: '#f97316', color: '#fff',
                      borderRadius: 9, minWidth: 16, height: 16,
                      fontSize: 10, fontWeight: 700, lineHeight: '16px',
                      textAlign: 'center', padding: '0 3px',
                      pointerEvents: 'none',
                    }}>
                      {unreadCounts['public'] > 99 ? '99+' : unreadCounts['public']}
                    </span>
                  )}
                </div>
              </QuickTooltip>
              {/* 成员头像列表 */}
              {[...crewMembers].sort((a, b) => (a.role === 'producer' ? -1 : b.role === 'producer' ? 1 : 0)).map((member, idx) => {
                const isProducer = member.role === 'producer';
                const isSelf = member.user_id === user?.id;
                const borderColor = isProducer ? '#fbbf24' : '#3f3f46';
                const roleLabel = isProducer ? '项目负责人' : (member.role === 'director' ? '设计师' : '');
                const tooltipText = isSelf
                  ? `${member.username}（我）`
                  : `私聊 ${member.username}${roleLabel ? `（${roleLabel}）` : ''}`;
                return (
                  <QuickTooltip key={`crew-${member.user_id}-${idx}`} content={tooltipText} position="top" delay={300}>
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isSelf) {
                          setChatTargetId(member.user_id);
                          setIsChatOpen(true);
                          markRead(member.user_id);
                        }
                      }}
                      style={{
                        marginLeft: -8, zIndex: crewMembers.length - idx, position: 'relative',
                        cursor: isSelf ? 'default' : 'pointer', transition: 'transform 0.15s',
                      }}
                      onMouseEnter={e => { if (!isSelf) e.currentTarget.style.transform = 'scale(1.1)'; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                    >
                      <UserAvatar
                        avatar={member.avatar}
                        username={member.username}
                        size={28}
                        fontSize={12}
                        border={`2px solid ${borderColor}`}
                      />
                      {/* 私聊未读角标 */}
                      {!isSelf && unreadCounts[String(member.user_id)] > 0 && (
                        <span style={{
                          position: 'absolute', top: -4, right: -4,
                          background: '#f97316', color: '#fff',
                          borderRadius: 9, minWidth: 16, height: 16,
                          fontSize: 10, fontWeight: 700, lineHeight: '16px',
                          textAlign: 'center', padding: '0 3px',
                          pointerEvents: 'none', zIndex: 10,
                        }}>
                          {unreadCounts[String(member.user_id)] > 99 ? '99+' : unreadCounts[String(member.user_id)]}
                        </span>
                      )}
                    </div>
                  </QuickTooltip>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* 本地缓存横幅（独立组件，不触发主画布重渲染） */}
      <CacheBanner
        cacheInvalid={cacheInvalid} setCacheInvalid={setCacheInvalid}
        cacheReconfiguring={cacheReconfiguring} setCacheReconfiguring={setCacheReconfiguring}
        showCacheConfirm={showCacheConfirm} setShowCacheConfirm={setShowCacheConfirm}
        setAlertMsg={setAlertMsg} nodesRef={nodesRef} globalAssetsRef={globalAssetsRef}
        crewIdRef={crewIdRef} crewNameRef={crewNameRef}
      />

      {isEditorReadOnly && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 10000,
          background: 'rgba(220, 53, 69, 0.95)', color: '#fff',
          padding: '8px 16px', display: 'flex', alignItems: 'center',
          justifyContent: 'center', gap: 16, fontSize: 13,
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
        }}>
          <span>⚠️ 另一标签页/窗口正在编辑此图板,本页面已切换为只读以防数据互覆盖</span>
          <button
            onClick={() => { takeOverEditor(); window.location.reload(); }}
            style={{
              background: '#fff', color: '#dc3545', border: 'none',
              padding: '4px 12px', borderRadius: 4, cursor: 'pointer', fontWeight: 600
            }}
          >
            在此处接管(将刷新)
          </button>
        </div>
      )}

      {/* 返回仪表盘 logo：左侧拼屏时改为相对画布主区的绝对定位（top-left），
          随画布被面板推移而平移，始终悬于工具条上方且不被面板遮挡；
          其它布局沿用组件默认的 fixed 顶部定位 */}
      <CineartLogo
        onClick={handleNavigateHome}
        title="返回仪表盘"
        style={modelMasterLayout === 'left'
          ? { position: 'absolute', top: '24px', left: '16px', opacity: 0.8 }
          : { opacity: 0.8 }}
      />
      <BottomControls undo={undo} redo={redo} canUndo={canUndo} canRedo={canRedo} />

      <NodeCallbackProvider callbacks={nodeCallbacks}>
       <MenuOpenProvider open={!!menuData}>
        <ReactFlow
          nodes={nodes} edges={edges}
          onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
          onConnect={onConnect} onConnectStart={onConnectStart} onConnectEnd={onConnectEnd}
          connectOnClick={false} connectionRadius={45}
          onNodesDelete={onNodesDelete}
          deleteKeyCode={null}
          nodeTypes={initialNodeTypes} edgeTypes={initialEdgeTypes}
          zoomOnDoubleClick={false}
          elevateNodesOnSelect={true}
          onInit={(instance) => {
            if (restoredViewport) setTimeout(() => instance.setViewport(restoredViewport), 10);
            // 🆕 初始化预测式预加载器
            predictivePreloader.init({
              reactFlowInstance: instance,
              nodes,
              priorityQueue,
              fetchCallback: (node) => {
                const fileType = node.data.type === 'video' ? 'video' : node.data.type === 'audio' ? 'audio' : 'image';
                return localCacheService.getLocalUrlOrFetch(
                  node.data.output,
                  crewId,
                  crewName,
                  fileType
                );
              }
            });
          }}
          onSelectionChange={onSelectionChange}
          onPaneContextMenu={handlePaneContextMenu}
          onNodeClick={onNodeClick}
          onNodeDragStart={onNodeDragStart}
          onNodeDrag={onNodeDrag}
          onNodeDragStop={onNodeDragStop}
          onNodeContextMenu={(e, node) => {
            e.preventDefault();
            setContextMenu({
              type: 'node', mouseX: e.clientX, mouseY: e.clientY, targetNodeId: node.id,
              flowPos: screenToFlowPosition({ x: e.clientX, y: e.clientY })
            });
          }}
          onPaneClick={(e) => { if (suppressClickRef.current) return; dismissMenus(); if (e.detail === 2) { lastUploadPosRef.current = screenToFlowPosition({ x: e.clientX, y: e.clientY }); fileInputRef.current?.click(); } }}
          onMoveStart={(event) => { if (event?.type === 'mousedown') setNodes(nds => nds.map(n => ({ ...n, selected: false }))); }}
          onMove={predictivePreloader.onViewportChange.bind(predictivePreloader)}
          minZoom={0.2} maxZoom={5.0} autoPanOnNodeDrag={false}
          nodeDragThreshold={4}
          translateExtent={[[-50000, -50000], [50000, 50000]]}
          nodeExtent={[[-50000, -50000], [50000, 50000]]}
          multiSelectionKeyCode="Shift" selectionKeyCode="Shift"
          style={{ userSelect: 'none' }}
          onSelectCapture={(e) => e.preventDefault()}
        >
          <SelectionToolbar selectedNodes={selectedNodes} onCreateGroup={handleSaveWorkflow} onGroupNodes={handleGroupNodes} />
          <MultiSelectHandle selectedNodes={selectedNodes} onMultiSelectConnectStart={handleMultiSelectConnectStart} />
          <GroupToolbar selectedNodes={selectedNodes} onUngroup={handleUngroup} />
          <Background color={theme === 'light' ? '#c4c4c4' : '#999'} variant="dots" gap={30} size={1} style={{ opacity: 0.5 }} />
          <HelperLines horizontal={helperLines.horizontal} vertical={helperLines.vertical} />
        </ReactFlow>
       </MenuOpenProvider>
      </NodeCallbackProvider>

      {/* ── 画布区域 Loading 遮罩（不影响抽屉和工具栏）── */}
      {!isInitialized && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 9999,
          background: 'rgba(0, 0, 0, 0.85)',
          backdropFilter: 'blur(8px)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          color: '#fff', pointerEvents: 'all',
        }}>
          <Loader2 size={56} style={{ animation: 'spin 1s linear infinite', color: '#3b82f6', marginBottom: 16 }} />
          <p style={{ marginTop: 0, fontSize: 20, fontWeight: 600, color: '#fff' }}>正在打开舞台...</p>
          <p style={{ marginTop: 8, fontSize: 14, color: 'rgba(255,255,255,0.7)' }}>请稍候</p>
          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* ── 弹窗 / 抽屉 ── */}
      <CreditLogsModal open={isCreditLogsOpen} onClose={() => setIsCreditLogsOpen(false)} />
      <RechargeModal open={isRechargeOpen} onClose={() => setIsRechargeOpen(false)} onSuccess={() => refreshUser()} />
      {/* 🎓 算力券兑换（教育版）。process.env 折叠后商业版这块整个消失 */}
      {(process.env.REACT_APP_SITE_EDITION === 'edu' || (process.env.REACT_APP_SITE_EDITION || '').endsWith('-edu')) && (
        <VoucherRedeemModal open={isVoucherOpen} onClose={() => setIsVoucherOpen(false)} onSuccess={() => refreshUser()} />
      )}
      <AssetDrawer
        isOpen={isDrawerOpen} onClose={() => setDrawerOpen(false)} onToggle={() => setDrawerOpen(prev => !prev)}
        bottomOffset={modelMasterLayout === 'bottom' ? modelMasterSize + 5 : 0}
        assets={globalAssets} publicAssets={publicAssets} globalSpells={globalSpells}
        spellCategories={mergedSpellCategories}
        projects={allProjects} currentProjectId={parseInt(projectId)}
        activeTab={activeTab} onTabChange={setActiveTab}
        onUploadToPublic={handleUploadToPublic} onDownloadFromPublic={handleDownloadFromPublic}
        onSpawnAsset={handleSpawnAsset} onDeleteAsset={handleDeleteAsset}
        onDeletePublicAsset={handleDeletePublicAsset} onDeleteGlobalAsset={handleDeleteGlobalAsset} onUpdateAsset={handleUpdateAsset}
        onUpdateAssetDescription={handleUpdateAssetDescription}
        canDeletePublic={canDeletePublic}
        user={user}
        onCreateProject={handleCreateProject} onRenameProject={handleRenameProject}
        onDeleteProject={handleDeleteProject} onSetProjectCover={handleSetProjectCover}
        onSetAssetCover={handleSetAssetCover}
        onEditAsset={handleEditAsset}
        onSaveBeforeSwitch={async () => {
          // 🛡️ 裁剪中拦截：浏览器端 FFmpeg.wasm 正在裁剪视频时切换图板会导致任务丢失
          const clippingNodes = nodesRef.current.filter(n => n.data?.clipping);
          if (clippingNodes.length > 0) {
            setAlertMsg({ text: '视频正在裁剪中，请等裁剪完成后再切换图板', type: 'warning' });
            return false; // 返回 false 表示不允许切换
          }
          // 🛡️ Risk 1 修复：触发 textarea 失焦，flush 正在 debounce 中的 prompt 到 nodes 状态
          // NodeConsole 的 onBlur 会立即 clearTimeout + handleUpdate({ prompt: localPrompt })
          if (document.activeElement && (document.activeElement.tagName === 'TEXTAREA' || document.activeElement.tagName === 'INPUT')) {
            document.activeElement.blur();
          }
          // 等待 React 完成因 blur 触发的 setNodes 批处理，确保 nodesRef.current 已更新
          await new Promise(r => setTimeout(r, 50));
          // 🛡️ Risk 2 修复：保存失败时提示用户（saveProject 返回 boolean）
          // 必须用 true：手动模式会 await 在途保存完成再发起一次保存，否则数据会丢
          const ok = await saveProject(true);
          if (!ok) {
            setAlertMsg({ text: '切换图板前保存失败，数据可能未保存', type: 'warning' });
          }
        }}
      />
      <SaveAssetModal
        isOpen={saveModalData?.isOpen}
        nodeData={saveModalData?.nodeData}
        onClose={() => setSaveModalData(null)}
        onSave={handleCommitSave}
        isSuperAdmin={user?.role === 'super_admin'}
        projectId={projectId}
        // 全局咒语分类列表（按持久化排序），未排序的 distinct 分类追加在末尾
        globalSpellCategories={mergedSpellCategories}
        onRenameSpellCategory={user?.role === 'super_admin' ? handleRenameSpellCategory : undefined}
        onDeleteSpellCategory={user?.role === 'super_admin' ? handleDeleteSpellCategory : undefined}
        onReorderSpellCategories={user?.role === 'super_admin' ? handleReorderSpellCategories : undefined}
      />
      <AlertToast message={typeof alertMsg === 'string' ? alertMsg : alertMsg?.text} type={alertMsg?.type || 'warning'} />

      <ConfirmModal title="确认删除"
        message={deleteConfirm
          ? (deleteConfirm.isUploading
            ? `文件正在上传，确认要删除吗？`
            : `「${deleteConfirm.nodeTitle}」正在生成中，删除后将不会返回生成结果。\n\n确定要删除吗？`)
          : null}
        confirmText="确认删除" cancelText="取消" onConfirm={handleConfirmDelete} onCancel={() => setDeleteConfirm(null)} danger />

      {/* ── 右键菜单 ── */}
      {contextMenu && (
        <div className="nodrag" style={{ ...C.contextMenuStyle, left: contextMenu.mouseX, top: contextMenu.mouseY }}>
          {contextMenu.type === 'pane' ? (
            <>
              <ContextMenuItem icon={<Upload />} label="上传素材" onClick={() => { lastUploadPosRef.current = contextMenu.flowPos; fileInputRef.current.click(); setContextMenu(null); }} />
              <div style={{ height: '1px', background: 'var(--border-strong)', margin: '2px 6px' }} />
              <ContextMenuItem icon={<Sparkles />} label="新建咒语" onClick={() => addNodeFromContextMenu('text')} />
              <div style={{ height: '1px', background: 'var(--border-strong)', margin: '2px 6px' }} />
              <ContextMenuItem icon={<ImageIcon />} label="图像模型" onClick={() => addNodeFromContextMenu('image')} />
              <ContextMenuItem icon={<Clapperboard />} label="视频模型" onClick={() => addNodeFromContextMenu('video')} />
              <div style={{ height: '1px', background: 'var(--border-strong)', margin: '2px 6px' }} />
              <ContextMenuItem icon={<LayoutGrid />} label={hasMemberFeature('storyboard_master') ? '分镜大师' : '分镜大师（不可用）'} onClick={() => {
                if (!hasMemberFeature('storyboard_master')) { setAlertMsg({ type: 'warning', text: '没有权限操作，当前会员等级未开通「分镜大师」，请联系管理员开通' }); setContextMenu(null); return; }
                handleAddStoryboard(9, contextMenu.flowPos);
              }} />
            </>
          ) : (() => {
            const targetNode = getNodes().find(n => n.id === contextMenu?.targetNodeId);
            const isSystemNode = !!targetNode?.data?.isSystemSpell;
            return (
            <>
              <ContextMenuItem icon={<Save />} label="保存到个人库" onClick={handleOpenSaveModal} />
              <div style={{ height: '1px', background: 'var(--border-strong)', margin: '2px 6px' }} />
              <ContextMenuItem icon={<GitFork />} label="创建副本" onClick={handleDuplicateNode} />
              <ContextMenuItem icon={<FilePlus />} label="系统复制" onClick={handleSystemCopy} />
              {!isSystemNode && <ContextMenuItem icon={<Link />} label="复制分享链接" onClick={handleCopyShareLink} disabled={(() => {
                const n = targetNode;
                // 禁用条件：① 无输出 URL ② 临时 blob / 本地缓存地址 ③ 多图层复合节点尚未合并（annotationData 存在）
                return !n?.data?.output
                  || n.data.output.startsWith('blob:')
                  || n.data.output.startsWith('local-cache://')
                  || !!n?.data?.annotationData;
              })()} />}
              {!isSystemNode && <ContextMenuItem icon={<ImagePlus />} label="设为封面" onClick={handleSetAsCover} disabled={(() => { const n = targetNode; return !!(n?.data?.annotationData); })()} />}
              {!isSystemNode && <ContextMenuItem icon={<ImageIcon />} label="设为海报" onClick={handleSetAsPoster} disabled={crewRole !== 'producer' || (() => { const n = targetNode; return !!(n?.data?.annotationData); })()} />}
              <div style={{ height: '1px', background: 'var(--border-strong)', margin: '2px 6px' }} />
              {!isSystemNode && <ContextMenuItem icon={<Trash2 />} label="删除节点" danger onClick={() => { deleteElements({ nodes: [{ id: contextMenu.targetNodeId }] }); setContextMenu(null); }} />}
            </>
            );
          })()}
        </div>
      )}

      {/* 🔗 "留住"的预览连线：起点由 sourceId 从 store 实时读，缩放/平移/节点改尺寸都跟着走。
          菜单一关（选了节点 / 点空白）就随 menuData 消失。 */}
      {menuData && menuData.previewEndX != null && (
        <>
          {menuData.isMultiSelect && menuData.sourceIds ? (
            // 多选模式：渲染多条预览线
            menuData.sourceIds.map(srcId => (
              <PreviewConnectLine key={srcId} sourceId={srcId} endX={menuData.previewEndX} endY={menuData.previewEndY} />
            ))
          ) : menuData.sourceId ? (
            // 单选模式：渲染单条预览线
            <PreviewConnectLine sourceId={menuData.sourceId} endX={menuData.previewEndX} endY={menuData.previewEndY} />
          ) : null}
        </>
      )}

      {menuData && (
        <div className="nodrag" style={{ ...C.menuContainerStyle, left: menuData.mouseX, top: menuData.mouseY }}>
          <div style={C.menuHeaderStyle}>
            {menuData.isMultiSelect
              ? `引用选中的 ${menuData.sourceIds?.length || 0} 个节点`
              : '可连接以下节点:'}
          </div>
          <div style={C.menuListStyle}>
            {U.getRecommendations(menuData).map((item) => (
              <div key={item.title} className="menu-item" style={C.menuItemStyle}
                onClick={() => addNewNodeFromMenu(
                  menuData.isMultiSelect ? menuData.sourceIds : menuData.sourceId,
                  menuData.sourceNodePos,
                  item.title,
                  menuData.dropFlowPos,
                  menuData.isMultiSelect
                )}>
                <div style={C.menuItemIconStyle}>{item.icon}</div>
                <div style={C.menuItemContentStyle}>
                  <div style={C.menuItemTitleStyle}>{item.title}</div>
                  <div style={C.menuItemDescStyle}>{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showImagePicker && (
        <ImagePickerModal images={projectImages} onSelect={handleSelectCoverImage} onClose={handleCloseCoverModals} />
      )}

      {coverCropUrl && (
        <CoverCropperModal imageUrl={coverCropUrl}
          onClose={coverProject ? handleCloseCoverModals : () => setCoverCropUrl(null)}
          onSave={coverProject ? handleOtherProjectCoverSave : handleCoverSave} />
      )}

      {posterCropUrl && (
        <CoverCropperModal imageUrl={posterCropUrl}
          onClose={() => setPosterCropUrl(null)}
          onSave={handlePosterSave}
          aspectRatio={9/16}
          title="设置项目海报"
          buttonText="设为海报" />
      )}

      {inpaintState && (
        <InpaintMaskEditor
          imageUrl={inpaintState.imageUrl}
          onClose={closeInpaintEditor}
          onSubmit={handleMjInpaintSubmit}
        />
      )}

      {gptInpaintState && (
        <InpaintMaskEditor
          imageUrl={gptInpaintState.imageUrl}
          onClose={closeGptInpaintEditor}
          onSubmit={handleGptInpaintSubmit}
          allowEmptyMask
          maskFormat="openai"
          hint="涂抹要重绘的区域 · 不涂遮罩则整图按提示词重绘 · 可输入提示词引导重绘内容"
        />
      )}

      {/* 💬 项目组聊天窗口（受控组件：chatTarget 由本组件的 chatTargetId 驱动） */}
      {isChatOpen && crewId && crewMembers.length > 1 && (
        <ChatWindow
          crewId={crewId}
          crewMembers={crewMembers}     /* 成员列表，每项含 user_id 字段 */
          user={user}
          chatTarget={chatTargetId}      /* null=公共 | userId=私聊 */
          onChatTargetChange={(id) => { setChatTargetId(id); markRead(id); }}  /* ChatWindow 内切换时回调，同时清零未读 */
          onChatFocus={() => setNodes(nds => nds.map(n => ({ ...n, selected: false })))}  /* 聊天面板获得焦点时取消所有节点选中，避免语音快捷键冲突 */
          onClose={() => setIsChatOpen(false)}
          onAddToCanvas={({ type, url }) => {
            const id = `node-${Date.now()}`;
            const mediaType = type || 'image';

            // 聊天窗口在屏幕 left:80, width:360，节点出现在其右侧
            const chatRight = 80 + 360 + 30; // 聊天窗口右边再留 30px 间距
            const chatCenterY = window.innerHeight / 2;

            const placeNode = (w, h) => {
              const customSize = { width: w, height: h };
              const pos = screenToFlowPosition({ x: chatRight, y: chatCenterY - h / 2 });
              const newNode = {
                id, type: 'aiNode', position: pos, selected: true,
                data: buildNodeData(id, mediaType, '聊天媒体', {
                  output: url, outputs: [url], isDirectUpload: true, customSize,
                }),
              };
              setNodes(nds => [...nds.map(n => ({ ...n, selected: false })), newNode]);
              setAlertMsg('已添加到画布');
            };

            if (mediaType === 'audio') {
              placeNode(160, 56);
            } else if (mediaType === 'image') {
              const img = new window.Image();
              img.onload = () => {
                const MAX = 320;
                let w = img.naturalWidth, h = img.naturalHeight;
                if (w > MAX || h > MAX) {
                  const scale = Math.min(MAX / w, MAX / h);
                  w = Math.round(w * scale);
                  h = Math.round(h * scale);
                }
                placeNode(Math.max(w, 80), Math.max(h, 80));
              };
              img.onerror = () => placeNode(240, 240);
              img.src = url;
            } else if (mediaType === 'video') {
              const video = document.createElement('video');
              video.preload = 'metadata';
              video.onloadedmetadata = () => {
                const MAX = 320;
                let w = video.videoWidth || 240, h = video.videoHeight || 240;
                if (w > MAX || h > MAX) {
                  const scale = Math.min(MAX / w, MAX / h);
                  w = Math.round(w * scale);
                  h = Math.round(h * scale);
                }
                placeNode(Math.max(w, 80), Math.max(h, 80));
              };
              video.onerror = () => placeNode(240, 135);
              video.src = url;
            } else {
              placeNode(240, 240);
            }
          }}
        />
      )}

      <style>{`.menu-item:hover { background: var(--bg-hover) !important; } .sidebar-btn:hover { background: var(--bg-hover) !important; color: var(--text-hover) !important; }`}</style>
      </div>{/* ── 画布主区结束 ── */}

      {/* ── 🎬 剧本大师分屏：divider + 面板 ── */}
      {modelMasterLayout && (
        <>
          {/* 可拖动分隔条 */}
          <div
            ref={dividerRef}
            onMouseDown={handleModelMasterDragStart}
            style={{
              order: 1, flexShrink: 0, position: 'relative', zIndex: 20,
              background: 'rgba(255,255,255,0.08)',
              touchAction: 'none',
              ...(modelMasterLayout === 'left'
                ? { width: 5, height: '100%', cursor: 'col-resize' }
                : { height: 5, width: '100%', cursor: 'row-resize' }),
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#3b82f6'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
          >
            {/* 把手：圆角方块 + 抓手点，向两侧稍突出，便于触屏抓取。
                位置放在分隔条下方约 8/9 处（文本输入框上方），不居中；
                zIndex 抬高，避免被相邻面板盖住半截（iPad 上曾出现）。
                自身也绑拖动起手（含原生 touchstart，见 gripRef effect），突出部分照样可拖。 */}
            <div
              ref={gripRef}
              onMouseDown={handleModelMasterDragStart}
              style={{
                position: 'absolute',
                background: 'rgba(255,255,255,0.18)',
                borderRadius: '6px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: modelMasterLayout === 'left' ? '3px' : '0',
                flexDirection: modelMasterLayout === 'left' ? 'row' : 'column',
                touchAction: 'none',
                zIndex: 20,
                cursor: modelMasterLayout === 'left' ? 'col-resize' : 'row-resize',
                ...(modelMasterLayout === 'left'
                  // 竖条：把手竖向、横向突出；放在 75% 处（偏下，文本框上方）
                  ? { width: '15px', height: '46px', top: '75%', left: '50%', transform: 'translate(-50%, -50%)' }
                  // 横条：把手横向、纵向突出；水平居中
                  : { width: '46px', height: '15px', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }),
              }}
            >
              {/* 抓手点：竖条时排成两列点、横条时两行点 */}
              {modelMasterLayout === 'left' ? (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    {[0,1,2].map(i => <div key={i} style={{ width: '3px', height: '3px', borderRadius: '50%', background: 'rgba(255,255,255,0.75)' }} />)}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    {[0,1,2].map(i => <div key={i} style={{ width: '3px', height: '3px', borderRadius: '50%', background: 'rgba(255,255,255,0.75)' }} />)}
                  </div>
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: '3px' }}>
                    {[0,1,2].map(i => <div key={i} style={{ width: '3px', height: '3px', borderRadius: '50%', background: 'rgba(255,255,255,0.75)' }} />)}
                  </div>
                  <div style={{ display: 'flex', gap: '3px' }}>
                    {[0,1,2].map(i => <div key={i} style={{ width: '3px', height: '3px', borderRadius: '50%', background: 'rgba(255,255,255,0.75)' }} />)}
                  </div>
                </>
              )}
            </div>
          </div>
          {/* 面板本体 */}
          <div style={{
            order: modelMasterLayout === 'left' ? 0 : 2, flexShrink: 0,
            ...(modelMasterLayout === 'left'
              // 横屏宽度下限 360（兜底老数据/小屏）；面板可自由变大，不再限 720
              ? { width: Math.max(360, modelMasterSize), height: '100%' }
              : { height: modelMasterSize, width: '100%' }),
          }}>
            <ModelMasterPanel
              layout={modelMasterLayout}
              onClose={() => setModelMasterLayout(null)}
              onSpawnTextNode={handleSpawnTextFromModelMaster}
              onImportToStoryboard={handleImportOutlineToStoryboard}
            />
          </div>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// EditorPage — 外层包裹 ReactFlowProvider
// ═══════════════════════════════════════════════════════
export default function EditorPage() {
  return (
    <ReactFlowProvider>
      <EditorContent />
    </ReactFlowProvider>
  );
}
